import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChefHat, DollarSign, TrendingUp, Users, CheckCircle, XCircle, Package, BookOpen, BarChart3, Globe, LogIn, LayoutDashboard } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import i18n from '@/i18n';
import { useAuth } from '../contexts/AuthContext';

// Multi-language content
const TRANSLATIONS = {
  en: {
    // Header
    languageLabel: 'Language',
    
    // Hero Section (Hook)
    heroTitle: 'From Pantry to Plate,',
    heroSubtitle: 'Profit on Every Milligram.',
    heroDescription: 'The complete ecosystem for culinary professionals',
    heroPrecision: '6-Decimal Precision. Every Milligram Counts.',
    ctaButton: 'Start for as low as $2.99/mo',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'Save Grandma\'s Secrets Forever.',
    leftHeroSubtext: 'Build and protect your Master Recipe Bank with 6-decimal precision.',
    leftHeroCta: 'Start Artisan ($2.99/mo)',
    rightHeroTitle: 'Pantry to Plate Profitability.',
    rightHeroSubtext: 'The Executive Chef\'s engine for 100% margin control.',
    rightHeroCta: 'Go Professional ($5.99/mo)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: 'Raw Ingredients',
    precisionTracking: 'Precision sourcing, tracked daily',
    engineTitle: 'PlateProfit Engine',
    engineSubtitle: '6-Decimal precision tracking',
    unitCostLabel: 'Unit-Cost Precision',
    perGramLabel: 'per Gram',
    noWastageLabel: 'No hidden wastage. Every milligram tracked.',
    platedLabel: 'Plated Perfection',
    profitableLabel: 'Profitable, every time',
    joinChefs: 'Join chefs in 150+ countries tracking every milligram',
    
    // Pricing Section
    pricingTitle: 'Transparent Pricing',
    pricingSubtitle: 'Less than the cost of a cup of coffee for a month of precision.',
    artisanTier: 'Artisan',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'The Master Recipe Bank—Heritage-ready',
    artisanFeature1: 'The Master Recipe Bank (6-decimal precision)',
    artisanFeature2: 'Up to 250 Master Recipes',
    artisanFeature3: '6-Decimal Costing for every ingredient',
    artisanScalingHook: 'Grow as you go: +$2.99/mo for every additional 250 recipes.',
    artisanCta: 'Start Artisan',
    professionalTier: 'Professional',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'The Executive Engine for Profit Control',
    professionalBridge: 'Everything in Artisan, plus:',
    professionalFeature1: 'Unlimited Master Recipes',
    professionalFeature2: 'Advanced Menu Building & Dynamic Pricing',
    professionalFeature3: 'Menu Sharing to Multiple Devices',
    professionalFeature4: 'Real-time Profit Analysis & Multi-department Tracking',
    professionalFeature5: 'PDF Export & Professional Reporting',
    professionalCta: 'Start Professional',
    popularBadge: 'Most Popular',
    
    // PWA Utility Bar
    utilityNoApp: 'No App Store Needed',
    utilityOffline: 'Kitchen-Proof Offline',
    utilitySync: 'Instant Multi-Device Sync',
    
    // Specialist Verticals
    specialistTitle: 'Built for Every Culinary Specialist',
    mixologyTitle: 'The Mixology Lab',
    mixologyDesc: 'Precision costing for every pour, from craft cocktails to coffee art.',
    mixologyUnit: 'per ml',
    pastryTitle: 'The Pastry Artist',
    pastryDesc: 'Micro-measurement tracking for delicate desserts and baking.',
    pastryUnit: 'per gram',
    spiceTitle: 'The Spice Blender',
    spiceDesc: 'Protect your secret blends with ingredient-level precision.',
    spiceUnit: 'per gram',
    
    // Section B: Problem/Solution
    problemTitle: 'The Hidden Gap in Your Kitchen',
    problemSubtitle: 'Most chefs guess at pricing. We help you know.',
    problemCard1Title: 'Rising Ingredient Costs',
    problemCard1Desc: 'Prices change daily, but your menu stays the same. Are you still profitable?',
    problemCard2Title: 'Menu Pricing Mystery',
    problemCard2Desc: 'How much does each dish really cost? Without accurate data, you\'re flying blind.',
    problemCard3Title: 'Wasted Time & Money',
    problemCard3Desc: 'Manual spreadsheets, outdated costs, and guesswork lead to shrinking margins.',
    
    solutionTitle: 'PlateProfit: Your Recipe & Cost Command Center',
    solutionDesc: 'Track every ingredient, calculate exact recipe costs, and set profitable menu prices—all in one platform.',
    
    // Section C: Personas
    personasTitle: 'Built for Every Culinary Journey',
    
    individualTitle: 'Individual Home Maker',
    individualSubtitle: 'Perfect for passionate home cooks',
    individualFeature1: 'Store unlimited recipes',
    individualFeature2: 'Track ingredient costs',
    individualFeature3: 'Calculate serving costs',
    individualFeature4: 'Basic recipe management',
    individualCta: 'Start as Individual',
    
    professionalTitle: 'Professional Chef',
    professionalSubtitle: 'For restaurants and food businesses',
    professionalSpectrum: 'From Michelin-star kitchens to global street food—PlateProfit is your command center.',
    professionalFeature1: 'Everything in Individual, plus:',
    professionalFeature2: 'Advanced menu pricing',
    professionalFeature3: 'Multi-department tracking',
    professionalFeature4: 'Real-time profit analysis',
    professionalFeature5: 'PDF export & sharing',
    professionalCta: 'Go Professional',
    
    // Section D: Features
    featuresTitle: 'Everything You Need to Run a Profitable Kitchen',
    
    feature1Title: 'Recipe Bank',
    feature1Desc: 'Store, organize, and manage all your recipes in one place. Never lose a recipe again.',
    
    feature2Title: 'Active/Inactive Recipes',
    feature2Desc: 'Toggle recipes on and off based on seasonality or availability. Keep your menu fresh.',
    
    feature3Title: 'Real-Time Cost Tracking',
    feature3Desc: 'Update ingredient prices once, and see cost changes across all recipes instantly.',
    
    feature4Title: 'Menu Pricing Intelligence',
    feature4Desc: 'Set target margins and get instant recommendations for profitable menu pricing.',
    
    feature5Title: 'Multi-Currency Support',
    feature5Desc: 'Work in your local currency. We support USD, EUR, QAR, INR, and 20+ more.',
    
    feature6Title: 'Department Tracking',
    feature6Desc: 'Organize recipes by Kitchen, Bar, Pastry, or custom departments for better control.',
    
    // Footer
    footerCta: 'Ready to Transform Your Kitchen?',
    footerCtaButton: 'Start Your Free Account',
    footerTagline: 'PlateProfit — Precision in Every Portion',
  },
  
  ar: {
    // Header
    languageLabel: 'اللغة',
    
    // Hero Section
    heroTitle: 'من المخزن إلى الطبق،',
    heroSubtitle: 'ربح في كل ملليغرام.',
    heroDescription: 'نظام متكامل لمحترفي الطهي',
    heroPrecision: 'دقة 6 خانات عشرية. كل ملليغرام مهم.',
    ctaButton: 'ابدأ من $2.99/شهر',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'احفظ أسرار جدتك إلى الأبد.',
    leftHeroSubtext: 'ابنِ واحمِ بنك وصفاتك الرئيسي بدقة 6 خانات عشرية.',
    leftHeroCta: 'ابدأ حرفي ($2.99/شهر)',
    rightHeroTitle: 'ربحية من المخزن إلى الطبق.',
    rightHeroSubtext: 'محرك الشيف التنفيذي للتحكم الكامل بالهامش 100%.',
    rightHeroCta: 'ابدأ محترف ($5.99/شهر)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: 'المكونات الخام',
    precisionTracking: 'مصادر دقيقة، تتبع يومي',
    engineTitle: 'محرك PlateProfit',
    engineSubtitle: 'تتبع دقيق بـ 6 خانات عشرية',
    unitCostLabel: 'دقة تكلفة الوحدة',
    perGramLabel: 'لكل غرام',
    noWastageLabel: 'لا هدر خفي. كل ملليغرام مُتتبع.',
    platedLabel: 'الكمال المُقدَّم',
    profitableLabel: 'مُربح، في كل مرة',
    joinChefs: 'انضم إلى الطهاة في أكثر من 150 دولة يتتبعون كل ملليغرام',
    
    // Pricing Section
    pricingTitle: 'تسعير شفاف',
    pricingSubtitle: 'أقل من تكلفة فنجان قهوة لشهر من الدقة.',
    artisanTier: 'حرفي',
    artisanPrice: '$2.99',
    artisanPeriod: '/شهر',
    artisanDesc: 'بنك الوصفات الرئيسي—جاهز للإرث',
    artisanFeature1: 'بنك الوصفات الرئيسي (دقة 6 خانات عشرية)',
    artisanFeature2: 'حتى 250 وصفة رئيسية',
    artisanFeature3: 'تكلفة بدقة 6 خانات عشرية لكل مكون',
    artisanScalingHook: 'نمو مع نموك: +$2.99/شهر لكل 250 وصفة إضافية.',
    artisanCta: 'ابدأ حرفي',
    professionalTier: 'محترف',
    professionalPrice: '$5.99',
    professionalPeriod: '/شهر',
    professionalDesc: 'محرك التنفيذي للتحكم بالربح',
    professionalBridge: 'كل ما في الحرفي، بالإضافة إلى:',
    professionalFeature1: 'وصفات رئيسية غير محدودة',
    professionalFeature2: 'بناء القائمة المتقدم والتسعير الديناميكي',
    professionalFeature3: 'مشاركة القائمة لأجهزة متعددة',
    professionalFeature4: 'تحليل الربح في الوقت الفعلي وتتبع متعدد الأقسام',
    professionalFeature5: 'تصدير PDF وتقارير احترافية',
    professionalCta: 'ابدأ محترف',
    popularBadge: 'الأكثر شعبية',
    
    // PWA Utility Bar
    utilityNoApp: 'لا حاجة لمتجر التطبيقات',
    utilityOffline: 'يعمل بلا إنترنت في المطبخ',
    utilitySync: 'مزامنة فورية متعددة الأجهزة',
    
    // Specialist Verticals
    specialistTitle: 'مبني لكل متخصص طهي',
    mixologyTitle: 'مختبر الخلط',
    mixologyDesc: 'تكلفة دقيقة لكل صب، من الكوكتيلات إلى فن القهوة.',
    mixologyUnit: 'لكل مل',
    pastryTitle: 'فنان المعجنات',
    pastryDesc: 'تتبع القياسات الدقيقة للحلويات والخبز الرقيق.',
    pastryUnit: 'لكل غرام',
    spiceTitle: 'خلاط التوابل',
    spiceDesc: 'احمِ خلطاتك السرية بدقة مستوى المكونات.',
    spiceUnit: 'لكل غرام',
    
    // Problem/Solution
    problemTitle: 'الفجوة الخفية في مطبخك',
    problemSubtitle: 'معظم الطهاة يخمنون الأسعار. نحن نساعدك على المعرفة.',
    problemCard1Title: 'ارتفاع تكاليف المكونات',
    problemCard1Desc: 'الأسعار تتغير يوميًا، لكن قائمتك تبقى كما هي. هل ما زلت تحقق ربحًا؟',
    problemCard2Title: 'غموض تسعير القائمة',
    problemCard2Desc: 'كم تكلف كل طبق حقًا؟ بدون بيانات دقيقة، أنت تطير أعمى.',
    problemCard3Title: 'وقت وأموال مهدرة',
    problemCard3Desc: 'جداول البيانات اليدوية والتكاليف القديمة والتخمين تؤدي إلى تقلص الأرباح.',
    
    solutionTitle: 'PlateProfit: مركز قيادة الوصفات والتكاليف',
    solutionDesc: 'تتبع كل مكون، احسب تكاليف الوصفات بدقة، وحدد أسعار قائمة مربحة—كل ذلك في منصة واحدة.',
    
    // Personas
    personasTitle: 'مصمم لكل رحلة طهي',
    
    individualTitle: 'ربة منزل فردية',
    individualSubtitle: 'مثالي للطهاة المنزليين المتحمسين',
    individualFeature1: 'تخزين وصفات غير محدودة',
    individualFeature2: 'تتبع تكاليف المكونات',
    individualFeature3: 'حساب تكاليف الوجبة',
    individualFeature4: 'إدارة أساسية للوصفات',
    individualCta: 'ابدأ كفرد',
    
    professionalTitle: 'طاهٍ محترف',
    professionalSubtitle: 'للمطاعم والأعمال الغذائية',
    professionalSpectrum: 'من مطابخ ميشلان إلى طعام الشوارع العالمي—PlateProfit هو مركز قيادتك.',
    professionalFeature1: 'كل ما في الفردي، بالإضافة إلى:',
    professionalFeature2: 'تسعير متقدم للقائمة',
    professionalFeature3: 'تتبع متعدد الأقسام',
    professionalFeature4: 'تحليل الربح في الوقت الفعلي',
    professionalFeature5: 'تصدير PDF ومشاركة',
    professionalCta: 'احترف',
    
    // Features
    featuresTitle: 'كل ما تحتاجه لإدارة مطبخ مربح',
    
    feature1Title: 'بنك الوصفات',
    feature1Desc: 'تخزين وتنظيم وإدارة جميع وصفاتك في مكان واحد. لن تفقد وصفة مرة أخرى.',
    
    feature2Title: 'وصفات نشطة/غير نشطة',
    feature2Desc: 'تبديل الوصفات بناءً على الموسمية أو التوفر. حافظ على قائمتك طازجة.',
    
    feature3Title: 'تتبع التكلفة في الوقت الفعلي',
    feature3Desc: 'حدث أسعار المكونات مرة واحدة، وشاهد تغييرات التكلفة عبر جميع الوصفات على الفور.',
    
    feature4Title: 'ذكاء تسعير القائمة',
    feature4Desc: 'حدد الهوامش المستهدفة واحصل على توصيات فورية لتسعير قائمة مربح.',
    
    feature5Title: 'دعم متعدد العملات',
    feature5Desc: 'اعمل بعملتك المحلية. ندعم الدولار واليورو والريال القطري والروبية الهندية و20+ عملة أخرى.',
    
    feature6Title: 'تتبع الأقسام',
    feature6Desc: 'نظم الوصفات حسب المطبخ أو البار أو الحلويات أو الأقسام المخصصة للتحكم بشكل أفضل.',
    
    // Footer
    footerCta: 'هل أنت مستعد لتحويل مطبخك؟',
    footerCtaButton: 'ابدأ حسابك المجاني',
    footerTagline: 'PlateProfit — دقة في كل جزء',
  },
  
  es: {
    // Header
    languageLabel: 'Idioma',
    
    // Hero Section
    heroTitle: 'De la Despensa al Plato,',
    heroSubtitle: 'Beneficio en Cada Miligramo.',
    heroDescription: 'El ecosistema completo para profesionales culinarios',
    heroPrecision: 'Precisión de 6 Decimales. Cada Miligramo Cuenta.',
    ctaButton: 'Desde $2.99/mes',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'Guarda los Secretos de la Abuela para Siempre.',
    leftHeroSubtext: 'Construye y protege tu Banco de Recetas Maestro con precisión de 6 decimales.',
    leftHeroCta: 'Comenzar Artesano ($2.99/mes)',
    rightHeroTitle: 'Rentabilidad de la Despensa al Plato.',
    rightHeroSubtext: 'El motor del Chef Ejecutivo para control 100% del margen.',
    rightHeroCta: 'Ir a Profesional ($5.99/mes)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: 'Ingredientes Crudos',
    precisionTracking: 'Abastecimiento preciso, rastreado diariamente',
    engineTitle: 'Motor PlateProfit',
    engineSubtitle: 'Seguimiento de precisión de 6 decimales',
    unitCostLabel: 'Precisión de Costo Unitario',
    perGramLabel: 'por Gramo',
    noWastageLabel: 'Sin desperdicio oculto. Cada miligramo rastreado.',
    platedLabel: 'Perfección Emplatada',
    profitableLabel: 'Rentable, siempre',
    joinChefs: 'Únete a chefs en más de 150 países rastreando cada miligramo',
    
    // Pricing Section
    pricingTitle: 'Precios Transparentes',
    pricingSubtitle: 'Menos que el costo de una taza de café por un mes de precisión.',
    artisanTier: 'Artesano',
    artisanPrice: '$2.99',
    artisanPeriod: '/mes',
    artisanDesc: 'El Banco de Recetas Maestras—Listo para el Legado',
    artisanFeature1: 'El Banco de Recetas Maestras (precisión de 6 decimales)',
    artisanFeature2: 'Hasta 250 Recetas Maestras',
    artisanFeature3: 'Costeo de 6 Decimales para cada ingrediente',
    artisanScalingHook: 'Crece a tu ritmo: +$2.99/mes por cada 250 recetas adicionales.',
    artisanCta: 'Comenzar Artesano',
    professionalTier: 'Profesional',
    professionalPrice: '$5.99',
    professionalPeriod: '/mes',
    professionalDesc: 'El Motor Ejecutivo para Control de Ganancias',
    professionalBridge: 'Todo en Artesano, más:',
    professionalFeature1: 'Recetas Maestras Ilimitadas',
    professionalFeature2: 'Construcción de Menú Avanzada y Precios Dinámicos',
    professionalFeature3: 'Compartir Menú en Múltiples Dispositivos',
    professionalFeature4: 'Análisis de Ganancias en Tiempo Real y Rastreo Multi-departamental',
    professionalFeature5: 'Exportación PDF e Informes Profesionales',
    professionalCta: 'Comenzar Profesional',
    popularBadge: 'Más Popular',
    
    // PWA Utility Bar
    utilityNoApp: 'No Se Necesita App Store',
    utilityOffline: 'Funciona Sin Internet en Cocina',
    utilitySync: 'Sincronización Instantánea Multi-Dispositivo',
    
    // Specialist Verticals
    specialistTitle: 'Creado para Cada Especialista Culinario',
    mixologyTitle: 'El Laboratorio de Mixología',
    mixologyDesc: 'Costeo preciso para cada vertido, desde cócteles artesanales hasta arte del café.',
    mixologyUnit: 'por ml',
    pastryTitle: 'El Artista Pastelero',
    pastryDesc: 'Seguimiento de micromediciones para postres delicados y repostería.',
    pastryUnit: 'por gramo',
    spiceTitle: 'El Mezclador de Especias',
    spiceDesc: 'Protege tus mezclas secretas con precisión a nivel de ingrediente.',
    spiceUnit: 'por gramo',
    
    // Problem/Solution
    problemTitle: 'La Brecha Oculta en Tu Cocina',
    problemSubtitle: 'La mayoría de los chefs adivinan los precios. Te ayudamos a saber.',
    problemCard1Title: 'Costos Crecientes de Ingredientes',
    problemCard1Desc: 'Los precios cambian a diario, pero tu menú sigue igual. ¿Aún eres rentable?',
    problemCard2Title: 'Misterio de Precios del Menú',
    problemCard2Desc: '¿Cuánto cuesta realmente cada plato? Sin datos precisos, vuelas a ciegas.',
    problemCard3Title: 'Tiempo y Dinero Desperdiciados',
    problemCard3Desc: 'Hojas de cálculo manuales, costos desactualizados y suposiciones reducen los márgenes.',
    
    solutionTitle: 'PlateProfit: Tu Centro de Comando de Recetas y Costos',
    solutionDesc: 'Rastrea cada ingrediente, calcula costos exactos de recetas y fija precios de menú rentables—todo en una plataforma.',
    
    // Personas
    personasTitle: 'Construido para Cada Viaje Culinario',
    
    individualTitle: 'Ama de Casa Individual',
    individualSubtitle: 'Perfecto para cocineros caseros apasionados',
    individualFeature1: 'Almacena recetas ilimitadas',
    individualFeature2: 'Rastrea costos de ingredientes',
    individualFeature3: 'Calcula costos por porción',
    individualFeature4: 'Gestión básica de recetas',
    individualCta: 'Comenzar como Individual',
    
    professionalTitle: 'Chef Profesional',
    professionalSubtitle: 'Para restaurantes y negocios de comida',
    professionalSpectrum: 'Desde cocinas con estrellas Michelin hasta comida callejera global—PlateProfit es tu centro de comando.',
    professionalFeature1: 'Todo lo de Individual, más:',
    professionalFeature2: 'Precios avanzados de menú',
    professionalFeature3: 'Seguimiento multidepartamental',
    professionalFeature4: 'Análisis de ganancias en tiempo real',
    professionalFeature5: 'Exportación PDF y compartir',
    professionalCta: 'Hazte Profesional',
    
    // Features
    featuresTitle: 'Todo lo que Necesitas para una Cocina Rentable',
    
    feature1Title: 'Banco de Recetas',
    feature1Desc: 'Almacena, organiza y gestiona todas tus recetas en un solo lugar. Nunca pierdas una receta.',
    
    feature2Title: 'Recetas Activas/Inactivas',
    feature2Desc: 'Activa o desactiva recetas según la temporada o disponibilidad. Mantén tu menú fresco.',
    
    feature3Title: 'Seguimiento de Costos en Tiempo Real',
    feature3Desc: 'Actualiza los precios de ingredientes una vez y ve los cambios de costos en todas las recetas al instante.',
    
    feature4Title: 'Inteligencia de Precios del Menú',
    feature4Desc: 'Establece márgenes objetivo y obtén recomendaciones instantáneas para precios de menú rentables.',
    
    feature5Title: 'Soporte Multimoneda',
    feature5Desc: 'Trabaja en tu moneda local. Soportamos USD, EUR, QAR, INR y más de 20 monedas.',
    
    feature6Title: 'Seguimiento de Departamentos',
    feature6Desc: 'Organiza recetas por Cocina, Bar, Pastelería o departamentos personalizados para mejor control.',
    
    // Footer
    footerCta: '¿Listo para Transformar Tu Cocina?',
    footerCtaButton: 'Comienza Tu Cuenta Gratis',
    footerTagline: 'PlateProfit — Precisión en Cada Porción',
  },
  
  fr: {
    // Header
    languageLabel: 'Langue',
    
    // Hero Section
    heroTitle: 'Du Garde-Manger à l\'Assiette,',
    heroSubtitle: 'Profit sur Chaque Milligramme.',
    heroDescription: 'L\'écosystème complet pour les professionnels culinaires',
    heroPrecision: 'Précision à 6 Décimales. Chaque Milligramme Compte.',
    ctaButton: 'Dès $2.99/mois',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'Sauvez les Secrets de Grand-mère pour Toujours.',
    leftHeroSubtext: 'Créez et protégez votre Banque de Recettes Maîtresse avec précision à 6 décimales.',
    leftHeroCta: 'Commencer Artisan ($2.99/mois)',
    rightHeroTitle: 'Rentabilité du Garde-Manger à l\'Assiette.',
    rightHeroSubtext: 'Le moteur du Chef Exécutif pour contrôle 100% de marge.',
    rightHeroCta: 'Passer Pro ($5.99/mois)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: 'Ingrédients Bruts',
    precisionTracking: 'Approvisionnement précis, suivi quotidien',
    engineTitle: 'Moteur PlateProfit',
    engineSubtitle: 'Suivi de précision à 6 décimales',
    unitCostLabel: 'Précision du Coût Unitaire',
    perGramLabel: 'par Gramme',
    noWastageLabel: 'Aucun gaspillage caché. Chaque milligramme suivi.',
    platedLabel: 'Perfection dans l\'Assiette',
    profitableLabel: 'Rentable, à chaque fois',
    joinChefs: 'Rejoignez des chefs dans plus de 150 pays suivant chaque milligramme',
    
    // Pricing Section
    pricingTitle: 'Tarifs Transparents',
    pricingSubtitle: 'Moins cher qu\'une tasse de café pour un mois de précision.',
    artisanTier: 'Artisan',
    artisanPrice: '$2.99',
    artisanPeriod: '/mois',
    artisanDesc: 'La Banque de Recettes Maîtresses—Prête pour l\'Héritage',
    artisanFeature1: 'La Banque de Recettes Maîtresses (précision à 6 décimales)',
    artisanFeature2: 'Jusqu\'à 250 Recettes Maîtresses',
    artisanFeature3: 'Coût à 6 Décimales pour chaque ingrédient',
    artisanScalingHook: 'Grandissez à votre rythme: +$2.99/mois pour chaque 250 recettes supplémentaires.',
    artisanCta: 'Commencer Artisan',
    professionalTier: 'Professionnel',
    professionalPrice: '$5.99',
    professionalPeriod: '/mois',
    professionalDesc: 'Le Moteur Exécutif pour le Contrôle des Profits',
    professionalBridge: 'Tout en Artisan, plus:',
    professionalFeature1: 'Recettes Maîtresses Illimitées',
    professionalFeature2: 'Construction de Menu Avancée et Tarification Dynamique',
    professionalFeature3: 'Partage de Menu sur Plusieurs Appareils',
    professionalFeature4: 'Analyse de Profit en Temps Réel et Suivi Multi-départemental',
    professionalFeature5: 'Export PDF et Rapports Professionnels',
    professionalCta: 'Commencer Professionnel',
    popularBadge: 'Le Plus Populaire',
    
    // PWA Utility Bar
    utilityNoApp: 'Pas Besoin de l\'App Store',
    utilityOffline: 'Fonctionne Hors Ligne en Cuisine',
    utilitySync: 'Synchronisation Instantanée Multi-Appareils',
    
    // Specialist Verticals
    specialistTitle: 'Conçu pour Chaque Spécialiste Culinaire',
    mixologyTitle: 'Le Laboratoire de Mixologie',
    mixologyDesc: 'Coûts précis pour chaque versement, des cocktails artisanaux à l\'art du café.',
    mixologyUnit: 'par ml',
    pastryTitle: 'L\'Artiste Pâtissier',
    pastryDesc: 'Suivi des micro-mesures pour desserts délicats et pâtisserie.',
    pastryUnit: 'par gramme',
    spiceTitle: 'Le Mélangeur d\'Épices',
    spiceDesc: 'Protégez vos mélanges secrets avec précision au niveau des ingrédients.',
    spiceUnit: 'par gramme',
    
    // Problem/Solution
    problemTitle: 'L\'Écart Caché dans Votre Cuisine',
    problemSubtitle: 'La plupart des chefs devinent les prix. Nous vous aidons à savoir.',
    problemCard1Title: 'Coûts Croissants des Ingrédients',
    problemCard1Desc: 'Les prix changent quotidiennement, mais votre menu reste le même. Êtes-vous toujours rentable?',
    problemCard2Title: 'Mystère des Prix du Menu',
    problemCard2Desc: 'Combien coûte réellement chaque plat? Sans données précises, vous volez à l\'aveugle.',
    problemCard3Title: 'Temps et Argent Gaspillés',
    problemCard3Desc: 'Les feuilles de calcul manuelles, les coûts obsolètes et les approximations réduisent les marges.',
    
    solutionTitle: 'PlateProfit: Votre Centre de Commande Recettes et Coûts',
    solutionDesc: 'Suivez chaque ingrédient, calculez les coûts exacts des recettes et fixez des prix de menu rentables—le tout sur une seule plateforme.',
    
    // Personas
    personasTitle: 'Conçu pour Chaque Parcours Culinaire',
    
    individualTitle: 'Ménagère Individuelle',
    individualSubtitle: 'Parfait pour les cuisiniers passionnés',
    individualFeature1: 'Stockez des recettes illimitées',
    individualFeature2: 'Suivez les coûts des ingrédients',
    individualFeature3: 'Calculez les coûts par portion',
    individualFeature4: 'Gestion basique des recettes',
    individualCta: 'Commencer en Individuel',
    
    professionalTitle: 'Chef Professionnel',
    professionalSubtitle: 'Pour les restaurants et entreprises alimentaires',
    professionalSpectrum: 'Des cuisines étoilées Michelin à la street food mondiale—PlateProfit est votre centre de commande.',
    professionalFeature1: 'Tout ce qui est dans Individuel, plus:',
    professionalFeature2: 'Prix avancés du menu',
    professionalFeature3: 'Suivi multidépartemental',
    professionalFeature4: 'Analyse des profits en temps réel',
    professionalFeature5: 'Export PDF et partage',
    professionalCta: 'Devenir Professionnel',
    
    // Features
    featuresTitle: 'Tout ce dont Vous Avez Besoin pour une Cuisine Rentable',
    
    feature1Title: 'Banque de Recettes',
    feature1Desc: 'Stockez, organisez et gérez toutes vos recettes en un seul endroit. Ne perdez plus jamais une recette.',
    
    feature2Title: 'Recettes Actives/Inactives',
    feature2Desc: 'Activez ou désactivez les recettes selon la saisonnalité ou la disponibilité. Gardez votre menu frais.',
    
    feature3Title: 'Suivi des Coûts en Temps Réel',
    feature3Desc: 'Mettez à jour les prix des ingrédients une fois et voyez les changements de coûts dans toutes les recettes instantanément.',
    
    feature4Title: 'Intelligence de Prix du Menu',
    feature4Desc: 'Définissez des marges cibles et obtenez des recommandations instantanées pour des prix de menu rentables.',
    
    feature5Title: 'Support Multidevises',
    feature5Desc: 'Travaillez dans votre devise locale. Nous supportons USD, EUR, QAR, INR et plus de 20 devises.',
    
    feature6Title: 'Suivi des Départements',
    feature6Desc: 'Organisez les recettes par Cuisine, Bar, Pâtisserie ou départements personnalisés pour un meilleur contrôle.',
    
    // Footer
    footerCta: 'Prêt à Transformer Votre Cuisine?',
    footerCtaButton: 'Commencez Votre Compte Gratuit',
    footerTagline: 'PlateProfit — Précision dans Chaque Portion',
  },
  
  hi: {
    // Header
    languageLabel: 'भाषा',
    
    // Hero Section
    heroTitle: 'पेंट्री से प्लेट तक,',
    heroSubtitle: 'हर मिलीग्राम में मुनाफा.',
    heroDescription: 'पाक पेशेवरों के लिए संपूर्ण पारिस्थितिकी तंत्र',
    heroPrecision: '6-दशमलव सटीकता. हर मिलीग्राम मायने रखता है.',
    ctaButton: '$2.99/माह से शुरू करें',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'दादी की रेसिपी हमेशा के लिए सुरक्षित रखें.',
    leftHeroSubtext: '6-दशमलव सटीकता के साथ अपना मास्टर रेसिपी बैंक बनाएं और सुरक्षित रखें.',
    leftHeroCta: 'शिल्पकार शुरू करें ($2.99/माह)',
    rightHeroTitle: 'पेंट्री से प्लेट तक लाभप्रदता.',
    rightHeroSubtext: 'एग्जीक्यूटिव शेफ का इंजन 100% मार्जिन नियंत्रण के लिए.',
    rightHeroCta: 'पेशेवर बनें ($5.99/माह)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: 'कच्ची सामग्री',
    precisionTracking: 'सटीक सोर्सिंग, दैनिक ट्रैक किया गया',
    engineTitle: 'PlateProfit इंजन',
    engineSubtitle: '6-दशमलव सटीकता ट्रैकिंग',
    unitCostLabel: 'यूनिट-लागत सटीकता',
    perGramLabel: 'प्रति ग्राम',
    noWastageLabel: 'कोई छिपी बर्बादी नहीं. हर मिलीग्राम ट्रैक किया गया.',
    platedLabel: 'प्लेटेड पूर्णता',
    profitableLabel: 'लाभदायक, हर बार',
    joinChefs: '150+ देशों में हर मिलीग्राम को ट्रैक करने वाले शेफ में शामिल हों',
    
    // Pricing Section
    pricingTitle: 'पारदर्शी मूल्य निर्धारण',
    pricingSubtitle: 'एक महीने की सटीकता के लिए एक कप कॉफी की लागत से कम.',
    artisanTier: 'शिल्पकार',
    artisanPrice: '$2.99',
    artisanPeriod: '/माह',
    artisanDesc: 'मास्टर रेसिपी बैंक—विरासत के लिए तैयार',
    artisanFeature1: 'मास्टर रेसिपी बैंक (6-दशमलव सटीकता)',
    artisanFeature2: '250 तक मास्टर रेसिपी',
    artisanFeature3: 'हर सामग्री के लिए 6-दशमलव लागत',
    artisanScalingHook: 'अपनी गति से बढ़ें: हर 250 अतिरिक्त रेसिपी के लिए +$2.99/माह।',
    artisanCta: 'आर्टिसन शुरू करें',
    professionalTier: 'प्रोफेशनल',
    professionalPrice: '$5.99',
    professionalPeriod: '/माह',
    professionalDesc: 'लाभ नियंत्रण के लिए कार्यकारी इंजन',
    professionalBridge: 'आर्टिसन में सब कुछ, साथ ही:',
    professionalFeature1: 'असीमित मास्टर रेसिपी',
    professionalFeature2: 'उन्नत मेनू निर्माण और गतिशील मूल्य निर्धारण',
    professionalFeature3: 'कई उपकरणों में मेनू साझाकरण',
    professionalFeature4: 'रीयल-टाइम लाभ विश्लेषण और बहु-विभाग ट्रैकिंग',
    professionalFeature5: 'PDF निर्यात और पेशेवर रिपोर्टिंग',
    professionalCta: 'प्रोफेशनल शुरू करें',
    popularBadge: 'सबसे लोकप्रिय',
    
    // PWA Utility Bar
    utilityNoApp: 'ऐप स्टोर की जरूरत नहीं',
    utilityOffline: 'रसोई में बिना इंटरनेट काम करता है',
    utilitySync: 'तुरंत मल्टी-डिवाइस सिंक',
    
    // Specialist Verticals
    specialistTitle: 'हर पाक विशेषज्ञ के लिए बनाया गया',
    mixologyTitle: 'मिक्सोलॉजी लैब',
    mixologyDesc: 'हर डालने की सटीक लागत, क्राफ्ट कॉकटेल से कॉफी कला तक.',
    mixologyUnit: 'प्रति मिली',
    pastryTitle: 'पेस्ट्री कलाकार',
    pastryDesc: 'नाजुक मिठाइयों और बेकिंग के लिए सूक्ष्म-माप ट्रैकिंग.',
    pastryUnit: 'प्रति ग्राम',
    spiceTitle: 'मसाला मिश्रक',
    spiceDesc: 'सामग्री-स्तर की सटीकता के साथ अपने गुप्त मिश्रणों की रक्षा करें.',
    spiceUnit: 'प्रति ग्राम',
    
    // Problem/Solution
    problemTitle: 'आपकी रसोई में छिपी खाई',
    problemSubtitle: 'अधिकांश शेफ कीमतों का अनुमान लगाते हैं। हम आपको जानने में मदद करते हैं।',
    problemCard1Title: 'बढ़ती सामग्री लागत',
    problemCard1Desc: 'कीमतें दैनिक बदलती हैं, लेकिन आपका मेनू वही रहता है। क्या आप अभी भी लाभदायक हैं?',
    problemCard2Title: 'मेनू मूल्य निर्धारण रहस्य',
    problemCard2Desc: 'प्रत्येक व्यंजन की वास्तव में कितनी लागत है? सटीक डेटा के बिना, आप अंधे उड़ रहे हैं।',
    problemCard3Title: 'समय और पैसे की बर्बादी',
    problemCard3Desc: 'मैनुअल स्प्रेडशीट, पुरानी लागत, और अनुमान से मार्जिन कम होता है।',
    
    solutionTitle: 'PlateProfit: आपका रेसिपी और लागत कमांड सेंटर',
    solutionDesc: 'प्रत्येक सामग्री को ट्रैक करें, सटीक रेसिपी लागत की गणना करें, और लाभदायक मेनू कीमतें निर्धारित करें—सभी एक प्लेटफॉर्म में।',
    
    // Personas
    personasTitle: 'हर पाक यात्रा के लिए बनाया गया',
    
    individualTitle: 'व्यक्तिगत गृहिणी',
    individualSubtitle: 'भावुक घरेलू रसोइयों के लिए एकदम सही',
    individualFeature1: 'असीमित व्यंजन संग्रहीत करें',
    individualFeature2: 'सामग्री लागत ट्रैक करें',
    individualFeature3: 'सर्विंग लागत की गणना करें',
    individualFeature4: 'बुनियादी रेसिपी प्रबंधन',
    individualCta: 'व्यक्तिगत के रूप में शुरू करें',
    
    professionalTitle: 'पेशेवर शेफ',
    professionalSubtitle: 'रेस्तरां और खाद्य व्यवसायों के लिए',
    professionalSpectrum: 'मिशेलिन-स्टार रसोई से लेकर वैश्विक स्ट्रीट फूड तक—PlateProfit आपका कमांड सेंटर है।',
    professionalFeature1: 'व्यक्तिगत में सब कुछ, साथ ही:',
    professionalFeature2: 'उन्नत मेनू मूल्य निर्धारण',
    professionalFeature3: 'बहु-विभाग ट्रैकिंग',
    professionalFeature4: 'वास्तविक समय लाभ विश्लेषण',
    professionalFeature5: 'पीडीएफ निर्यात और साझाकरण',
    professionalCta: 'पेशेवर बनें',
    
    // Features
    featuresTitle: 'लाभदायक रसोई चलाने के लिए आपको जो कुछ भी चाहिए',
    
    feature1Title: 'रेसिपी बैंक',
    feature1Desc: 'अपनी सभी रेसिपी एक जगह पर स्टोर, व्यवस्थित और प्रबंधित करें। फिर कभी रेसिपी न खोएं।',
    
    feature2Title: 'सक्रिय/निष्क्रिय रेसिपी',
    feature2Desc: 'मौसमी या उपलब्धता के आधार पर रेसिपी टॉगल करें। अपने मेनू को ताजा रखें।',
    
    feature3Title: 'वास्तविक समय लागत ट्रैकिंग',
    feature3Desc: 'सामग्री की कीमतें एक बार अपडेट करें, और सभी रेसिपी में लागत परिवर्तन तुरंत देखें।',
    
    feature4Title: 'मेनू मूल्य निर्धारण बुद्धिमत्ता',
    feature4Desc: 'लक्ष्य मार्जिन सेट करें और लाभदायक मेनू मूल्य निर्धारण के लिए तत्काल सिफारिशें प्राप्त करें।',
    
    feature5Title: 'बहु-मुद्रा समर्थन',
    feature5Desc: 'अपनी स्थानीय मुद्रा में काम करें। हम USD, EUR, QAR, INR और 20+ मुद्राओं का समर्थन करते हैं।',
    
    feature6Title: 'विभाग ट्रैकिंग',
    feature6Desc: 'बेहतर नियंत्रण के लिए रसोई, बार, पेस्ट्री, या कस्टम विभागों द्वारा रेसिपी व्यवस्थित करें।',
    
    // Footer
    footerCta: 'अपनी रसोई को बदलने के लिए तैयार हैं?',
    footerCtaButton: 'अपना मुफ्त खाता शुरू करें',
    footerTagline: 'PlateProfit — हर हिस्से में सटीकता',
  },
  bn: {
    languageLabel: 'ভাষা',
    heroTitle: 'প্যান্ট্রি থেকে প্লেট পর্যন্ত,',
    heroSubtitle: 'প্রতি মিলিগ্রামে লাভ।',
    heroDescription: 'রান্নার পেশাদারদের জন্য সম্পূর্ণ ইকোসিস্টেম।',
    heroPrecision: '6-দশমিক নির্ভুলতা। প্রতিটি মিলিগ্রাম গণনা করা হয়।',
    ctaButton: 'মাত্র 2.99 মার্কিন ডলার/মাস থেকে শুরু।',
    leftHeroTitle: 'দাদীর গোপন কথাগুলো চিরকাল বাঁচিয়ে রাখো।',
    leftHeroSubtext: '6-দশমিক নির্ভুলতার সঙ্গে আপনার মাস্টার রেসিপি ব্যাংক তৈরি করুন এবং সুরক্ষিত রাখুন।',
    leftHeroCta: 'আর্টিজান শুরু করুন ($2.99/মাস)',
    rightHeroTitle: 'প্যান্ট্রি থেকে প্লেট পর্যন্ত লাভজনকতা।',
    rightHeroSubtext: 'এক্সিকিউটিভ শেফের 100% মার্জিন নিয়ন্ত্রণের ইঞ্জিন।',
    rightHeroCta: 'গো প্রফেশনাল ($5.99/মাস)',
    rawIngredientsLabel: 'কাঁচা উপকরণ',
    precisionTracking: 'নির্ভুল উৎস, প্রতিদিন নজরে রাখা হয়।',
    engineTitle: 'প্লেটপ্রফিট ইঞ্জিন',
    engineSubtitle: '6-দশমিক নির্ভুলতা ট্র্যাকিং',
    unitCostLabel: 'ইউনিট-খরচ নির্ভুলতা',
    perGramLabel: 'প্রতি গ্রাম',
    noWastageLabel: 'কোনো লুকানো অপচয় নেই। প্রতিটি মিলিগ্রামের হিসাব রাখা হয়।',
    platedLabel: 'প্লেটেড পারফেকশন',
    profitableLabel: 'লাভজনক, প্রতিবার।',
    joinChefs: '150টিরও বেশি দেশে শেফদের সাথে যোগ দিন, যারা প্রতিটি মিলিগ্রামের হিসাব রাখেন।',
    pricingTitle: 'স্বচ্ছ মূল্য নির্ধারণ',
    pricingSubtitle: 'এক মাসের নির্ভুলতার জন্য এক কাপ কফির দামের চেয়েও কম।',
    artisanTier: 'কারিগর',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'মাস্টার রেসিপি ব্যাংক—ঐতিহ্য-প্রস্তুত',
    artisanFeature1: 'মাস্টার রেসিপি ব্যাংক (6-দশমিক নির্ভুলতা)',
    artisanFeature2: '250টি পর্যন্ত মাস্টার রেসিপি',
    artisanFeature3: 'প্রতিটি উপাদানের জন্য 6-দশমিকের মূল্য নির্ধারণ।',
    artisanScalingHook: 'আপনি যত বাড়বেন: প্রতি অতিরিক্ত 250টি রেসিপির জন্য +$2.99/মাস।',
    artisanCta: 'কারিগর শুরু করুন',
    professionalTier: 'পেশাদার',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'মুনাফা নিয়ন্ত্রণের নির্বাহী ইঞ্জিন',
    professionalBridge: 'কারিগরীর সবকিছু, সাথে:',
    professionalFeature1: 'ব্যক্তিগতভাবে সবকিছু, সাথে:',
    professionalFeature2: 'উন্নত মেনু মূল্য নির্ধারণ',
    professionalFeature3: 'বহু-বিভাগীয় ট্র্যাকিং',
    professionalFeature4: 'রিয়েল-টাইম লাভ বিশ্লেষণ',
    professionalFeature5: 'পিডিএফ এক্সপোর্ট ও শেয়ারিং',
    professionalCta: 'পেশাদার হোন।',
    popularBadge: 'সবচেয়ে জনপ্রিয়',
    utilityNoApp: 'কোন অ্যাপ স্টোরের প্রয়োজন নেই',
    utilityOffline: 'রান্নাঘর-প্রুফ অফলাইন',
    utilitySync: 'তাত্ক্ষণিক মাল্টি-ডিভাইস সিঙ্ক',
    specialistTitle: 'প্রত্যেক রন্ধন বিশেষজ্ঞের জন্য নির্মিত।',
    mixologyTitle: 'মিশ্রণবিদ্যা ল্যাব',
    mixologyDesc: 'ক্রাফট ককটেল থেকে শুরু করে কফি আর্ট পর্যন্ত, প্রতিটি ঢালার জন্য নির্ভুল মূল্য নির্ধারণ।',
    mixologyUnit: 'প্রতি মিলি',
    pastryTitle: 'পেস্ট্রি শিল্পী',
    pastryDesc: 'সুক্ষ্ম ডেজার্ট এবং বেকিং-এর জন্য মাইক্রো-মেজারমেন্ট ট্র্যাকিং।',
    pastryUnit: 'প্রতি গ্রাম',
    spiceTitle: 'মশলার ব্লেন্ডার',
    spiceDesc: 'উপাদান-স্তরের নির্ভুলতার সাথে আপনার গোপন মিশ্রণগুলি রক্ষা করুন।',
    spiceUnit: 'প্রতি গ্রাম',
    problemTitle: 'আপনার রান্নাঘরের লুকানো ফাঁক',
    problemSubtitle: 'বেশিরভাগ রাঁধুনি দাম অনুমান করে। আমরা আপনাকে জানতে সাহায্য করি।',
    problemCard1Title: 'উপকরণ মূল্য বৃদ্ধি',
    problemCard1Desc: 'দাম প্রতিদিন পরিবর্তিত হয়, কিন্তু আপনার মেনু একই থাকে। আপনি কি এখনও লাভজনক?',
    problemCard2Title: 'মেনু মূল্যের রহস্য',
    problemCard2Desc: 'প্রতিটি পদের দাম আসলে কত? সঠিক তথ্য ছাড়া, আপনি অন্ধকারে চালাচ্ছেন।',
    problemCard3Title: 'নষ্ট হওয়া সময় ও অর্থ',
    problemCard3Desc: 'হাতে লেখা স্প্রেডশীট, পুরনো দিনের খরচ, এবং আন্দাজের ফলে লাভের পরিমাণ কমতে থাকে।',
    solutionTitle: 'প্লেটপ্রফিট: আপনার রেসিপি ও কস্ট কমান্ড সেন্টার',
    solutionDesc: 'প্রত্যেকটি উপাদানের হিসাব রাখুন, সঠিক রেসিপির খরচ হিসাব করুন, এবং লাভজনক মেনুর দাম নির্ধারণ করুন—সবকিছু একই প্ল্যাটফর্মে।',
    personasTitle: 'প্রত্যেক রন্ধন-যাত্রার জন্য নির্মিত।',
    individualTitle: 'স্বতন্ত্র গৃহিণী',
    individualSubtitle: 'অনুরাগী হোম কুকদের জন্য উপযুক্ত।',
    individualFeature1: 'সীমাহীন রেসিপি সংরক্ষণ করুন',
    individualFeature2: 'উপাদানের খরচ ট্র্যাক করুন',
    individualFeature3: 'পরিবেশন খরচ হিসাব করুন।',
    individualFeature4: 'বেসিক রেসিপি ম্যানেজমেন্ট',
    individualCta: 'ব্যক্তিগতভাবে শুরু করুন।',
    professionalTitle: 'পেশাদার রন্ধনশিল্পী',
    professionalSubtitle: 'রেস্তোরাঁ এবং খাদ্য ব্যবসার জন্য',
    professionalSpectrum: 'মিশেলিন-তারকা রান্নাঘর থেকে শুরু করে বিশ্বব্যাপী রাস্তার খাবার—প্লেটপ্রফিট হল আপনার কমান্ড সেন্টার।',
    featuresTitle: 'একটি লাভজনক রান্নাঘর চালানোর জন্য আপনার যা কিছু দরকার।',
    feature1Title: 'রেসিপি ব্যাংক',
    feature1Desc: 'আপনার সমস্ত রেসিপি এক জায়গায় সংরক্ষণ করুন, সাজান এবং পরিচালনা করুন। আর কখনও রেসিপি হারাবেন না।',
    feature2Title: 'সক্রিয়/নিষ্ক্রিয় রেসিপি',
    feature2Desc: 'ঋতু বা প্রাপ্যতার উপর ভিত্তি করে রেসিপিগুলি চালু এবং বন্ধ করুন। আপনার মেনু সতেজ রাখুন।',
    feature3Title: 'রিয়েল-টাইম খরচ ট্র্যাকিং',
    feature3Desc: 'উপাদানের দাম একবার আপডেট করুন, এবং সমস্ত রেসিপি জুড়ে তাৎক্ষণিকভাবে খরচের পরিবর্তন দেখুন।',
    feature4Title: 'মেনু মূল্য সংক্রান্ত বুদ্ধিমত্তা',
    feature4Desc: 'লক্ষ্যমাত্রা নির্ধারণ করুন এবং লাভজনক মেনু মূল্যের জন্য তাৎক্ষণিক সুপারিশ পান।',
    feature5Title: 'বহু-মুদ্রা সমর্থন',
    feature5Desc: 'আপনার স্থানীয় মুদ্রায় কাজ করুন। আমরা USD, EUR, QAR, INR, এবং আরও 20টির বেশি সমর্থন করি।',
    feature6Title: 'বিভাগীয় নিরীক্ষণ',
    feature6Desc: 'আরও ভাল নিয়ন্ত্রণের জন্য রান্নাঘর, বার, পেস্ট্রি বা কাস্টম বিভাগ অনুসারে রেসিপিগুলি সাজান।',
    footerCta: 'আপনার রান্নাঘর পরিবর্তন করতে প্রস্তুত?',
    footerCtaButton: 'বিনামূল্যে আপনার অ্যাকাউন্ট শুরু করুন',
    footerTagline: 'প্লেটপ্রফিট — প্রতিটি অংশে সূক্ষ্মতা',
  },
  gu: {
    languageLabel: 'ભાષા',
    heroTitle: 'રસોડાથી થાળી સુધી,',
    heroSubtitle: 'દરેક મિલિગ્રામ પર નફો.',
    heroDescription: 'રસોઈના વ્યવસાયિકો માટે સંપૂર્ણ ઇકોસિસ્ટમ',
    heroPrecision: '6-દશાંશ ચોકસાઈ. દરેક મિલિગ્રામ મહત્વપૂર્ણ છે.',
    ctaButton: 'માસિક $2.99 જેટલા ઓછાથી શરૂ કરો.',
    leftHeroTitle: 'દાદીમાના રહસ્યોને હંમેશ માટે સાચવો.',
    leftHeroSubtext: '6-દશાંશ ચોકસાઈ સાથે તમારી માસ્ટર રેસીપી બેંક બનાવો અને તેનું રક્ષણ કરો.',
    leftHeroCta: 'આર્ટિસન શરૂ કરો ($2.99/મહિનો)',
    rightHeroTitle: 'પેન્ટ્રીથી પ્લેટ નફાકારકતા.',
    rightHeroSubtext: 'એક્ઝિક્યુટિવ શેફનું 100% માર્જિન નિયંત્રણ માટેનું એન્જિન.',
    rightHeroCta: 'ગો પ્રોફેશનલ ($5.99/મહિના)',
    rawIngredientsLabel: 'કાચી સામગ્રી',
    precisionTracking: 'ચોકસાઇ સોર્સિંગ, દરરોજ ટ્રેક કરવામાં આવે છે',
    engineTitle: 'પ્લેટપ્રોફિટ એન્જિન',
    engineSubtitle: '6-દશાંશ ચોકસાઈ ટ્રેકિંગ',
    unitCostLabel: 'એકમ-ખર્ચ ચોકસાઈ',
    perGramLabel: 'ગ્રામ દીઠ',
    noWastageLabel: 'કોઈ છુપાયેલ બગાડ નહીં. દરેક મિલિગ્રામ ટ્રેક કરવામાં આવે છે.',
    platedLabel: 'પ્લેટેડ પરફેક્શન',
    profitableLabel: 'લાભદાયી, દર વખતે',
    joinChefs: '150+ થી વધુ દેશોમાં રસોઈયાઓ સાથે જોડાઓ જે દરેક મિલિગ્રામને ટ્રેક કરે છે.',
    pricingTitle: 'પારદર્શક કિંમત નિર્ધારણ',
    pricingSubtitle: 'એક મહિનાની ચોકસાઈ માટે એક કપ કોફીની કિંમત કરતાં ઓછી.',
    artisanTier: 'કારીગર',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'The Master Recipe Bank—Heritage-ready',
    artisanFeature1: 'માસ્ટર રેસીપી બેંક (6-દશાંશ ચોકસાઈ)',
    artisanFeature2: 'Up to 250 Master Recipes',
    artisanFeature3: 'દરેક ઘટક માટે 6-દશાંશ ખર્ચ',
    artisanScalingHook: 'જેમ જેમ તમે વધો તેમ તેમ: દરેક વધારાની 250 વાનગીઓ માટે +$2.99/મહિના.',
    artisanCta: 'Start Artisan',
    professionalTier: 'વ્યવસાયિક',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'નફા નિયંત્રણ માટેનું એક્ઝિક્યુટિવ એન્જિન',
    professionalBridge: 'આર્ટિસનમાં બધું જ, વત્તા:',
    professionalFeature1: 'વ્યક્તિગત રીતે બધું, વત્તા:',
    professionalFeature2: 'અદ્યતન મેનુ કિંમત નિર્ધારણ',
    professionalFeature3: 'બહુ-વિભાગ ટ્રેકિંગ',
    professionalFeature4: 'રીઅલ-ટાઇમ નફાનું વિશ્લેષણ',
    professionalFeature5: 'PDF નિકાસ અને શેરિંગ',
    professionalCta: 'વ્યવસાયિક બનો',
    popularBadge: 'સૌથી વધુ લોકપ્રિય',
    utilityNoApp: 'કોઈ એપ સ્ટોરની જરૂર નથી',
    utilityOffline: 'રસોડું-પ્રૂફ ઓફલાઇન',
    utilitySync: 'ઝડપી મલ્ટી-ડિવાઇસ સિંક',
    specialistTitle: 'દરેક રસોઈ નિષ્ણાત માટે બનાવેલ',
    mixologyTitle: 'મિક્સોલોજી લેબ',
    mixologyDesc: 'દરેક રેડવા માટે ચોકસાઇ કિંમત, ક્રાફ્ટ કોકટેલથી લઈને કોફી આર્ટ સુધી.',
    mixologyUnit: 'પ્રતિ મિલી',
    pastryTitle: 'પેસ્ટ્રી આર્ટિસ્ટ',
    pastryDesc: 'નાજુક મીઠાઈઓ અને બેકિંગ માટે માઇક્રો-માપન ટ્રેકિંગ.',
    pastryUnit: 'પ્રતિ ગ્રામ',
    spiceTitle: 'મસાલાનું મિશ્રણ',
    spiceDesc: 'ઘટકોના સ્તરની ચોકસાઈ સાથે તમારા ગુપ્ત મિશ્રણોનું રક્ષણ કરો.',
    spiceUnit: 'પ્રતિ ગ્રામ',
    problemTitle: 'તમારા રસોડામાં છુપાયેલું ગાબડું',
    problemSubtitle: 'મોટાભાગના રસોઈયાઓ કિંમતનો અંદાજ લગાવે છે. અમે તમને જાણવામાં મદદ કરીએ છીએ.',
    problemCard1Title: 'વધતા ઘટકોના ખર્ચ',
    problemCard1Desc: 'ભાવ રોજ બદલાય છે, પણ તમારું મેનુ એ જ રહે છે. શું તમે હજી પણ નફો કરો છો?',
    problemCard2Title: 'મેનુ પ્રાઇસિંગ મિસ્ટ્રી',
    problemCard2Desc: 'દરેક વાનગીની કિંમત ખરેખર કેટલી છે? ચોક્કસ માહિતી વિના, તમે આંધળા થઈને ઉડી રહ્યા છો.',
    problemCard3Title: 'વેડફાયેલો સમય અને પૈસા',
    problemCard3Desc: 'મેન્યુઅલ સ્પ્રેડશીટ્સ, જૂના ખર્ચાઓ અને અનુમાન લગાવવાથી માર્જિન ઘટે છે.',
    solutionTitle: 'પ્લેટપ્રોફિટ: તમારી રેસીપી અને કોસ્ટ કમાન્ડ સેન્ટર',
    solutionDesc: 'દરેક ઘટકને ટ્રૅક કરો, ચોક્કસ રેસીપી ખર્ચની ગણતરી કરો અને નફાકારક મેનુ કિંમતો સેટ કરો—એક જ પ્લેટફોર્મમાં.',
    personasTitle: 'દરેક રાંધણ યાત્રા માટે બનાવેલ',
    individualTitle: 'એકલ વ્યક્તિગત ગૃહ નિર્માતા',
    individualSubtitle: 'ઘરના રસોઈયાઓ માટે ઉત્તમ.',
    individualFeature1: 'અમર્યાદિત વાનગીઓ સ્ટોર કરો',
    individualFeature2: 'ઘટકોના ખર્ચને ટ્રૅક કરો.',
    individualFeature3: 'સેવા ખર્ચની ગણતરી કરો',
    individualFeature4: 'મૂળભૂત રેસીપી વ્યવસ્થાપન',
    individualCta: 'વ્યક્તિગત તરીકે શરૂઆત કરો',
    professionalTitle: 'વ્યાવસાયિક રસોઇયા',
    professionalSubtitle: 'રેસ્ટોરાં અને ખાદ્ય વ્યવસાયો માટે',
    professionalSpectrum: 'મિશેલિન-સ્ટાર રસોડાથી લઈને વૈશ્વિક સ્ટ્રીટ ફૂડ સુધી-પ્લેટપ્રોફિટ તમારું કમાન્ડ સેન્ટર છે.',
    featuresTitle: 'નફાકારક રસોડું ચલાવવા માટે તમારે જે જોઈએ છે તે બધું.',
    feature1Title: 'રેસીપી બેંક',
    feature1Desc: 'તમારી બધી વાનગીઓ એક જગ્યાએ સ્ટોર કરો, ગોઠવો અને મેનેજ કરો. ફરી ક્યારેય કોઈ વાનગી ગુમાવશો નહીં.',
    feature2Title: 'સક્રિય/નિષ્ક્રિય વાનગીઓ',
    feature2Desc: 'ઋતુ અથવા ઉપલબ્ધતાના આધારે વાનગીઓ ચાલુ અને બંધ કરો. તમારું મેનૂ તાજું રાખો.',
    feature3Title: 'રીઅલ-ટાઇમ ખર્ચ ટ્રેકિંગ',
    feature3Desc: 'એકવાર ઘટકોની કિંમતો અપડેટ કરો અને તમામ વાનગીઓમાં તરત જ ખર્ચમાં થતા ફેરફારો જુઓ.',
    feature4Title: 'મેનુ પ્રાઇસિંગ ઇન્ટેલિજન્સ',
    feature4Desc: 'લક્ષ્ય માર્જિન સેટ કરો અને નફાકારક મેનુ કિંમત માટે ત્વરિત ભલામણો મેળવો.',
    feature5Title: 'બહુ-ચલણ આધાર',
    feature5Desc: 'તમારી સ્થાનિક ચલણમાં કામ કરો. અમે USD, EUR, QAR, INR અને 20+ થી વધુ ચલણોને સપોર્ટ કરીએ છીએ.',
    feature6Title: 'વિભાગ ટ્રેકિંગ',
    feature6Desc: 'વધુ સારા નિયંત્રણ માટે રસોઈની વાનગીઓને રસોડું, બાર, પેસ્ટ્રી અથવા કસ્ટમ વિભાગો દ્વારા ગોઠવો.',
    footerCta: 'શું તમે તમારા રસોડાને બદલવા માટે તૈયાર છો?',
    footerCtaButton: 'તમારું મફત ખાતું શરૂ કરો',
    footerTagline: 'પ્લેટપ્રોફિટ - દરેક ભાગમાં ચોકસાઈ',
  },
  kn: {
    languageLabel: 'ಭಾಷೆ',
    heroTitle: 'ಪ್ಯಾಂಟ್ರಿಯಿಂದ ತಟ್ಟೆಯವರೆಗೆ,',
    heroSubtitle: 'ಪ್ರತಿ ಮಿಲಿಗ್ರಾಂಗೆ ಲಾಭ.',
    heroDescription: 'ಪಾಕಶಾಲೆಯ ವೃತ್ತಿಪರರಿಗಾಗಿ ಸಂಪೂರ್ಣ ಪರಿಸರ ವ್ಯವಸ್ಥೆ',
    heroPrecision: '6-ದಶಮಾಂಶ ನಿಖರತೆ. ಪ್ರತಿಯೊಂದು ಮಿಲಿಗ್ರಾಂ ಮುಖ್ಯ.',
    ctaButton: 'ತಿಂಗಳಿಗೆ ಕೇವಲ $2.99 ರಿಂದ ಪ್ರಾರಂಭಿಸಿ',
    leftHeroTitle: 'ಅಜ್ಜಿಯ ರಹಸ್ಯಗಳನ್ನು ಶಾಶ್ವತವಾಗಿ ಉಳಿಸಿ.',
    leftHeroSubtext: '6-ದಶಮಾಂಶ ನಿಖರತೆಯೊಂದಿಗೆ ನಿಮ್ಮ ಮಾಸ್ಟರ್ ರೆಸಿಪಿ ಬ್ಯಾಂಕ್ ಅನ್ನು ನಿರ್ಮಿಸಿ ಮತ್ತು ರಕ್ಷಿಸಿ.',
    leftHeroCta: 'ಆರ್ಟಿಸನ್ ಪ್ರಾರಂಭಿಸಿ ($2.99/ತಿಂಗಳು)',
    rightHeroTitle: 'ಪ್ಯಾಂಟ್ರಿಯಿಂದ ತಟ್ಟೆಯವರೆಗೆ ಲಾಭದಾಯಕತೆ.',
    rightHeroSubtext: 'ಕಾರ್ಯನಿರ್ವಾಹಕ ಬಾಣಸಿಗನ 100% ಲಾಭಾಂಶ ನಿಯಂತ್ರಣದ ಎಂಜಿನ್.',
    rightHeroCta: 'ಗೋ ಪ್ರೊಫೆಷನಲ್ ($5.99/ತಿಂಗಳು)',
    rawIngredientsLabel: 'ಕಚ್ಚಾ ಪದಾರ್ಥಗಳು',
    precisionTracking: 'ನಿಖರವಾದ ಮೂಲ, ಪ್ರತಿದಿನ ಟ್ರ್ಯಾಕ್ ಮಾಡಲಾಗುತ್ತದೆ',
    engineTitle: 'ಪ್ಲೇಟ್‌ಪ್ರಾಫಿಟ್ ಎಂಜಿನ್',
    engineSubtitle: '6-ದಶಮಾಂಶ ನಿಖರತೆಯ ಜಾಡು ಹಿಡಿಯುವಿಕೆ',
    unitCostLabel: 'ಘಟಕ-ವೆಚ್ಚದ ನಿಖರತೆ',
    perGramLabel: 'ಪ್ರತಿ ಗ್ರಾಂಗೆ',
    noWastageLabel: 'ಯಾವುದೇ ಗುಪ್ತ ವ್ಯರ್ಥವಿಲ್ಲ. ಪ್ರತಿ ಮಿಲಿಗ್ರಾಂ ಅನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಲಾಗಿದೆ.',
    platedLabel: 'ಲೇಪಿತ ಪರಿಪೂರ್ಣತೆ',
    profitableLabel: 'ಲಾಭದಾಯಕ, ಪ್ರತಿ ಬಾರಿಯೂ',
    joinChefs: '150+ ದೇಶಗಳಲ್ಲಿ ಪ್ರತಿ ಮಿಲಿಗ್ರಾಂ ಅನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡುವ ಬಾಣಸಿಗರಿಗೆ ಸೇರಿಕೊಳ್ಳಿ.',
    pricingTitle: 'ಪಾರದರ್ಶಕ ಬೆಲೆ ನಿಗದಿ',
    pricingSubtitle: 'ಒಂದು ತಿಂಗಳ ನಿಖರತೆಗೆ ಒಂದು ಕಪ್ ಕಾಫಿಯ ಬೆಲೆಗಿಂತ ಕಡಿಮೆ.',
    artisanTier: 'ಕುಶಲಕರ್ಮಿ',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'ಮಾಸ್ಟರ್ ರೆಸಿಪಿ ಬ್ಯಾಂಕ್—ಹೆರಿಟೇಜ್-ಸಿದ್ಧ',
    artisanFeature1: 'ಮಾಸ್ಟರ್ ರೆಸಿಪಿ ಬ್ಯಾಂಕ್ (6-ದಶಮಾಂಶ ನಿಖರತೆ)',
    artisanFeature2: '250 ವರೆಗಿನ ಮಾಸ್ಟರ್ ರೆಸಿಪಿಗಳು',
    artisanFeature3: 'ಪ್ರತಿ ವಸ್ತುವಿಗೂ 6-ದಶಾಂಶದ ವೆಚ್ಚ ನಿಗದಿ',
    artisanScalingHook: 'ನೀವು ಮುಂದುವರಿದಂತೆ ಬೆಳೆಯಿರಿ: ಪ್ರತಿ ಹೆಚ್ಚುವರಿ 250 ಪಾಕವಿಧಾನಗಳಿಗೆ +$2.99/ತಿಂಗಳು.',
    artisanCta: 'ಕುಶಲಕರ್ಮಿ ಪ್ರಾರಂಭಿಸಿ',
    professionalTier: 'ವೃತ್ತಿಪರ',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'ಲಾಭ ನಿಯಂತ್ರಣದ ಕಾರ್ಯನಿರ್ವಾಹಕ ಎಂಜಿನ್',
    professionalBridge: 'ಕಲಾವಿದೆಯಲ್ಲಿ ಎಲ್ಲವೂ, ಜೊತೆಗೆ:',
    professionalFeature1: 'ಪ್ರತಿಯೊಂದೂ ಪ್ರತ್ಯೇಕವಾಗಿ, ಜೊತೆಗೆ:',
    professionalFeature2: 'ಸುಧಾರಿತ ಮೆನು ಬೆಲೆ ನಿಗದಿ',
    professionalFeature3: 'ಬಹು-ಇಲಾಖಾ ಟ್ರ್ಯಾಕಿಂಗ್',
    professionalFeature4: 'ನೈಜ-ಸಮಯದ ಲಾಭ ವಿಶ್ಲೇಷಣೆ',
    professionalFeature5: 'ಪಿಡಿಎಫ್ ರಫ್ತು ಮತ್ತು ಹಂಚಿಕೆ',
    professionalCta: 'ವೃತ್ತಿಪರರಾಗಿರಿ',
    popularBadge: 'ಅತ್ಯಂತ ಜನಪ್ರಿಯ',
    utilityNoApp: 'ಯಾವುದೇ ಆಪ್ ಸ್ಟೋರ್ ಅಗತ್ಯವಿಲ್ಲ',
    utilityOffline: 'ಅಡುಗೆಮನೆಗೆ ಸೂಕ್ತವಾದ ಆಫ್‌ಲೈನ್',
    utilitySync: 'ತತ್‌ಕ್ಷಣದ ಬಹು-ಸಾಧನ ಸಿಂಕ್',
    specialistTitle: 'ಪ್ರತಿ ಪಾಕಶಾಲೆಯ ತಜ್ಞರಿಗಾಗಿ ನಿರ್ಮಿಸಲಾಗಿದೆ',
    mixologyTitle: 'ಮಿಕ್ಸಾಲಜಿ ಲ್ಯಾಬ್',
    mixologyDesc: 'ಕ್ರಾಫ್ಟ್ ಕಾಕ್ಟೇಲ್‌ಗಳಿಂದ ಹಿಡಿದು ಕಾಫಿ ಆರ್ಟ್ ವರೆಗೆ, ಪ್ರತಿ ಸುರಿಯುವಿಕೆಗೆ ನಿಖರವಾದ ಬೆಲೆ.',
    mixologyUnit: 'ಪ್ರತಿ ಮಿಲಿಲೀಟರ್‌ಗೆ',
    pastryTitle: 'ಪೇಸ್ಟ್ರಿ ಕಲಾವಿದೆ',
    pastryDesc: 'ಸೂಕ್ಷ್ಮ ಸಿಹಿತಿಂಡಿಗಳು ಮತ್ತು ಬೇಕಿಂಗ್‌ಗಾಗಿ ಸೂಕ್ಷ್ಮ-ಅಳತೆ ಟ್ರ್ಯಾಕಿಂಗ್.',
    pastryUnit: 'ಪ್ರತಿ ಗ್ರಾಂ',
    spiceTitle: 'ಮಸಾಲೆ ಬೆರೆಸುವ ಯಂತ್ರ',
    spiceDesc: 'ನಿಮ್ಮ ರಹಸ್ಯ ಮಿಶ್ರಣಗಳನ್ನು ಪದಾರ್ಥಗಳ ಮಟ್ಟದ ನಿಖರತೆಯಿಂದ ರಕ್ಷಿಸಿ.',
    spiceUnit: 'ಪ್ರತಿ ಗ್ರಾಂ',
    problemTitle: 'ನಿಮ್ಮ ಅಡುಗೆಮನೆಯಲ್ಲಿರುವ ಗುಪ್ತ ಬಿರುಕು',
    problemSubtitle: 'ಹೆಚ್ಚಿನ ಬಾಣಸಿಗರು ಬೆಲೆಯನ್ನು ಊಹಿಸುತ್ತಾರೆ. ನಿಮಗೆ ತಿಳಿಯಲು ನಾವು ಸಹಾಯ ಮಾಡುತ್ತೇವೆ.',
    problemCard1Title: 'ಏರುತ್ತಿರುವ ಪದಾರ್ಥಗಳ ಬೆಲೆಗಳು',
    problemCard1Desc: 'ಬೆಲೆಗಳು ಪ್ರತಿದಿನ ಬದಲಾಗುತ್ತವೆ, ಆದರೆ ನಿಮ್ಮ ಮೆನು ಒಂದೇ ಆಗಿರುತ್ತದೆ. ನೀವು ಇನ್ನೂ ಲಾಭದಾಯಕವಾಗಿದ್ದೀರಾ?',
    problemCard2Title: 'ಮೆನು ಬೆಲೆ ನಿಗದಿ ರಹಸ್ಯ',
    problemCard2Desc: 'ಪ್ರತಿ ಖಾದ್ಯದ ಬೆಲೆ ಎಷ್ಟು? ನಿಖರವಾದ ಮಾಹಿತಿ ಇಲ್ಲದೆ, ನೀವು ಕುರುಡರಾಗಿ ಹಾರುತ್ತಿದ್ದೀರಿ.',
    problemCard3Title: 'ವ್ಯರ್ಥವಾದ ಸಮಯ ಮತ್ತು ಹಣ',
    problemCard3Desc: 'ಕೈಯಿಂದ ಬರೆದ ಸ್ಪ್ರೆಡ್‌ಶೀಟ್‌ಗಳು, ಹಳೆಯ ವೆಚ್ಚಗಳು ಮತ್ತು ಊಹೆಗಳು ಲಾಭಾಂಶವನ್ನು ಕುಗ್ಗಿಸುತ್ತವೆ.',
    solutionTitle: 'ಪ್ಲೇಟ್‌ಪ್ರಾಫಿಟ್: ನಿಮ್ಮ ರೆಸಿಪಿ ಮತ್ತು ವೆಚ್ಚ ಕಮಾಂಡ್ ಸೆಂಟರ್',
    solutionDesc: 'ಪ್ರತಿ ವಸ್ತುವನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ, ನಿಖರವಾದ ಪಾಕವಿಧಾನದ ವೆಚ್ಚವನ್ನು ಲೆಕ್ಕ ಹಾಕಿ ಮತ್ತು ಲಾಭದಾಯಕ ಮೆನು ಬೆಲೆಗಳನ್ನು ಹೊಂದಿಸಿ - ಎಲ್ಲವೂ ಒಂದೇ ವೇದಿಕೆಯಲ್ಲಿ.',
    personasTitle: 'ಪ್ರತಿ ಪಾಕಶಾಲೆಯ ಪ್ರಯಾಣಕ್ಕೂ ನಿರ್ಮಿಸಲಾಗಿದೆ',
    individualTitle: 'ವೈಯಕ್ತಿಕ ಗೃಹಿಣಿ',
    individualSubtitle: 'ಅತ್ಯಾಸಕ್ತಿಯಿಂದ ಅಡುಗೆ ಮಾಡುವ ಗೃಹಿಣಿಯರಿಗೆ ಸೂಕ್ತವಾಗಿದೆ.',
    individualFeature1: 'ಅನಂತ ಪಾಕವಿಧಾನಗಳನ್ನು ಸಂಗ್ರಹಿಸಿ',
    individualFeature2: 'ಪದಾರ್ಥಗಳ ಬೆಲೆಗಳನ್ನು ಗಮನಿಸಿ.',
    individualFeature3: 'ಸೇವೆಯ ವೆಚ್ಚವನ್ನು ಲೆಕ್ಕ ಹಾಕಿ',
    individualFeature4: 'ಮೂಲ ಪಾಕವಿಧಾನ ನಿರ್ವಹಣೆ',
    individualCta: 'ವ್ಯಕ್ತಿಯಾಗಿ ಪ್ರಾರಂಭಿಸಿ',
    professionalTitle: 'ವೃತ್ತಿಪರ ಬಾಣಸಿಗ',
    professionalSubtitle: 'ರೆಸ್ಟೋರೆಂಟ್‌ಗಳು ಮತ್ತು ಆಹಾರ ಉದ್ಯಮಗಳಿಗೆ',
    professionalSpectrum: 'ಮಿಷೆಲಿನ್-ಸ್ಟಾರ್ ಅಡುಗೆಮನೆಗಳಿಂದ ಹಿಡಿದು ಜಾಗತಿಕ ಬೀದಿ ಆಹಾರದವರೆಗೆ—ಪ್ಲೇಟ್‌ಪ್ರಾಫಿಟ್ ನಿಮ್ಮ ಕಮಾಂಡ್ ಸೆಂಟರ್ ಆಗಿದೆ.',
    featuresTitle: 'ಲಾಭದಾಯಕ ಅಡುಗೆಮನೆಯನ್ನು ನಡೆಸಲು ನಿಮಗೆ ಬೇಕಾದ ಎಲ್ಲವೂ',
    feature1Title: 'ರೆಸಿಪಿ ಬ್ಯಾಂಕ್',
    feature1Desc: 'ನಿಮ್ಮ ಎಲ್ಲಾ ಅಡುಗೆ ವಿಧಾನಗಳನ್ನು ಒಂದೇ ಸ್ಥಳದಲ್ಲಿ ಸಂಗ್ರಹಿಸಿ, ವ್ಯವಸ್ಥಿತಗೊಳಿಸಿ ಮತ್ತು ನಿರ್ವಹಿಸಿ. ಇನ್ನು ಮುಂದೆ ಯಾವುದೇ ಅಡುಗೆ ವಿಧಾನವನ್ನು ಕಳೆದುಕೊಳ್ಳಬೇಡಿ.',
    feature2Title: 'ಚಾಲ್ತಿಯಲ್ಲಿರುವ/ಚಾಲ್ತಿಯಲ್ಲಿಲ್ಲದ ಅಡುಗೆ ವಿಧಾನಗಳು',
    feature2Desc: 'ಋತುಮಾನ ಅಥವಾ ಲಭ್ಯತೆಯ ಆಧಾರದ ಮೇಲೆ ಅಡುಗೆ ವಿಧಾನಗಳನ್ನು ಆನ್ ಮತ್ತು ಆಫ್ ಮಾಡಿ. ನಿಮ್ಮ ಮೆನುವನ್ನು ತಾಜಾವಾಗಿ ಇರಿಸಿ.',
    feature3Title: 'ನೈಜ-ಸಮಯದ ವೆಚ್ಚದ ಜಾಡು ಹಿಡಿಯುವಿಕೆ',
    feature3Desc: 'ಒಮ್ಮೆ ಪದಾರ್ಥಗಳ ಬೆಲೆಗಳನ್ನು ನವೀಕರಿಸಿ, ಮತ್ತು ಎಲ್ಲಾ ಅಡುಗೆ ವಿಧಾನಗಳಲ್ಲಿನ ವೆಚ್ಚದ ಬದಲಾವಣೆಗಳನ್ನು ತಕ್ಷಣವೇ ನೋಡಿ.',
    feature4Title: 'ಮೆನು ಬೆಲೆಗಳ ಬುದ್ಧಿವಂತಿಕೆ',
    feature4Desc: 'ಗುರಿ ಅಂಚುಗಳನ್ನು ಹೊಂದಿಸಿ ಮತ್ತು ಲಾಭದಾಯಕ ಮೆನು ಬೆಲೆಗೆ ತ್ವರಿತ ಶಿಫಾರಸುಗಳನ್ನು ಪಡೆಯಿರಿ.',
    feature5Title: 'ಬಹು-ಕರೆನ್ಸಿ ಬೆಂಬಲ',
    feature5Desc: 'ನಿಮ್ಮ ಸ್ಥಳೀಯ ಕರೆನ್ಸಿಯಲ್ಲಿ ಕೆಲಸ ಮಾಡಿ. ನಾವು USD, EUR, QAR, INR ಮತ್ತು 20+ ಕ್ಕೂ ಹೆಚ್ಚು ಕರೆನ್ಸಿಗಳನ್ನು ಬೆಂಬಲಿಸುತ್ತೇವೆ.',
    feature6Title: 'ಇಲಾಖೆ ಟ್ರ್ಯಾಕಿಂಗ್',
    feature6Desc: 'ಉತ್ತಮ ನಿಯಂತ್ರಣಕ್ಕಾಗಿ ಅಡುಗೆಮನೆ, ಬಾರ್, ಪೇಸ್ಟ್ರಿ ಅಥವಾ ಕಸ್ಟಮ್ ವಿಭಾಗಗಳ ಪ್ರಕಾರ ಪಾಕವಿಧಾನಗಳನ್ನು ಆಯೋಜಿಸಿ.',
    footerCta: 'ನಿಮ್ಮ ಅಡುಗೆಮನೆಯನ್ನು ಬದಲಾಯಿಸಲು ಸಿದ್ಧರಿದ್ದೀರಾ?',
    footerCtaButton: 'ನಿಮ್ಮ ಉಚಿತ ಖಾತೆಯನ್ನು ಪ್ರಾರಂಭಿಸಿ',
    footerTagline: 'ಪ್ಲೇಟ್‌ಪ್ರಾಫಿಟ್ — ಪ್ರತಿ ಭಾಗದಲ್ಲೂ ನಿಖರತೆ',
  },
  ml: {
    languageLabel: 'ഭാഷ',
    heroTitle: 'പാൻട്രിയ്ക്ക്/അലമാരയ്ക്ക്/പാത്രത്തിലേക്ക്,',
    heroSubtitle: 'ഓരോ മില്ലിഗ്രാമിനും ലാഭം.',
    heroDescription: 'പാചക വിദഗ്ധർക്കുള്ള സമ്പൂർണ്ണ ആവാസവ്യവസ്ഥ',
    heroPrecision: '6-ദശാംശ കൃത്യത. ഓരോ മില്ലിഗ്രാമും വിലപ്പെട്ടതാണ്.',
    ctaButton: '$2.99/മാസം എന്ന കുറഞ്ഞ നിരക്കിൽ തുടങ്ങുക',
    leftHeroTitle: 'മുത്തശ്ശിയുടെ രഹസ്യങ്ങൾ എന്നേക്കും സംരക്ഷിക്കുക.',
    leftHeroSubtext: '6-ദശാംശ കൃത്യതയോടെ നിങ്ങളുടെ മാസ്റ്റർ റെസിപ്പി ബാങ്ക് നിർമ്മിക്കുകയും സംരക്ഷിക്കുകയും ചെയ്യുക.',
    leftHeroCta: 'ആർട്ടിസാൻ ആരംഭിക്കുക ($2.99/മാസം)',
    rightHeroTitle: 'പാൻട്രി ടു പ്ലേറ്റ് ലാഭം.',
    rightHeroSubtext: 'എക്സിക്യൂട്ടീവ് ഷെഫിന്‍റെ 100% മാർജിൻ നിയന്ത്രണത്തിനുള്ള എഞ്ചിൻ.',
    rightHeroCta: 'ഗോ പ്രൊഫഷണൽ ($5.99/മാസം)',
    rawIngredientsLabel: 'അസംസ്കൃത വസ്തുക്കൾ',
    precisionTracking: 'കൃത്യമായ ഉറവിടം, ദിവസവും ട്രാക്ക് ചെയ്യുന്നു.',
    engineTitle: 'പ്ലേറ്റ്പ്രോഫിറ്റ് എഞ്ചിൻ',
    engineSubtitle: '6-ദശാംശ കൃത്യത ട്രാക്കിംഗ്',
    unitCostLabel: 'യൂണിറ്റ്-കോസ്റ്റ് കൃത്യത',
    perGramLabel: 'ഗ്രാമിന്',
    noWastageLabel: 'ഒളിപ്പിച്ച പാഴാക്കലുകളില്ല. ഓരോ മില്ലിഗ്രാമും ട്രാക്ക് ചെയ്യുന്നു.',
    platedLabel: 'പലകകൊണ്ടുള്ള പൂർണ്ണത',
    profitableLabel: 'എപ്പോഴും ലാഭകരം, എല്ലാ തവണയും.',
    joinChefs: 'ഓരോ മില്ലിഗ്രാമും ട്രാക്ക് ചെയ്യുന്ന 150-ൽ അധികം രാജ്യങ്ങളിലെ പാചകക്കാരിൽ ചേരുക.',
    pricingTitle: 'സുതാര്യമായ വിലനിർണ്ണയം',
    pricingSubtitle: 'ഒരു മാസത്തെ കൃത്യതയ്ക്ക് ഒരു കപ്പ് കാപ്പിയുടെ വിലയിൽ കുറവ്.',
    artisanTier: 'കരകൗശലവിദഗ്ദ്ധ',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'മാസ്റ്റർ റെസിപ്പി ബാങ്ക്—പാരമ്പര്യത്തിന് അനുയോജ്യം',
    artisanFeature1: 'മാസ്റ്റർ റെസിപ്പി ബാങ്ക് (6-ദശാംശ കൃത്യത)',
    artisanFeature2: '250 വരെ മാസ്റ്റർ പാചകക്കുറിപ്പുകൾ',
    artisanFeature3: 'ഓരോ ചേരുവയ്ക്കും 6-ദശാംശ വിലനിർണ്ണയം.',
    artisanScalingHook: 'നിങ്ങൾ പോകുന്തോറും വളരുക: ഓരോ 250 പാചകക്കുറിപ്പുകൾക്കും +$2.99/മാസം.',
    artisanCta: 'ആർട്ടിസാൻ തുടങ്ങുക',
    professionalTier: 'തൊഴിൽപരമായ',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'ലാഭ നിയന്ത്രണത്തിനായുള്ള എക്സിക്യൂട്ടീവ് എഞ്ചിൻ',
    professionalBridge: 'ആർട്ടിസാനിലെ എല്ലാം, കൂടാതെ:',
    professionalFeature1: 'എല്ലാം വ്യക്തിഗതമായി, കൂടാതെ:',
    professionalFeature2: 'വിപുലമായ മെനു വിലനിർണ്ണയം',
    professionalFeature3: 'മൾട്ടി-ഡിപ്പാർട്ട്മെൻ്റ് ട്രാക്കിംഗ്',
    professionalFeature4: 'തത്സമയ ലാഭ വിശകലനം',
    professionalFeature5: 'പിഡിഎഫ് എക്സ്പോർട്ട് & ഷെയറിംഗ്',
    professionalCta: 'പ്രൊഫഷണലാകൂ',
    popularBadge: 'ഏറ്റവും പ്രചാരമുള്ളത്',
    utilityNoApp: 'ആപ്പ് സ്റ്റോർ ആവശ്യമില്ല',
    utilityOffline: 'അടുക്കളയ്ക്ക് അനുയോജ്യമായ ഓഫ്‌ലൈൻ',
    utilitySync: 'തൽക്ഷണ മൾട്ടി-ഡിവൈസ് സിങ്ക്',
    specialistTitle: 'എല്ലാ പാചക വിദഗ്ദ്ധർക്കും വേണ്ടി നിർമ്മിച്ചത്',
    mixologyTitle: 'മിക്സോളജി ലാബ്',
    mixologyDesc: 'ക്രാഫ്റ്റ് കോക്ക്‌ടെയിലുകൾ മുതൽ കോഫി ആർട്ട് വരെ, ഓരോ പെയറിനുമുള്ള കൃത്യമായ വിലനിർണ്ണയം.',
    mixologyUnit: 'ഒരു ml-ന്',
    pastryTitle: 'പേസ്ട്രി ആർട്ടിസ്റ്റ്',
    pastryDesc: 'സൂക്ഷ്മമായ മധുരപലഹാരങ്ങൾക്കും ബേക്കിംഗിനുമുള്ള മൈക്രോ-മെഷർമെൻ്റ് ട്രാക്കിംഗ്.',
    pastryUnit: 'ഒരു ഗ്രാമിന്',
    spiceTitle: 'മസാല അരയ്ക്കുന്ന യന്ത്രം',
    spiceDesc: 'ചേരുവകളുടെ കൃത്യതയോടെ നിങ്ങളുടെ രഹസ്യ മിശ്രിതങ്ങൾ സംരക്ഷിക്കുക.',
    spiceUnit: 'ഒരു ഗ്രാമിന്',
    problemTitle: 'നിങ്ങളുടെ അടുക്കളയിലെ ഒളിഞ്ഞുകിടക്കുന്ന വിടവ്',
    problemSubtitle: 'മിക്ക ഷെഫുമാരും വിലയിടൽ ഊഹിച്ചാണ് ചെയ്യുന്നത്. അറിയാൻ ഞങ്ങൾ നിങ്ങളെ സഹായിക്കുന്നു.',
    problemCard1Title: 'ഉയരുന്ന ചേരുവകളുടെ വില',
    problemCard1Desc: 'വിലകൾ ദിവസവും മാറിക്കൊണ്ടിരിക്കുന്നു, പക്ഷേ നിങ്ങളുടെ മെനു അതേപടി നിലനിൽക്കുന്നു. നിങ്ങൾ ഇപ്പോഴും ലാഭത്തിലാണോ?',
    problemCard2Title: 'മെനു വിലനിർണ്ണയ രഹസ്യം',
    problemCard2Desc: 'ഓരോ വിഭവത്തിനും ശരിക്കും എത്ര വിലയുണ്ട്? കൃത്യമായ വിവരങ്ങളില്ലാതെ, നിങ്ങൾ കണ്ണടച്ച് പറക്കുകയാണ്.',
    problemCard3Title: 'സമയം, പണം എന്നിവ വെറുതെ കളഞ്ഞു.',
    problemCard3Desc: 'കൈകൊണ്ടുള്ള സ്പ്രെഡ്ഷീറ്റുകൾ, കാലഹരണപ്പെട്ട ചിലവുകൾ, ഊഹങ്ങൾ എന്നിവ മാർജിനുകൾ കുറയ്ക്കുന്നതിലേക്ക് നയിക്കുന്നു.',
    solutionTitle: 'PlateProfit: നിങ്ങളുടെ പാചകക്കുറിപ്പും വില നിർണ്ണയ കേന്ദ്രവും',
    solutionDesc: 'ഓരോ ചേരുവയും ട്രാക്ക് ചെയ്യുക, കൃത്യമായ പാചകക്കുറിപ്പ് ചിലവുകൾ കണക്കാക്കുക, ലാഭകരമായ മെനു വിലകൾ നിശ്ചയിക്കുക - എല്ലാം ഒരൊറ്റ പ്ലാറ്റ്‌ഫോമിൽ.',
    personasTitle: 'ഓരോ പാചക യാത്രയ്ക്കും വേണ്ടി നിർമ്മിച്ചത്.',
    individualTitle: 'ഒറ്റയ്ക്ക് വീട്ടിൽ താമസിക്കുന്ന സ്ത്രീ',
    individualSubtitle: 'പാചകത്തിൽ താല്പര്യമുള്ള വീട്ടമ്മമാർക്ക് അനുയോജ്യം.',
    individualFeature1: 'പരിധിയില്ലാത്ത പാചകക്കുറിപ്പുകൾ സംഭരിക്കുക',
    individualFeature2: 'ചേരുവകളുടെ വില ട്രാക്ക് ചെയ്യുക',
    individualFeature3: 'സേവനത്തിനുള്ള ചിലവുകൾ കണക്കാക്കുക',
    individualFeature4: 'അടിസ്ഥാന പാചകക്കുറിപ്പ് മാനേജ്മെന്റ്',
    individualCta: 'ഒറ്റയ്ക്ക് തുടങ്ങുക',
    professionalTitle: 'പ്രൊഫഷണൽ ഷെഫ്',
    professionalSubtitle: 'റെസ്റ്റോറന്റുകൾക്കും ഭക്ഷ്യ ബിസിനസ്സുകൾക്കും വേണ്ടി',
    professionalSpectrum: 'മിഷേലിൻ-സ്റ്റാർ കിച്ചണുകൾ മുതൽ ആഗോള തെരുവ് ഭക്ഷണം വരെ - പ്ലേറ്റ് പ്രോഫിറ്റ് നിങ്ങളുടെ കമാൻഡ് സെന്‍ററാണ്.',
    featuresTitle: 'ലാഭകരമായ ഒരു അടുക്കള നടത്താൻ നിങ്ങൾക്കാവശ്യമായതെല്ലാം',
    feature1Title: 'പാചകക്കുറിപ്പ് ശേഖരം',
    feature1Desc: 'നിങ്ങളുടെ എല്ലാ പാചകക്കുറിപ്പുകളും ഒരിടത്ത് സൂക്ഷിക്കുക, ക്രമീകരിക്കുക, കൈകാര്യം ചെയ്യുക. ഇനി ഒരു പാചകക്കുറിപ്പ് പോലും നഷ്ടപ്പെടില്ല.',
    feature2Title: 'പ്രവർത്തനക്ഷമമായ/പ്രവർത്തനരഹിതമായ പാചകക്കുറിപ്പുകൾ',
    feature2Desc: 'ഋതുക്കളുടെ അടിസ്ഥാനത്തിലോ ലഭ്യത അനുസരിച്ചോ പാചകക്കുറിപ്പുകൾ ഓൺ ചെയ്യുക, ഓഫ് ചെയ്യുക. നിങ്ങളുടെ മെനു പുതിയതായി നിലനിർത്തുക.',
    feature3Title: 'തത്സമയ ചിലവ് ട്രാക്കിംഗ്',
    feature3Desc: 'ചേരുവകളുടെ വില ഒരു തവണ പുതുക്കിയാൽ എല്ലാ പാചകക്കുറിപ്പുകളിലെയും വില വ്യത്യാസങ്ങൾ തൽക്ഷണം കാണാനാകും.',
    feature4Title: 'മെനു വില വിവര വിശകലനം',
    feature4Desc: 'ലക്ഷ്യ മാർജിനുകൾ സജ്ജമാക്കുകയും ലാഭകരമായ മെനു വിലനിർണ്ണയത്തിനുള്ള തൽക്ഷണ ശുപാർശകൾ നേടുകയും ചെയ്യുക.',
    feature5Title: 'ബഹു-കറൻസി പിന്തുണ',
    feature5Desc: 'നിങ്ങളുടെ പ്രാദേശിക കറൻസിയിൽ പ്രവർത്തിക്കുക. ഞങ്ങൾ USD, EUR, QAR, INR, കൂടാതെ 20-ൽ അധികം കറൻസികളും പിന്തുണയ്ക്കുന്നു.',
    feature6Title: 'ഡിപ്പാർട്ട്മെൻ്റ് ട്രാക്കിംഗ്',
    feature6Desc: 'കൂടുതൽ നിയന്ത്രണത്തിനായി അടുക്കള, ബാർ, പേസ്ട്രി അല്ലെങ്കിൽ ഇഷ്ടമുള്ളവ  എന്നിവയുടെ  ഡിപ്പാർട്ട്മെൻ്റുകൾ അനുസരിച്ച് പാചകക്കുറിപ്പുകൾ ക്രമീകരിക്കുക.',
    footerCta: 'നിങ്ങളുടെ അടുക്കളയെ രൂപാന്തരപ്പെടുത്താൻ തയ്യാറാണോ?',
    footerCtaButton: 'നിങ്ങളുടെ സൗജന്യ അക്കൗണ്ട് ആരംഭിക്കുക',
    footerTagline: 'പ്ലേറ്റ്പ്രോഫിറ്റ് - ഓരോ ഭാഗത്തിലും കൃത്യത',
  },
  mr: {
    languageLabel: 'भाषा',
    heroTitle: 'पॅन्ट्रीपासून ताटापर्यंत,',
    heroSubtitle: 'प्रत्येक मिलीग्रामवर नफा.',
    heroDescription: 'पाककला व्यावसायिकांसाठी संपूर्ण परिसंस्था',
    heroPrecision: '6-दशांश अचूकता. प्रत्येक मिलीग्राम महत्त्वाचा आहे.',
    ctaButton: 'किमान $2.99 प्रति महिना पासून सुरुवात करा.',
    leftHeroTitle: 'आजीच्या गुपितांना कायमचे जतन करा.',
    leftHeroSubtext: '6 दशांश अचूकतेसह तुमचा मास्टर रेसिपी बँक तयार करा आणि संरक्षित करा.',
    leftHeroCta: 'सुरुवात आर्टिसन ($2.99/महिना)',
    rightHeroTitle: 'पँट्री ते प्लेट नफा.',
    rightHeroSubtext: 'कार्यकारी आचारीचे 100% मार्जिन नियंत्रणासाठी इंजिन.',
    rightHeroCta: 'गो प्रोफेशनल ($5.99/महिना)',
    rawIngredientsLabel: 'कच्चा माल',
    precisionTracking: 'अचूक सोर्सिंग, दररोज ट्रॅक केले जाते.',
    engineTitle: 'प्लेटप्रोफिट इंजिन',
    engineSubtitle: '6-दशांश अचूकता मागोवा',
    unitCostLabel: 'युनिट-खर्चाची अचूकता',
    perGramLabel: 'प्रति ग्रॅम',
    noWastageLabel: 'कोणताही लपलेला अपव्यय नाही. प्रत्येक मिलीग्रामचा मागोवा घेतला जातो.',
    platedLabel: 'प्लेटेड परफेक्शन',
    profitableLabel: 'नफादायक, प्रत्येक वेळी',
    joinChefs: 'प्रत्येक मिलीग्रामचा मागोवा घेणाऱ्या 150 हून अधिक देशांतील आचाऱ्यांसोबत सामील व्हा.',
    pricingTitle: 'पारदर्शक किंमत',
    pricingSubtitle: 'एका महिन्याच्या अचूकतेसाठी एका कप कॉफीच्या किमतीपेक्षा कमी.',
    artisanTier: 'कारागीर',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'मास्टर रेसिपी बँक—हेरिटेज-रेडी',
    artisanFeature1: 'मास्टर रेसिपी बँक (6-दशांश अचूकता)',
    artisanFeature2: '250 पर्यंत मास्टर रेसिपी',
    artisanFeature3: 'प्रत्येक घटकासाठी 6-दशांची किंमत',
    artisanScalingHook: 'तुम्ही जसे जाल तसे वाढवाः प्रत्येक अतिरिक्त 250 पाककृतींसाठी +$2.99/महिना.',
    artisanCta: 'शुरू करा कारागीर',
    professionalTier: 'व्यावसायिक',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'नफा नियंत्रणासाठी कार्यकारी यंत्रणा',
    professionalBridge: 'आर्टिसनमधील सर्व काही, अधिक:',
    professionalFeature1: 'प्रत्येक गोष्ट वैयक्तिकरित्या, अधिक:',
    professionalFeature2: 'प्रगत मेनू किंमत',
    professionalFeature3: 'बहु-विभागीय मागोवा',
    professionalFeature4: 'रिअल-टाइम नफा विश्लेषण',
    professionalFeature5: 'पीडीएफ एक्सपोर्ट आणि शेअरिंग',
    professionalCta: 'व्यावसायिक व्हा.',
    popularBadge: 'सर्वात लोकप्रिय',
    utilityNoApp: 'ॲप स्टोअरची गरज नाही',
    utilityOffline: 'स्वयंपाकघरासाठी योग्य ऑफलाइन',
    utilitySync: 'त्वरित मल्टी-डिव्हाइस सिंक',
    specialistTitle: 'प्रत्येक पाककला तज्ञासाठी तयार केलेले',
    mixologyTitle: 'मिश्रणशास्त्र प्रयोगशाळा',
    mixologyDesc: 'प्रत्येक घोट्यासाठी अचूक खर्च, क्राफ्ट कॉकटेलपासून कॉफी आर्टपर्यंत.',
    mixologyUnit: 'प्रति मिली',
    pastryTitle: 'पिठाचा गोळा बनवणारी स्त्री कलाकार',
    pastryDesc: 'नाजुक मिष्टान्ने आणि बेकिंगसाठी सूक्ष्म-मापन ट्रॅकिंग.',
    pastryUnit: 'प्रति ग्रॅम',
    spiceTitle: 'मसाला मिक्सर',
    spiceDesc: 'घटक-स्तरावरील अचूकतेसह तुमच्या गुप्त मिश्रणांचे संरक्षण करा.',
    spiceUnit: 'प्रति ग्रॅम',
    problemTitle: 'तुमच्या किचनमधील लपलेली फट',
    problemSubtitle: 'बहुतेक आचारी किंमतीचा अंदाज लावतात. आम्ही तुम्हाला ते जाणून घेण्यास मदत करतो.',
    problemCard1Title: 'वाढत्या घटकांच्या किंमती',
    problemCard1Desc: 'किंमती दररोज बदलतात, पण तुमचे मेनू तेच राहतात. तुम्ही अजूनही फायदेशीर आहात का?',
    problemCard2Title: 'मेनू किंमतीचे रहस्य',
    problemCard2Desc: 'प्रत्येक डिशची किंमत नेमकी किती आहे? अचूक आकडेवारीशिवाय, तुम्ही अंधारात तीर मारत आहात.',
    problemCard3Title: 'वाया गेलेला वेळ आणि पैसा',
    problemCard3Desc: 'हाताने भरलेले स्प्रेडशीट, जुने झालेले खर्च आणि अंदाजे आकडेवारी यामुळे नफा कमी होतो.',
    solutionTitle: 'प्लेट प्रॉफिट: तुमची रेसिपी आणि कॉस्ट कमांड सेंटर',
    solutionDesc: 'प्रत्येक घटकाचा मागोवा घ्या, अचूक कृती खर्चाची गणना करा आणि फायदेशीर मेनू किंमती सेट करा—हे सर्व एकाच प्लॅटफॉर्मवर.',
    personasTitle: 'प्रत्येक पाककला प्रवासासाठी तयार केलेले.',
    individualTitle: 'एकट्या घरची बाई',
    individualSubtitle: 'उत्कट घरगुती स्वयंपाक्यांसाठी योग्य.',
    individualFeature1: 'अमर्यादित पाककृती साठवा',
    individualFeature2: 'घटक खर्चाचा मागोवा घ्या',
    individualFeature3: 'सेवेचा खर्च मोजा.',
    individualFeature4: 'मूलभूत पाककृती व्यवस्थापन',
    individualCta: 'एका व्यक्ती म्हणून सुरुवात करा',
    professionalTitle: 'व्यावसायिक आचारी',
    professionalSubtitle: 'रेस्टॉरंट आणि खाद्य व्यवसायांसाठी',
    professionalSpectrum: 'मिशेलिन-स्टार किचनपासून ते जागतिक स्ट्रीट फूडपर्यंत - प्लेटप्रॉफिट हे तुमचे कमांड सेंटर आहे.',
    featuresTitle: 'नफा मिळवणारे स्वयंपाकघर चालवण्यासाठी तुम्हाला आवश्यक असलेले सर्व काही.',
    feature1Title: 'पाककृती बँक',
    feature1Desc: 'तुमच्या सर्व पाककृती एकाच ठिकाणी साठवा, व्यवस्थित करा आणि व्यवस्थापित करा. पुन्हा कधीही पाककृती गमावू नका.',
    feature2Title: 'सक्रिय/निष्क्रिय पाककृती',
    feature2Desc: 'हंगामानुसार किंवा उपलब्धतेनुसार पाककृती चालू आणि बंद करा. आपले मेनू ताजे ठेवा.',
    feature3Title: 'रिअल-टाइम खर्च मागोवा',
    feature3Desc: 'घटकांच्या किंमती एकदा अद्ययावत करा आणि सर्व पाककृतींमधील खर्चातील बदल त्वरित पहा.',
    feature4Title: 'मेनू किंमत बुद्धिमत्ता',
    feature4Desc: 'लक्ष्य मार्जिन सेट करा आणि फायदेशीर मेनू किंमतीसाठी त्वरित शिफारसी मिळवा.',
    feature5Title: 'बहु-चलन समर्थन',
    feature5Desc: 'तुमच्या स्थानिक चलनात काम करा. आम्ही USD, EUR, QAR, INR आणि 20+ पेक्षा जास्त चलनांना सपोर्ट करतो.',
    feature6Title: 'विभाग मागोवा',
    feature6Desc: 'अधिक चांगल्या नियंत्रणासाठी पाककृती स्वयंपाकघर, बार, पेस्ट्री किंवा सानुकूल विभागांनुसार आयोजित करा.',
    footerCta: 'तुमच्या स्वयंपाकघराचे रूप बदलण्यासाठी तयार आहात?',
    footerCtaButton: 'तुमचे विनामूल्य खाते सुरू करा',
    footerTagline: 'प्लेट प्रॉफिट — प्रत्येक भागामध्ये अचूकता',
  },
  or: {
    languageLabel: 'ଭାଷା',
    heroTitle: 'ପ୍ୟାଣ୍ଟ୍ରିରୁ ପ୍ଲେଟ୍ ପର୍ଯ୍ୟନ୍ତ,',
    heroSubtitle: 'ପ୍ରତି ମିଲିଗ୍ରାମରେ ଲାଭ।',
    heroDescription: 'ରୋଷେଇ କରୁଥିବା ପେସାଦାରମାନଙ୍କ ପାଇଁ ସମ୍ପୂର୍ଣ୍ଣ ଇକୋସିଷ୍ଟମ୍ |',
    heroPrecision: '6-ଦଶମିକ ସଠିକତା | ପ୍ରତି ମିଲିଗ୍ରାମ ଗୁରୁତ୍ୱପୂର୍ଣ୍ଣ |',
    ctaButton: 'ମାସିକ $2.99ରୁ ଆରମ୍ଭ କରନ୍ତୁ।',
    leftHeroTitle: 'ଆଈଙ୍କ ରହସ୍ୟକୁ ସବୁଦିନ ପାଇଁ ସାଇତି ରଖନ୍ତୁ।',
    leftHeroSubtext: '6-ଦଶମିକ ସଠିକତା ସହିତ ଆପଣଙ୍କ ମାଷ୍ଟର ରେସିପି ବ୍ୟାଙ୍କ୍ ନିର୍ମାଣ ଏବଂ ସୁରକ୍ଷା କରନ୍ତୁ।',
    leftHeroCta: 'ଆରମ୍ଭ କରନ୍ତୁ ଆର୍ଟିଜାନ ($2.99/ମାସ)',
    rightHeroTitle: 'ପ୍ୟାଣ୍ଟ୍ରି ଟୁ ପ୍ଲେଟ୍ ଲାଭଦାୟକତା।',
    rightHeroSubtext: 'ଏକ୍ଜିକ୍ୟୁଟିଭ୍ ସେଫ୍‌ଙ୍କ ଇଞ୍ଜିନ 100% ମାର୍ଜିନ ନିୟନ୍ତ୍ରଣ ପାଇଁ।',
    rightHeroCta: 'ଗୋ ପ୍ରଫେସନାଲ୍ ($5.99/ମାସ)',
    rawIngredientsLabel: 'କଞ୍ଚାମାଲ',
    precisionTracking: 'ସଠିକ୍ ଉତ୍ସ, ପ୍ରତିଦିନ ଟ୍ରାକ୍ କରାଯାଏ |',
    engineTitle: 'ପ୍ଲେଟପ୍ରୋଫିଟ୍ ଇଞ୍ଜିନ୍',
    engineSubtitle: '6-ଦଶମିକ ସଠିକତା ଟ୍ରାକିଂ',
    unitCostLabel: 'ଏକକ-ମୂଲ୍ୟ ସଠିକତା',
    perGramLabel: 'ପ୍ରତି ଗ୍ରାମରେ',
    noWastageLabel: 'କୌଣସି ଲୁକ୍କାୟିତ ଅପଚୟ ନାହିଁ | ପ୍ରତି ମିଲିଗ୍ରାମ ଟ୍ରାକ୍ କରାଯାଏ |',
    platedLabel: 'ପ୍ଲେଟେଡ୍ ପରଫେକ୍ସନ୍',
    profitableLabel: 'ଲାଭଜନକ, ସବୁବେଳେ।',
    joinChefs: '150+ ଦେଶରେ ରୋଷେୟାମାନଙ୍କ ସହିତ ଯୋଗ ଦିଅନ୍ତୁ, ଯେଉଁମାନେ ପ୍ରତି ମିଲିଗ୍ରାମକୁ ଟ୍ରାକ୍ କରୁଛନ୍ତି।',
    pricingTitle: 'ସ୍ୱଚ୍ଛ ମୂଲ୍ୟ ନିର୍ଦ୍ଧାରଣ',
    pricingSubtitle: 'ଏକ ମାସର ସଠିକତା ପାଇଁ ଏକ କପ୍ କଫିର ମୂଲ୍ୟଠାରୁ କମ୍।',
    artisanTier: 'କାରିଗର',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'ମାଷ୍ଟର ରେସିପି ବ୍ୟାଙ୍କ୍—ହେରିଟେଜ୍-ପ୍ରସ୍ତୁତ',
    artisanFeature1: 'ମାଷ୍ଟର ରେସିପି ବ୍ୟାଙ୍କ (6-ଦଶମିକ ସଠିକତା)',
    artisanFeature2: '250 ପର୍ଯ୍ୟନ୍ତ ମାଷ୍ଟର ରେସିପି',
    artisanFeature3: 'ପ୍ରତ୍ୟେକ ଉପାଦାନ ପାଇଁ 6-ଦଶମିକ ମୂଲ୍ୟ ନିର୍ଦ୍ଧାରଣ',
    artisanScalingHook: 'ଆପଣ ଯେମିତି ଆଗକୁ ବଢ଼ିବେ, ସେମିତି ବଢ଼ନ୍ତୁ: ପ୍ରତି ଅତିରିକ୍ତ 250ଟି ରେସିପି ପାଇଁ +$2.99/ମାସ।',
    artisanCta: 'ଆରମ୍ଭ କରନ୍ତୁ କାରିଗର',
    professionalTier: 'ବୃତ୍ତିଗତ',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'ଲାଭ ନିୟନ୍ତ୍ରଣ ପାଇଁ କାର୍ଯ୍ୟନିର୍ବାହୀ ଇଞ୍ଜିନ୍',
    professionalBridge: 'ଆର୍ଟିସାନରେ ସବୁକିଛି, ଏହା ସହିତ:',
    professionalFeature1: 'ସବୁକିଛି ବ୍ୟକ୍ତିଗତ ଭାବରେ, ଏହା ସହିତ:',
    professionalFeature2: 'ଉନ୍ନତ ମେନୁ ମୂଲ୍ୟ ନିର୍ଦ୍ଧାରଣ',
    professionalFeature3: 'ବହୁ-ବିଭାଗୀୟ ଟ୍ରାକିଂ',
    professionalFeature4: 'ରିଅଲ୍-ଟାଇମ୍ ଲାଭ ବିଶ୍ଳେଷଣ',
    professionalFeature5: 'PDF ରପ୍ତାନି ଏବଂ ଅଂଶୀଦାର',
    professionalCta: 'ପେସାଦାର ହୁଅନ୍ତୁ |',
    popularBadge: 'ଅଧିକ ଲୋକପ୍ରିୟ',
    utilityNoApp: 'କୌଣସି ଆପ୍ ଷ୍ଟୋର୍ ଆବଶ୍ୟକ ନାହିଁ',
    utilityOffline: 'ରୋଷେଇ ଘର-ପ୍ରୁଫ୍ ଅଫଲାଇନ୍',
    utilitySync: 'ତତ୍କାଳୀନ ମଲ୍ଟି-ଡିଭାଇସ୍ ସିଙ୍କ୍',
    specialistTitle: 'ପ୍ରତ୍ୟେକ ରୋଷେଇ ବିଶେଷଜ୍ଞଙ୍କ ପାଇଁ ନିର୍ମିତ |',
    mixologyTitle: 'ମିକ୍ସୋଲୋଜି ଲାବ୍',
    mixologyDesc: 'କ୍ରାଫ୍ଟ କକଟେଲ୍ ଠାରୁ ଆରମ୍ଭ କରି କଫି ଆର୍ଟ ପର୍ଯ୍ୟନ୍ତ, ପ୍ରତ୍ୟେକ ପାଉର ପାଇଁ ସଠିକ୍ ମୂଲ୍ୟ ନିର୍ଦ୍ଧାରଣ କରାଯାଏ।',
    mixologyUnit: 'ପ୍ରତି ମିଲିଲିଟରରେ',
    pastryTitle: 'ପେଷ୍ଟ୍ରି କଳାକାର',
    pastryDesc: 'ସୂକ୍ଷ୍ମ ମିଠା ଏବଂ ବେକିଂ ପାଇଁ ମାଇକ୍ରୋ-ମାପ ଟ୍ରାକିଂ।',
    pastryUnit: 'ପ୍ରତି ଗ୍ରାମରେ',
    spiceTitle: 'ମସଲା ବ୍ଲେଣ୍ଡର୍',
    spiceDesc: 'ଉପାଦାନ ସ୍ତରର ସଠିକତା ସହିତ ତୁମର ଗୁପ୍ତ ମିଶ୍ରଣକୁ ସୁରକ୍ଷା ଦିଅ।',
    spiceUnit: 'ପ୍ରତି ଗ୍ରାମରେ',
    problemTitle: 'ତୁମ ରୋଷେଇ ଘରେ ଲୁକ୍କାୟିତ ଫାଙ୍କ',
    problemSubtitle: 'ଅଧିକାଂଶ ରୋଷେୟା ମୂଲ୍ୟ ଆକଳନ କରନ୍ତି | ଆମେ ଆପଣଙ୍କୁ ଜାଣିବାରେ ସାହାଯ୍ୟ କରୁ |',
    problemCard1Title: 'ବଢୁଥିବା ଉପାଦାନ ମୂଲ୍ୟ',
    problemCard1Desc: 'ଦାମ୍ ପ୍ରତିଦିନ ବଦଳୁଛି, କିନ୍ତୁ ତୁମର ମେନୁ ସମାନ ରହୁଛି। ତୁମେ ଏବେ ବି ଲାଭରେ ଅଛ କି?',
    problemCard2Title: 'ମେନୁ ମୂଲ୍ୟ ରହସ୍ୟ',
    problemCard2Desc: 'ପ୍ରତ୍ୟେକ ଡିସ୍‌ର ପ୍ରକୃତ ମୂଲ୍ୟ କେତେ? ସଠିକ୍ ତଥ୍ୟ ବିନା, ତୁମେ ଅନ୍ଧ ଭାବରେ ଉଡ଼ୁଛ।',
    problemCard3Title: 'ଅପଚୟ ହୋଇଥିବା ସମୟ ଓ ଟଙ୍କା',
    problemCard3Desc: 'ମାନୁଆଲ୍ ସ୍ପ୍ରେଡସିଟ୍, ପୁରୁଣା ଖର୍ଚ୍ଚ ଏବଂ ଅନୁମାନ ଲାଭ କମାଇଥାଏ |',
    solutionTitle: 'ପ୍ଲେଟପ୍ରୋଫିଟ୍: ଆପଣଙ୍କ ରେସିପି ଏବଂ କଷ୍ଟ କମାଣ୍ଡ ସେଣ୍ଟର',
    solutionDesc: 'ପ୍ରତ୍ୟେକ ଉପାଦାନ ଉପରେ ନଜର ରଖନ୍ତୁ, ସଠିକ୍ ରେସିପି ଖର୍ଚ୍ଚ ଗଣନା କରନ୍ତୁ ଏବଂ ଲାଭଜନକ ମେନୁ ମୂଲ୍ୟ ସ୍ଥିର କରନ୍ତୁ - ସବୁ ଏକ ପ୍ଲାଟଫର୍ମରେ।',
    personasTitle: 'ପ୍ରତ୍ୟେକ ରୋଷେଇ ଯାତ୍ରା ପାଇଁ ନିର୍ମିତ |',
    individualTitle: 'ବ୍ୟକ୍ତିଗତ ଗୃହିଣୀ',
    individualSubtitle: 'ଉତ୍ସାହୀ ଗୃହିଣୀ ରୋଷେଇଆଙ୍କ ପାଇଁ ଉପଯୁକ୍ତ |',
    individualFeature1: 'ଅସୀମିତ ରେସିପି ସଂରକ୍ଷଣ କରନ୍ତୁ |',
    individualFeature2: 'ଉପାଦାନ ଖର୍ଚ୍ଚ ଉପରେ ନଜର ରଖନ୍ତୁ |',
    individualFeature3: 'ସେବା ମୂଲ୍ୟ ଗଣନା କରନ୍ତୁ |',
    individualFeature4: 'ମୌଳିକ ରେସିପି ପରିଚାଳନା',
    individualCta: 'ବ୍ୟକ୍ତିଗତ ଭାବରେ ଆରମ୍ଭ କରନ୍ତୁ |',
    professionalTitle: 'ବୃତ୍ତିଗତ ରୋଷେୟା',
    professionalSubtitle: 'ରେଷ୍ଟୁରାଣ୍ଟ ଏବଂ ଖାଦ୍ୟ ବ୍ୟବସାୟ ପାଇଁ',
    professionalSpectrum: 'ମିସେଲିନ୍-ଷ୍ଟାର୍ ରୋଷେଇ ଘରଠାରୁ ଆରମ୍ଭ କରି ବିଶ୍ୱସ୍ତରୀୟ ଷ୍ଟ୍ରିଟ୍ ଫୁଡ୍ ପର୍ଯ୍ୟନ୍ତ—ପ୍ଲେଟ୍‌ପ୍ରୋଫିଟ୍ ହେଉଛି ଆପଣଙ୍କ କମାଣ୍ଡ ସେଣ୍ଟର୍।',
    featuresTitle: 'ଲାଭଜନକ ରୋଷେଇ ଘର ଚଳାଇବା ପାଇଁ ଆପଣଙ୍କୁ ଯାହା ଦରକାର |',
    feature1Title: 'ରେସିପି ବ୍ୟାଙ୍କ',
    feature1Desc: 'ଗୋଟିଏ ସ୍ଥାନରେ ତୁମର ସମସ୍ତ ରେସିପିକୁ ସଂରକ୍ଷଣ କର, ସଜାଡ଼ ଏବଂ ପରିଚାଳନା କର | ଆଉ କେବେବି ରେସିପି ହରାଅ ନାହିଁ |',
    feature2Title: 'ସକ୍ରିୟ/ଅନିଷ୍କ୍ରିୟ ରେସିପିଗୁଡିକ',
    feature2Desc: 'ଋତୁ କିମ୍ବା ଉପଲବ୍ଧତା ଅନୁସାରେ ରେସିପିଗୁଡ଼ିକୁ ଅନ୍ ଏବଂ ଅଫ୍ କରନ୍ତୁ | ଆପଣଙ୍କ ମେନୁକୁ ସତେଜ ରଖନ୍ତୁ |',
    feature3Title: 'ରିଅଲ୍-ଟାଇମ୍ ଖର୍ଚ୍ଚ ଟ୍ରାକିଙ୍ଗ୍',
    feature3Desc: 'ଥରେ ଉପାଦାନଗୁଡ଼ିକର ମୂଲ୍ୟ ଅପଡେଟ୍ କରନ୍ତୁ, ଏବଂ ସମସ୍ତ ରେସିପିରେ ତୁରନ୍ତ ମୂଲ୍ୟ ପରିବର୍ତ୍ତନ ଦେଖନ୍ତୁ।',
    feature4Title: 'ମେନୁ ମୂଲ୍ୟ ସୂଚନା',
    feature4Desc: 'ଲକ୍ଷ୍ୟ ମାର୍ଜିନ ସେଟ୍ କରନ୍ତୁ ଏବଂ ଲାଭଜନକ ମେନୁ ମୂଲ୍ୟ ନିର୍ଦ୍ଧାରଣ ପାଇଁ ତତକ୍ଷଣାତ୍ ପରାମର୍ଶ ପାଆନ୍ତୁ।',
    feature5Title: 'ବହୁ-ମୁଦ୍ରା ସହାୟତା',
    feature5Desc: 'ଆପଣଙ୍କ ସ୍ଥାନୀୟ ମୁଦ୍ରାରେ କାମ କରନ୍ତୁ | ଆମେ USD, EUR, QAR, INR, ଏବଂ 20+ ରୁ ଅଧିକକୁ ସମର୍ଥନ କରୁ |',
    feature6Title: 'ବିଭାଗ ଟ୍ରାକିଂ',
    feature6Desc: 'ଅଧିକ ନିୟନ୍ତ୍ରଣ ପାଇଁ ରୋଷେଇ, ମଦ୍ୟଶାଳା, ପେଷ୍ଟ୍ରି କିମ୍ବା ନିଜ ଇଚ୍ଛା ଅନୁସାରେ ବିଭାଗ ଅନୁସାରେ ରେସିପିଗୁଡ଼ିକୁ ସଜାନ୍ତୁ।',
    footerCta: 'ଆପଣଙ୍କ ରୋଷେଇ ଘରକୁ ବଦଳାଇବାକୁ ପ୍ରସ୍ତୁତ କି?',
    footerCtaButton: 'ଆପଣଙ୍କର ମାଗଣା ଆକାଉଣ୍ଟ ଆରମ୍ଭ କରନ୍ତୁ |',
    footerTagline: 'ପ୍ଲେଟ୍‌ପ୍ରୋଫିଟ୍ — ପ୍ରତ୍ୟେକ ଅଂଶରେ ସଠିକତା',
  },
  pa: {
    languageLabel: 'ਭਾਸ਼ਾ',
    heroTitle: 'ਪੈਂਟਰੀ ਤੋਂ ਪਲੇਟ ਤੱਕ,',
    heroSubtitle: 'ਹਰ ਮਿਲੀਗ੍ਰਾਮ \'ਤੇ ਮੁਨਾਫ਼ਾ।',
    heroDescription: 'ਖਾਣਾ ਬਣਾਉਣ ਵਾਲੀਆਂ ਮਾਹਿਰਾਂ ਲਈ ਸੰਪੂਰਨ ਈਕੋਸਿਸਟਮ',
    heroPrecision: '6-ਦਸ਼ਮਲਵ ਸ਼ੁੱਧਤਾ। ਹਰ ਮਿਲੀਗ੍ਰਾਮ ਮਹੱਤਵਪੂਰਨ ਹੈ।',
    ctaButton: 'ਸਿਰਫ਼ $2.99/ਮਹੀਨਾ ਤੋਂ ਸ਼ੁਰੂ ਕਰੋ।',
    leftHeroTitle: 'ਦਾਦੀ ਜੀ ਦੇ ਰਾਜ਼ਾਂ ਨੂੰ ਹਮੇਸ਼ਾ ਲਈ ਸੁਰੱਖਿਅਤ ਰੱਖੋ।',
    leftHeroSubtext: '6-ਦਸ਼ਮਲਵ ਸ਼ੁੱਧਤਾ ਨਾਲ ਆਪਣੇ ਮਾਸਟਰ ਰੈਸਿਪੀ ਬੈਂਕ ਨੂੰ ਬਣਾਓ ਅਤੇ ਸੁਰੱਖਿਅਤ ਕਰੋ।',
    leftHeroCta: 'ਆਰਟੀਸਨ ਸ਼ੁਰੂ ਕਰੋ ($2.99/ਮਹੀਨਾ)',
    rightHeroTitle: 'ਪੈਂਟਰੀ ਤੋਂ ਪਲੇਟ ਤੱਕ ਮੁਨਾਫ਼ਾ।',
    rightHeroSubtext: '100% ਹਾਸ਼ੀਏ ਦੇ ਨਿਯੰਤਰਣ ਲਈ ਕਾਰਜਕਾਰੀ ਸ਼ੈੱਫ ਦਾ ਇੰਜਣ।',
    rightHeroCta: 'ਗੋ ਪ੍ਰੋਫੈਸ਼ਨਲ ($5.99/ਮਹੀਨਾ)',
    rawIngredientsLabel: 'ਕੱਚਾ ਮਾਲ',
    precisionTracking: 'ਸ਼ੁੱਧਤਾ ਸਰੋਤ, ਰੋਜ਼ਾਨਾ ਟਰੈਕ ਕੀਤਾ ਜਾਂਦਾ ਹੈ।',
    engineTitle: 'ਪਲੇਟਪ੍ਰੋਫਿਟ ਇੰਜਣ',
    engineSubtitle: '6-ਦਸ਼ਮਲਵ ਸ਼ੁੱਧਤਾ ਟਰੈਕਿੰਗ',
    unitCostLabel: 'ਇਕਾਈ-ਕੀਮਤ ਸ਼ੁੱਧਤਾ',
    perGramLabel: 'ਪ੍ਰਤੀ ਗ੍ਰਾਮ',
    noWastageLabel: 'ਕੋਈ ਲੁਕਿਆ ਹੋਇਆ ਬਰਬਾਦੀ ਨਹੀਂ। ਹਰ ਮਿਲੀਗ੍ਰਾਮ ਟਰੈਕ ਕੀਤਾ ਗਿਆ।',
    platedLabel: 'ਪਲੇਟਿਡ ਪਰਫੈਕਸ਼ਨ',
    profitableLabel: 'ਹਰ ਵਾਰ ਫ਼ਾਇਦੇਮੰਦ, ਹਰ ਵਾਰ।',
    joinChefs: '150+ ਤੋਂ ਵੱਧ ਦੇਸ਼ਾਂ ਵਿੱਚ ਸ਼ੈੱਫਾਂ ਨਾਲ ਹਰ ਮਿਲੀਗ੍ਰਾਮ ਨੂੰ ਟਰੈਕ ਕਰਨ ਵਿੱਚ ਸ਼ਾਮਲ ਹੋਵੋ।',
    pricingTitle: 'ਪਾਰਦਰਸ਼ੀ ਕੀਮਤਾਂ',
    pricingSubtitle: 'ਇੱਕ ਮਹੀਨੇ ਦੀ ਸ਼ੁੱਧਤਾ ਲਈ ਇੱਕ ਕੱਪ ਕੌਫੀ ਦੀ ਕੀਮਤ ਤੋਂ ਵੀ ਘੱਟ।',
    artisanTier: 'ਕਾਰੀਗਰ',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'ਮਾਸਟਰ ਰੈਸਿਪੀ ਬੈਂਕ—ਵਿਰਾਸਤ-ਤਿਆਰ',
    artisanFeature1: 'ਮਾਸਟਰ ਰੈਸਿਪੀ ਬੈਂਕ (6-ਦਸ਼ਮਲਵ ਸ਼ੁੱਧਤਾ)',
    artisanFeature2: '250 ਤੱਕ ਮਾਸਟਰ ਰੈਸਿਪੀਆਂ',
    artisanFeature3: 'ਹਰੇਕ ਸਮੱਗਰੀ ਲਈ 6-ਦਸ਼ਮਲਵ ਲਾਗਤ',
    artisanScalingHook: 'ਜਿਵੇਂ ਤੁਸੀਂ ਜਾਂਦੇ ਹੋ, ਵਧੋ: ਹਰ ਵਾਧੂ 250 ਪਕਵਾਨਾਂ ਲਈ +$2.99/ਮਹੀਨਾ।',
    artisanCta: 'ਸ਼ੁਰੂ ਕਰੋ ਆਰਟੀਸਨ',
    professionalTier: 'ਪੇਸ਼ੇਵਰ',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'ਮੁਨਾਫ਼ੇ ਦੇ ਕੰਟਰੋਲ ਲਈ ਕਾਰਜਕਾਰੀ ਇੰਜਣ',
    professionalBridge: 'ਆਰਟੀਸਨ ਵਿੱਚ ਸਭ ਕੁਝ, ਨਾਲ ਹੀ:',
    professionalFeature1: 'ਹਰ ਚੀਜ਼ ਵਿਅਕਤੀਗਤ ਤੌਰ \'ਤੇ, ਨਾਲ ਹੀ:',
    professionalFeature2: 'ਐਡਵਾਂਸਡ ਮੇਨੂ ਕੀਮਤਾਂ',
    professionalFeature3: 'ਬਹੁ-ਵਿਭਾਗੀ ਟਰੈਕਿੰਗ',
    professionalFeature4: 'ਰੀਅਲ-ਟਾਈਮ ਮੁਨਾਫ਼ੇ ਦਾ ਵਿਸ਼ਲੇਸ਼ਣ',
    professionalFeature5: 'ਪੀਡੀਐਫ ਐਕਸਪੋਰਟ ਅਤੇ ਸ਼ੇਅਰਿੰਗ',
    professionalCta: 'ਪੇਸ਼ੇਵਰ ਬਣੋ',
    popularBadge: 'ਸਭ ਤੋਂ ਮਸ਼ਹੂਰ',
    utilityNoApp: 'ਕਿਸੇ ਐਪ ਸਟੋਰ ਦੀ ਲੋੜ ਨਹੀਂ ਹੈ',
    utilityOffline: 'ਰਸੋਈ-ਪ੍ਰੂਫ਼ ਔਫਲਾਈਨ',
    utilitySync: 'ਤੁਰੰਤ ਮਲਟੀ-ਡਿਵਾਈਸ ਸਿੰਕ',
    specialistTitle: 'ਹਰ ਰਸੋਈ ਮਾਹਿਰ ਲਈ ਬਣਾਇਆ ਗਿਆ',
    mixologyTitle: 'ਮਿਕਸੋਲੋਜੀ ਲੈਬ',
    mixologyDesc: 'ਹਰ ਇੱਕ ਪਾਉਣ ਲਈ ਸ਼ੁੱਧਤਾ ਦੀ ਕੀਮਤ, ਕਰਾਫਟ ਕਾਕਟੇਲਾਂ ਤੋਂ ਲੈ ਕੇ ਕੌਫੀ ਆਰਟ ਤੱਕ।',
    mixologyUnit: 'ਪ੍ਰਤੀ ਮਿਲੀਲੀਟਰ',
    pastryTitle: 'ਪੇਸਟਰੀ ਕਲਾਕਾਰ',
    pastryDesc: 'ਨਾਜ਼ੁਕ ਮਿਠਾਈਆਂ ਅਤੇ ਬੇਕਿੰਗ ਲਈ ਮਾਈਕ੍ਰੋ-ਮਾਪ ਟਰੈਕਿੰਗ।',
    pastryUnit: 'ਪ੍ਰਤੀ ਗ੍ਰਾਮ',
    spiceTitle: 'ਮਸਾਲਾ ਬਲੈਂਡਰ',
    spiceDesc: 'ਆਪਣੇ ਗੁਪਤ ਮਿਸ਼ਰਣਾਂ ਨੂੰ ਸਮੱਗਰੀ-ਪੱਧਰ ਦੀ ਸ਼ੁੱਧਤਾ ਨਾਲ ਸੁਰੱਖਿਅਤ ਕਰੋ।',
    spiceUnit: 'ਪ੍ਰਤੀ ਗ੍ਰਾਮ',
    problemTitle: 'ਤੁਹਾਡੀ ਰਸੋਈ ਵਿੱਚ ਲੁਕਿਆ ਹੋਇਆ ਪਾੜਾ',
    problemSubtitle: 'ਜ਼ਿਆਦਾਤਰ ਸ਼ੈੱਫ ਕੀਮਤ ਦਾ ਅੰਦਾਜ਼ਾ ਲਗਾਉਂਦੇ ਹਨ। ਅਸੀਂ ਤੁਹਾਨੂੰ ਜਾਣਨ ਵਿੱਚ ਮਦਦ ਕਰਦੇ ਹਾਂ।',
    problemCard1Title: 'ਵਧਦੀ ਸਮੱਗਰੀ ਦੀ ਕੀਮਤ',
    problemCard1Desc: 'ਕੀਮਤਾਂ ਰੋਜ਼ਾਨਾ ਬਦਲਦੀਆਂ ਹਨ, ਪਰ ਤੁਹਾਡਾ ਮੇਨੂ ਉਹੀ ਰਹਿੰਦਾ ਹੈ। ਕੀ ਤੁਸੀਂ ਅਜੇ ਵੀ ਲਾਭਦਾਇਕ ਹੋ?',
    problemCard2Title: 'ਮੇਨੂ ਕੀਮਤਾਂ ਦਾ ਭੇਤ',
    problemCard2Desc: 'ਹਰ ਡਿਸ਼ ਦੀ ਅਸਲ ਕੀਮਤ ਕਿੰਨੀ ਹੈ? ਸਹੀ ਡੇਟਾ ਤੋਂ ਬਿਨਾਂ, ਤੁਸੀਂ ਅੰਨ੍ਹੇ ਰੂਪ ਵਿੱਚ ਉੱਡ ਰਹੇ ਹੋ।',
    problemCard3Title: 'ਬਰਬਾਦ ਹੋਇਆ ਸਮਾਂ ਅਤੇ ਪੈਸਾ',
    problemCard3Desc: 'ਮੈਨੂਅਲ ਸਪ੍ਰੈਡਸ਼ੀਟਾਂ, ਪੁਰਾਣੀਆਂ ਕੀਮਤਾਂ, ਅਤੇ ਅੰਦਾਜ਼ੇ ਘਟਦੇ ਮਾਰਜਿਨ ਵੱਲ ਲੈ ਜਾਂਦੇ ਹਨ।',
    solutionTitle: 'ਪਲੇਟਪ੍ਰੋਫਿਟ: ਤੁਹਾਡੀ ਰੈਸਿਪੀ ਅਤੇ ਲਾਗਤ ਕਮਾਂਡ ਸੈਂਟਰ',
    solutionDesc: 'ਹਰ ਸਮੱਗਰੀ ਦਾ ਧਿਆਨ ਰੱਖੋ, ਵਿਅੰਜਨ ਦੀ ਸਹੀ ਕੀਮਤ ਦਾ ਹਿਸਾਬ ਲਗਾਓ, ਅਤੇ ਲਾਭਦਾਇਕ ਮੇਨੂ ਦੀਆਂ ਕੀਮਤਾਂ ਨਿਰਧਾਰਤ ਕਰੋ—ਸਭ ਇੱਕੋ ਪਲੇਟਫਾਰਮ ਵਿੱਚ।',
    personasTitle: 'ਹਰ ਖਾਣਾ ਬਣਾਉਣ ਦੇ ਸਫ਼ਰ ਲਈ ਬਣਾਈ ਗਈ',
    individualTitle: 'ਇਕੱਲੀ ਘਰੇਲੂ ਔਰਤ',
    individualSubtitle: 'ਸ਼ੌਕੀਨ ਘਰੇਲੂ ਕੁੱਕਾਂ ਲਈ ਬਿਲਕੁਲ ਸਹੀ।',
    individualFeature1: 'ਬੇਅੰਤ ਪਕਵਾਨਾਂ ਸਟੋਰ ਕਰੋ',
    individualFeature2: 'ਸਮੱਗਰੀ ਦੀ ਲਾਗਤ \'ਤੇ ਨਜ਼ਰ ਰੱਖੋ',
    individualFeature3: 'ਸਰਵਿੰਗ ਖਰਚਿਆਂ ਦੀ ਗਣਨਾ ਕਰੋ',
    individualFeature4: 'ਮੂਲ ਰੈਸਿਪੀ ਪ੍ਰਬੰਧਨ',
    individualCta: 'ਇੱਕ ਵਿਅਕਤੀ ਵਜੋਂ ਸ਼ੁਰੂ ਕਰੋ',
    professionalTitle: 'ਪੇਸ਼ੇਵਰ ਸ਼ੈੱਫ',
    professionalSubtitle: 'ਰੈਸਟੋਰੈਂਟਾਂ ਅਤੇ ਖਾਣ-ਪੀਣ ਦੇ ਕਾਰੋਬਾਰਾਂ ਲਈ',
    professionalSpectrum: 'ਮਿਸ਼ੇਲਿਨ-ਸਟਾਰ ਰਸੋਈਆਂ ਤੋਂ ਲੈ ਕੇ ਗਲੋਬਲ ਸਟ੍ਰੀਟ ਫੂਡ ਤੱਕ—ਪਲੇਟਪ੍ਰੋਫਿਟ ਤੁਹਾਡਾ ਕਮਾਂਡ ਸੈਂਟਰ ਹੈ।',
    featuresTitle: 'ਇੱਕ ਲਾਭਦਾਇਕ ਰਸੋਈ ਚਲਾਉਣ ਲਈ ਤੁਹਾਨੂੰ ਜੋ ਕੁਝ ਵੀ ਚਾਹੀਦਾ ਹੈ।',
    feature1Title: 'ਰੈਸਿਪੀ ਬੈਂਕ',
    feature1Desc: 'ਆਪਣੀਆਂ ਸਾਰੀਆਂ ਪਕਵਾਨਾਂ ਨੂੰ ਇੱਕ ਥਾਂ \'ਤੇ ਸਟੋਰ ਕਰੋ, ਵਿਵਸਥਿਤ ਕਰੋ ਅਤੇ ਪ੍ਰਬੰਧਿਤ ਕਰੋ। ਦੁਬਾਰਾ ਕਦੇ ਵੀ ਕੋਈ ਪਕਵਾਨਾ ਨਾ ਗੁਆਓ।',
    feature2Title: 'ਚਾਲੂ/ਬੰਦ ਰੈਸਿਪੀਆਂ',
    feature2Desc: 'ਮੌਸਮ ਜਾਂ ਉਪਲਬਧਤਾ ਦੇ ਆਧਾਰ \'ਤੇ ਪਕਵਾਨਾਂ ਨੂੰ ਚਾਲੂ ਅਤੇ ਬੰਦ ਕਰੋ। ਆਪਣਾ ਮੇਨੂ ਤਾਜ਼ਾ ਰੱਖੋ।',
    feature3Title: 'ਰੀਅਲ-ਟਾਈਮ ਲਾਗਤ ਟਰੈਕਿੰਗ',
    feature3Desc: 'ਸਮੱਗਰੀ ਦੀਆਂ ਕੀਮਤਾਂ ਨੂੰ ਇੱਕ ਵਾਰ ਅੱਪਡੇਟ ਕਰੋ, ਅਤੇ ਸਾਰੀਆਂ ਪਕਵਾਨਾਂ ਵਿੱਚ ਤੁਰੰਤ ਲਾਗਤ ਵਿੱਚ ਤਬਦੀਲੀਆਂ ਵੇਖੋ।',
    feature4Title: 'ਮੇਨੂ ਕੀਮਤ ਬੁੱਧੀ',
    feature4Desc: 'ਨਿਸ਼ਾਨਾ ਹਾਸ਼ੀਏ ਨਿਰਧਾਰਤ ਕਰੋ ਅਤੇ ਲਾਭਦਾਇਕ ਮੇਨੂ ਕੀਮਤਾਂ ਲਈ ਤੁਰੰਤ ਸਿਫ਼ਾਰਸ਼ਾਂ ਪ੍ਰਾਪਤ ਕਰੋ।',
    feature5Title: 'ਬਹੁ-ਮੁਦਰਾ ਸਹਾਇਤਾ',
    feature5Desc: 'ਆਪਣੀ ਸਥਾਨਕ ਮੁਦਰਾ ਵਿੱਚ ਕੰਮ ਕਰੋ। ਅਸੀਂ USD, EUR, QAR, INR, ਅਤੇ 20+ ਤੋਂ ਵੱਧ ਦਾ ਸਮਰਥਨ ਕਰਦੇ ਹਾਂ।',
    feature6Title: 'ਵਿਭਾਗ ਟਰੈਕਿੰਗ',
    feature6Desc: 'ਬਿਹਤਰ ਨਿਯੰਤਰਣ ਲਈ ਰਸੋਈ, ਬਾਰ, ਪੇਸਟਰੀ, ਜਾਂ ਕਸਟਮ ਵਿਭਾਗਾਂ ਦੁਆਰਾ ਪਕਵਾਨਾਂ ਦਾ ਪ੍ਰਬੰਧ ਕਰੋ।',
    footerCta: 'ਕੀ ਤੁਸੀਂ ਆਪਣੀ ਰਸੋਈ ਨੂੰ ਬਦਲਣ ਲਈ ਤਿਆਰ ਹੋ?',
    footerCtaButton: 'ਆਪਣਾ ਮੁਫ਼ਤ ਖਾਤਾ ਸ਼ੁਰੂ ਕਰੋ',
    footerTagline: 'ਪਲੇਟਪ੍ਰੋਫਿਟ — ਹਰ ਹਿੱਸੇ ਵਿੱਚ ਸ਼ੁੱਧਤਾ',
  },
  ta: {
    languageLabel: 'மொழி',
    heroTitle: 'சமையலறையிலிருந்து சாப்பாட்டுத் தட்டு வரை,',
    heroSubtitle: 'ஒவ்வொரு மில்லிகிராமிலும் லாபம்.',
    heroDescription: 'சமையல் நிபுணர்களுக்கான முழுமையான சூழலியல்.',
    heroPrecision: '6-தசம துல்லியம். ஒவ்வொரு மில்லிகிராமும் முக்கியம்.',
    ctaButton: 'மாதத்திற்கு $2.99 என்ற குறைந்த விலையில் தொடங்கலாம்.',
    leftHeroTitle: 'பாட்டியின் ரகசியங்களை என்றென்றும் பாதுகாக்கவும்.',
    leftHeroSubtext: '6 தசமத் துல்லியத்துடன் உங்கள் முதன்மை ரெசிபி வங்கியை உருவாக்கிப் பாதுகாக்கவும்.',
    leftHeroCta: 'ஆர்டிசன் தொடங்கவும் ($2.99/மாதம்)',
    rightHeroTitle: 'சமையலறை முதல் தட்டு வரை லாபம்.',
    rightHeroSubtext: 'நிர்வாக சமையல்காரரின் 100% லாப வரம்பைக் கட்டுப்படுத்துவதற்கான உந்துசக்தி.',
    rightHeroCta: 'Go Professional ($5.99/மாதம்)',
    rawIngredientsLabel: 'பச்சைக் காய்கறிகள்',
    precisionTracking: 'துல்லியமான ஆதாரங்கள், தினமும் கண்காணிக்கப்படுகின்றன.',
    engineTitle: 'பிளேட்லாபம் எஞ்சின்',
    engineSubtitle: '6-தசம துல்லிய கண்காணிப்பு',
    unitCostLabel: 'ஒரு அலகு விலை துல்லியம்',
    perGramLabel: 'கிராம் ஒன்றுக்கு',
    noWastageLabel: 'மறைக்கப்பட்ட விரயம் இல்லை. ஒவ்வொரு மில்லிகிராமும் கண்காணிக்கப்படுகிறது.',
    platedLabel: 'தங்க முலாம் பூசப்பட்ட பூரணத்துவம்',
    profitableLabel: 'லாபகரமானது, ஒவ்வொரு முறையும்',
    joinChefs: '150+ நாடுகளிலுள்ள சமையல்காரர்களுடன் இணைந்து ஒவ்வொரு மில்லிகிராமையும் கண்காணிக்கவும்.',
    pricingTitle: 'வெளிப்படையான விலை நிர்ணயம்',
    pricingSubtitle: 'ஒரு மாத கால துல்லியத்திற்கு ஒரு கப் காபியின் விலையை விட குறைவு.',
    artisanTier: 'கைவினைஞர்',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'தலைமுறை கைவினை வங்கி—பாரம்பரியத்திற்கு ஏற்றது',
    artisanFeature1: 'தலைசிறந்த சமையல் குறிப்பு வங்கி (6-பதம புள்ளித் துல்லியம்)',
    artisanFeature2: '250 வரை தலைசிறந்த சமையல் குறிப்புகள்',
    artisanFeature3: 'ஒவ்வொரு மூலப்பொருளுக்கும் 6-தசம செலவு நிர்ணயம்.',
    artisanScalingHook: 'நீங்கள் முன்னேறும்போது: ஒவ்வொரு கூடுதல் 250 சமையல் குறிப்புகளுக்கும் +$2.99/மாதம்.',
    artisanCta: 'கைவினைஞரைத் தொடங்கு',
    professionalTier: 'தொழில்முறை',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'லாபக் கட்டுப்பாட்டிற்கான நிர்வாக இயந்திரம்',
    professionalBridge: 'கைவினைஞரில் உள்ள அனைத்தும், மேலும்:',
    professionalFeature1: 'தனித்தனியாக எல்லாம், மேலும்:',
    professionalFeature2: 'மேம்பட்ட மெனு விலை நிர்ணயம்',
    professionalFeature3: 'பல துறை கண்காணிப்பு',
    professionalFeature4: 'உடனடி லாப பகுப்பாய்வு',
    professionalFeature5: 'PDF ஏற்றுமதி & பகிர்வு',
    professionalCta: 'தொழில்முறைக்குச் செல்லுங்கள்',
    popularBadge: 'மிகவும் பிரபலம்',
    utilityNoApp: 'ஆப் ஸ்டோர் தேவையில்லை',
    utilityOffline: 'சமையலறைக்கு ஏற்ற ஆஃப்லைன்',
    utilitySync: 'உடனடி பல-சாதன ஒத்திசைவு',
    specialistTitle: 'சமையல் நிபுணர்கள் ஒவ்வொருவருக்கும் ஏற்றவாறு உருவாக்கப்பட்டது.',
    mixologyTitle: 'கலவை ஆய்வகம்',
    mixologyDesc: 'கைவினை காக்டெய்ல் முதல் காபி கலை வரை, ஒவ்வொரு ஊற்றுக்கும் துல்லியமான விலை நிர்ணயம்.',
    mixologyUnit: 'ஒரு மில்லி லிட்டருக்கு',
    pastryTitle: 'பேஸ்ட்ரி கலைஞர்',
    pastryDesc: 'மென்மையான இனிப்புகள் மற்றும் பேக்கிங்கிற்கான நுண் அளவீட்டு கண்காணிப்பு.',
    pastryUnit: 'ஒரு கிராம்',
    spiceTitle: 'மசாலா அரைக்கும் இயந்திரம்',
    spiceDesc: 'பொருள்-நிலை துல்லியத்துடன் உங்கள் ரகசியக் கலவைகளைப் பாதுகாக்கவும்.',
    spiceUnit: 'ஒரு கிராம்',
    problemTitle: 'உங்கள் சமையலறையில் மறைந்திருக்கும் இடைவெளி',
    problemSubtitle: 'பெரும்பாலான சமையல்காரர்கள் விலையை யூகிக்கிறார்கள். நாங்கள் உங்களுக்குத் தெரிவிக்க உதவுகிறோம்.',
    problemCard1Title: 'உயர்ந்து வரும் மூலப்பொருள் செலவுகள்',
    problemCard1Desc: 'விலைகள் தினமும் மாறுகின்றன, ஆனால் உங்கள் பட்டியல் அப்படியே இருக்கிறது. நீங்கள் இன்னும் லாபமாக இருக்கிறீர்களா?',
    problemCard2Title: 'மெனு விலை மர்மம்',
    problemCard2Desc: 'ஒவ்வொரு உணவின் விலையும் உண்மையில் எவ்வளவு? சரியான தரவு இல்லாமல், நீங்கள் குருட்டுத்தனமாகப் பறக்கிறீர்கள்.',
    problemCard3Title: 'வீணான நேரமும் பணமும்',
    problemCard3Desc: 'கைமுறை விரிதாள்கள், காலாவதியான செலவுகள் மற்றும் யூகங்கள் ஆகியவை சுருங்கும் விளிம்புகளுக்கு வழிவகுக்கின்றன.',
    solutionTitle: 'PlateProfit: உங்கள் சமையல் குறிப்பு & விலை கட்டளை மையம்',
    solutionDesc: 'ஒவ்வொரு மூலப்பொருளையும் கண்காணிக்கவும், சரியான செய்முறைக்கான செலவுகளைக் கணக்கிடவும், லாபகரமான மெனு விலைகளை நிர்ணயிக்கவும் - இவை அனைத்தும் ஒரே தளத்தில்.',
    personasTitle: 'ஒவ்வொரு சமையல் பயணத்திற்கும் ஏற்றது',
    individualTitle: 'தனிப்பட்ட இல்லத்தரசி',
    individualSubtitle: 'வீட்டில் சமையல் செய்வதில் ஆர்வமுள்ளவர்களுக்கு ஏற்றது.',
    individualFeature1: 'வரம்பற்ற சமையல் குறிப்புகளை சேமிக்கவும்.',
    individualFeature2: 'பொருள் செலவுகளைக் கண்காணிக்கவும்.',
    individualFeature3: 'சேவைக்கான செலவுகளைக் கணக்கிடுங்கள்.',
    individualFeature4: 'அடிப்படை சமையல் குறிப்பு மேலாண்மை',
    individualCta: 'தனிநபராகத் தொடங்குங்கள்',
    professionalTitle: 'தொழில்முறை சமையல்காரி',
    professionalSubtitle: 'உணவகங்கள் மற்றும் உணவு வணிகங்களுக்கானது',
    professionalSpectrum: 'மிஷெலின்-ஸ்டார் சமையலறைகள் முதல் உலகளாவிய தெரு உணவு வரை - பிளேட் ப்ராஃபிட் உங்கள் கட்டளை மையம்.',
    featuresTitle: 'லாபகரமான சமையலறையை நடத்த உங்களுக்கு தேவையான அனைத்தும்',
    feature1Title: 'சமையல் குறிப்பு வங்கி',
    feature1Desc: 'உங்கள் எல்லா சமையல் குறிப்புகளையும் ஒரே இடத்தில் சேமித்து, ஒழுங்கமைத்து, நிர்வகிக்கவும். இனி ஒருபோதும் சமையல் குறிப்பை இழக்காதீர்கள்.',
    feature2Title: 'செயலில் உள்ள/செயலில் இல்லாத சமையல் குறிப்புகள்',
    feature2Desc: 'பருவகாலம் அல்லது கிடைக்கும் தன்மையின் அடிப்படையில் சமையல் குறிப்புகளை ஆன் மற்றும் ஆஃப் செய்யுங்கள். உங்கள் மெனுவை எப்போதும் புதியதாக வைத்திருங்கள்.',
    feature3Title: 'உடனடி செலவு கண்காணிப்பு',
    feature3Desc: 'ஒரு முறை பொருட்களின் விலையை புதுப்பித்துவிட்டு, அனைத்து சமையல் குறிப்புகளிலும் உடனடியாக ஏற்படும் விலை மாற்றங்களைக் காணலாம்.',
    feature4Title: 'பட்டியல் விலை நுண்ணறிவு',
    feature4Desc: 'குறிப்பிட்ட இலக்கு வரம்புகளை அமைத்து, லாபகரமான உணவுப் பட்டியல் விலைகளுக்கான உடனடி பரிந்துரைகளைப் பெறுங்கள்.',
    feature5Title: 'பன்முக நாணய ஆதரவு',
    feature5Desc: 'உள்ளூர் நாணயத்தில் வேலை செய்யுங்கள். நாங்கள் USD, EUR, QAR, INR மற்றும் 20+ க்கும் மேற்பட்டவற்றை ஆதரிக்கிறோம்.',
    feature6Title: 'துறை கண்காணிப்பு',
    feature6Desc: 'சிறந்த கட்டுப்பாட்டிற்காக சமையலறை, மதுபானம், பேஸ்ட்ரி அல்லது தனிப்பயன் துறைகளின்படி சமையல் குறிப்புகளை ஒழுங்கமைக்கவும்.',
    footerCta: 'உங்கள் சமையலறையை மாற்ற தயாரா?',
    footerCtaButton: 'உங்கள் இலவச கணக்கை தொடங்கவும்',
    footerTagline: 'பிளேட் லாபம் - ஒவ்வொரு பகுதியிலும் துல்லியம்',
  },
  te: {
    languageLabel: 'భాష',
    heroTitle: 'ప్యాంట్రీ నుండి ప్లేట్ వరకు,',
    heroSubtitle: 'ప్రతి మిల్లీగ్రాముపై లాభం.',
    heroDescription: 'వంట నిపుణుల కోసం పూర్తి పర్యావరణ వ్యవస్థ',
    heroPrecision: '6-దశాంశ ఖచ్చితత్వం. ప్రతి మిల్లీగ్రామ్ ముఖ్యమే.',
    ctaButton: 'నెలకు $2.99 నుండి ప్రారంభించండి',
    leftHeroTitle: 'నానమ్మ రహస్యాలను ఎప్పటికీ భద్రపరచండి.',
    leftHeroSubtext: '6-దశాంశ ఖచ్చితత్వంతో మీ మాస్టర్ రెసిపీ బ్యాంకును నిర్మించి, రక్షించండి.',
    leftHeroCta: 'ఆర్టిసన్ ప్రారంభించండి ($2.99/నెల)',
    rightHeroTitle: 'ప్యాంట్రీ నుండి ప్లేట్ లాభదాయకత.',
    rightHeroSubtext: 'ఎగ్జిక్యూటివ్ చెఫ్ యొక్క 100% మార్జిన్ నియంత్రణ కోసం ఇంజిన్.',
    rightHeroCta: 'గో ప్రొఫెషనల్ ($5.99/నెల)',
    rawIngredientsLabel: 'పచ్చి పదార్థాలు',
    precisionTracking: 'ఖచ్చితమైన సోర్సింగ్, ప్రతిరోజు ట్రాక్ చేయబడుతుంది.',
    engineTitle: 'ప్లేట్‌ప్రాఫిట్ ఇంజిన్',
    engineSubtitle: '6-దశాంశ ఖచ్చితత్వ ట్రాకింగ్',
    unitCostLabel: 'యూనిట్-ధర ఖచ్చితత్వం',
    perGramLabel: 'గ్రాముకు',
    noWastageLabel: 'దాచిన వృథా లేదు. ప్రతి మిల్లీగ్రాము ట్రాక్ చేయబడుతుంది.',
    platedLabel: 'ప్లేటెడ్ పర్ఫెక్షన్',
    profitableLabel: 'లాభదాయకం, ప్రతిసారీ',
    joinChefs: 'ప్రతి మిల్లీగ్రామును ట్రాక్ చేసే 150+ దేశాలలో చెఫ్‌లతో చేరండి.',
    pricingTitle: 'పారదర్శక ధరలు',
    pricingSubtitle: 'ఒక నెల కచ్చితత్వానికి ఒక కప్పు కాఫీ ఖర్చు కంటే తక్కువ.',
    artisanTier: 'హస్తకళాకారిణి',
    artisanPrice: '$2.99',
    artisanPeriod: '/mo',
    artisanDesc: 'ది మాస్టర్ రెసిపీ బ్యాంక్—హెరిటేజ్-రెడీ',
    artisanFeature1: 'మాస్టర్ రెసిపీ బ్యాంక్ (6-దశాంశ ఖచ్చితత్వం)',
    artisanFeature2: '250 వరకు మాస్టర్ వంటకాలు',
    artisanFeature3: 'ప్రతి పదార్థానికి 6-దశాంశాల ధర నిర్ణయం.',
    artisanScalingHook: 'మీరు వెళ్లే కొద్దీ పెంచుకోండి: ప్రతి అదనపు 250 వంటకాలకు +$2.99/నెల.',
    artisanCta: 'ఆర్టిసన్ ప్రారంభించండి',
    professionalTier: 'వృత్తిపరమైన',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'లాభ నియంత్రణ కోసం కార్యనిర్వాహక యంత్రం',
    professionalBridge: 'ఆర్టిసన్‌లో ప్రతిదీ, అదనంగా:',
    professionalFeature1: 'వ్యక్తిగతంగా ప్రతిదీ, అదనంగా:',
    professionalFeature2: 'అధునాతన మెనూ ధరలు',
    professionalFeature3: 'బహుళ-విభాగాల ట్రాకింగ్',
    professionalFeature4: 'నిజ-సమయ లాభ విశ్లేషణ',
    professionalFeature5: 'PDF ఎగుమతి & భాగస్వామ్యం',
    professionalCta: 'వృత్తిపరంగా వెళ్ళు',
    popularBadge: 'అత్యంత ప్రాచుర్యం పొందినది',
    utilityNoApp: 'యాప్ స్టోర్ అవసరం లేదు',
    utilityOffline: 'వంటగదికి అనుకూలం కాని ఆఫ్‍లైన్',
    utilitySync: 'తక్షణ బహుళ-పరికర సమకాలీకరణ',
    specialistTitle: 'ప్రతి వంటల నిపుణురాలి కోసం నిర్మించబడింది.',
    mixologyTitle: 'మిక్సాలజీ ల్యాబ్',
    mixologyDesc: 'క్రాఫ్ట్ కాక్టెయిల్స్ నుండి కాఫీ ఆర్ట్ వరకు, ప్రతి పోర్ కోసం ఖచ్చితమైన ధర.',
    mixologyUnit: 'ప్రతి ml',
    pastryTitle: 'పేస్ట్రీ కళాకారిణి',
    pastryDesc: 'సున్నితమైన డెజర్ట్‌లు మరియు బేకింగ్ కోసం మైక్రో-మెజర్‌మెంట్ ట్రాకింగ్.',
    pastryUnit: 'గ్రాముకు',
    spiceTitle: 'సుగంధ ద్రవ్యాల మిక్సీ',
    spiceDesc: 'పదార్థాల స్థాయి ఖచ్చితత్వంతో మీ రహస్య మిశ్రమాలను రక్షించండి.',
    spiceUnit: 'గ్రాముకు',
    problemTitle: 'మీ వంటగదిలో దాగి ఉన్న ఖాళీ',
    problemSubtitle: 'చాలా మంది చెఫ్‌లు ధరను ఊహిస్తారు. మేము మీకు తెలియజేస్తాము.',
    problemCard1Title: 'పెరుగుతున్న ముడిసరుకుల ధరలు',
    problemCard1Desc: 'ధరలు రోజురోజుకు మారుతూ ఉంటాయి, కానీ మీ మెనూ మాత్రం అలాగే ఉంటుంది. మీరు ఇంకా లాభాల్లోనే ఉన్నారా?',
    problemCard2Title: 'మెనూ ధరల రహస్యం',
    problemCard2Desc: 'ప్రతి వంటకం ధర ఎంత? ఖచ్చితమైన డేటా లేకుండా, మీరు గుడ్డిగా ఎగురుతున్నారు.',
    problemCard3Title: 'వ్యర్థమైన సమయం & డబ్బు',
    problemCard3Desc: 'మాన్యువల్ స్ప్రెడ్‌షీట్‌లు, పాత ఖర్చులు మరియు ఊహాగానాలు తగ్గిపోతున్న మార్జిన్‌లకు దారితీస్తాయి.',
    solutionTitle: 'PlateProfit: మీ వంటకం & ధర కమాండ్ సెంటర్',
    solutionDesc: 'ప్రతి పదార్థాన్ని ట్రాక్ చేయండి, ఖచ్చితమైన వంటకాల ఖర్చులను లెక్కించండి మరియు లాభదాయకమైన మెనూ ధరలను సెట్ చేయండి - అన్నీ ఒకే వేదికపై.',
    personasTitle: 'ప్రతి వంటల ప్రయాణం కోసం నిర్మించబడింది',
    individualTitle: 'ఒంటరి గృహిణి',
    individualSubtitle: 'అంకితభావంతో ఇంటిలో వంట చేసేవారికి ఇది చాలా అనుకూలంగా ఉంటుంది.',
    individualFeature1: 'అపరిమిత వంటకాలను నిల్వ చేయండి.',
    individualFeature2: 'పదార్థాల ఖర్చులను ట్రాక్ చేయండి',
    individualFeature3: 'సేవల ఖర్చులను లెక్కించండి',
    individualFeature4: 'ప్రాథమిక వంటకాల నిర్వహణ',
    individualCta: 'వ్యక్తిగతంగా ప్రారంభించండి',
    professionalTitle: 'వృత్తిపరమైన చెఫ్',
    professionalSubtitle: 'రెస్టారెంట్లు మరియు ఆహార వ్యాపారాల కోసం',
    professionalSpectrum: 'మిచెలిన్-స్టార్ కిచెన్‌ల నుండి గ్లోబల్ స్ట్రీట్ ఫుడ్ వరకు—ప్లేట్‌ప్రాఫిట్ మీ కమాండ్ సెంటర్.',
    featuresTitle: 'లాభదాయకమైన వంటగదిని నడపడానికి మీకు కావలసిన ప్రతిదీ',
    feature1Title: 'రెసిపీ బ్యాంక్',
    feature1Desc: 'మీ వంటకాలన్నింటినీ ఒకే చోట నిల్వ చేయండి, క్రమబద్ధీకరించండి మరియు నిర్వహించండి. మళ్ళీ ఎప్పుడూ వంటకాన్ని పోగొట్టుకోకండి.',
    feature2Title: 'క్రియాశీల/నిష్క్రియాత్మక వంటకాలు',
    feature2Desc: 'రుతువు లేదా లభ్యత ఆధారంగా వంటకాలను ఆన్ మరియు ఆఫ్ చేయండి. మీ మెనూను తాజాగా ఉంచండి.',
    feature3Title: 'నిజ-సమయ వ్యయ పర్యవేక్షణ',
    feature3Desc: 'ఒకసారి పదార్థాల ధరలను నవీకరించండి, మరియు అన్ని వంటకాలలో తక్షణమే ఖర్చు మార్పులను చూడండి.',
    feature4Title: 'మెనూ ధరల సమాచారం',
    feature4Desc: 'లక్ష్య మార్జిన్‌లను సెట్ చేయండి మరియు లాభదాయకమైన మెనూ ధరల కోసం తక్షణ సిఫార్సులను పొందండి.',
    feature5Title: 'బహుళ కరెన్సీ మద్దతు',
    feature5Desc: 'మీ స్థానిక కరెన్సీలో పని చేయండి. మేము USD, EUR, QAR, INR మరియు 20+ కంటే ఎక్కువ వాటికి మద్దతు ఇస్తాము.',
    feature6Title: 'డిపార్ట్‌మెంట్ ట్రాకింగ్',
    feature6Desc: 'మంచి నియంత్రణ కోసం వంటగది, బార్, పేస్ట్రీ లేదా అనుకూల విభాగాల ప్రకారం వంటకాలను నిర్వహించండి.',
    footerCta: 'మీ వంటగదిని మార్చడానికి సిద్ధంగా ఉన్నారా?',
    footerCtaButton: 'మీ ఉచిత ఖాతాను ప్రారంభించండి',
    footerTagline: 'PlateProfit — ప్రతి భాగంలో ఖచ్చితత్వం',
  },
  
  ja: {
    // Header
    languageLabel: '言語',
    
    // Hero Section
    heroTitle: 'パントリーからプレートへ、',
    heroSubtitle: 'すべてのミリグラムで利益.',
    heroDescription: '料理のプロのための完全なエコシステム',
    heroPrecision: '6桁の精度。すべてのミリグラムが重要です。',
    ctaButton: '月額$2.99から',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'おばあちゃんの秘密を永遠に保存.',
    leftHeroSubtext: '6桁の精度でマスターレシピバンクを構築・保護.',
    leftHeroCta: 'アーティザン開始 ($2.99/月)',
    rightHeroTitle: 'パントリーからプレートへの収益性.',
    rightHeroSubtext: 'エグゼクティブシェフの100%マージン管理エンジン.',
    rightHeroCta: 'プロへ ($5.99/月)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: '生材料',
    precisionTracking: '精密な調達、毎日追跡',
    engineTitle: 'PlateProfit エンジン',
    engineSubtitle: '6桁精度トラッキング',
    unitCostLabel: 'ユニットコスト精度',
    perGramLabel: 'グラムあたり',
    noWastageLabel: '隠れた無駄なし。すべてのミリグラムを追跡。',
    platedLabel: '盛り付けの完璧さ',
    profitableLabel: '収益性、毎回',
    joinChefs: '150か国以上のシェフがミリグラムを追跡しています',
    
    // Pricing Section
    pricingTitle: '透明な料金体系',
    pricingSubtitle: '1か月の精度のためのコーヒー1杯未満の費用.',
    artisanTier: 'アーティザン',
    artisanPrice: '$2.99',
    artisanPeriod: '/月',
    artisanDesc: 'マスターレシピバンク—伝承準備完了',
    artisanFeature1: 'マスターレシピバンク（小数点以下6桁の精度）',
    artisanFeature2: '最大250のマスターレシピ',
    artisanFeature3: 'すべての食材の小数点以下6桁のコスト計算',
    artisanScalingHook: '成長に合わせて: 250レシピごとに+$2.99/月。',
    artisanCta: 'アルチザンを開始',
    professionalTier: 'プロフェッショナル',
    professionalPrice: '$5.99',
    professionalPeriod: '/月',
    professionalDesc: '利益管理のためのエグゼクティブエンジン',
    professionalBridge: 'アルチザンのすべて、さらに:',
    professionalFeature1: '無制限のマスターレシピ',
    professionalFeature2: '高度なメニュー構築と動的価格設定',
    professionalFeature3: '複数デバイスへのメニュー共有',
    professionalFeature4: 'リアルタイム利益分析と部門別追跡',
    professionalFeature5: 'PDF エクスポートとプロフェッショナルレポート',
    professionalCta: 'プロフェッショナルを開始',
    popularBadge: '最も人気',
    
    // PWA Utility Bar
    utilityNoApp: 'App Storeは不要',
    utilityOffline: 'キッチンでオフライン対応',
    utilitySync: '瞬時マルチデバイス同期',
    
    // Specialist Verticals
    specialistTitle: 'すべての料理専門家のために構築',
    mixologyTitle: 'ミクソロジーラボ',
    mixologyDesc: 'クラフトカクテルからコーヒーアートまで、一注ぎごとの精密コスト計算.',
    mixologyUnit: 'mlあたり',
    pastryTitle: 'パティシエ',
    pastryDesc: '繊細なデザートとベーキングのマイクロ測定追跡.',
    pastryUnit: 'グラムあたり',
    spiceTitle: 'スパイスブレンダー',
    spiceDesc: '成分レベルの精度で秘密のブレンドを保護.',
    spiceUnit: 'グラムあたり',
    
    // Problem/Solution
    problemTitle: 'キッチンの隠れたギャップ',
    problemSubtitle: 'ほとんどのシェフは価格を推測します。私たちはあなたが知るのを助けます。',
    problemCard1Title: '上昇する食材コスト',
    problemCard1Desc: '価格は毎日変わりますが、メニューは同じままです。まだ利益が出ていますか？',
    problemCard2Title: 'メニュー価格の謎',
    problemCard2Desc: '各料理の本当のコストはいくらですか？正確なデータがなければ、盲目的に飛んでいます。',
    problemCard3Title: '時間とお金の無駄',
    problemCard3Desc: '手動スプレッドシート、古いコスト、推測はマージンを縮小させます。',
    
    solutionTitle: 'PlateProfit：レシピとコストのコマンドセンター',
    solutionDesc: 'すべての食材を追跡し、正確なレシピコストを計算し、収益性の高いメニュー価格を設定—すべて1つのプラットフォームで。',
    
    // Personas
    personasTitle: 'すべての料理の旅のために構築',
    
    individualTitle: '個人の主婦',
    individualSubtitle: '情熱的な家庭料理人に最適',
    individualFeature1: '無制限のレシピを保存',
    individualFeature2: '食材コストを追跡',
    individualFeature3: 'サービングコストを計算',
    individualFeature4: '基本的なレシピ管理',
    individualCta: '個人として始める',
    
    professionalTitle: 'プロのシェフ',
    professionalSubtitle: 'レストランや食品ビジネス向け',
    professionalSpectrum: 'ミシュランスターキッチンから世界のストリートフードまで—PlateProfit はあなたのコマンドセンターです。',
    professionalFeature1: '個人のすべて、さらに：',
    professionalFeature2: '高度なメニュー価格設定',
    professionalFeature3: 'マルチ部門追跡',
    professionalFeature4: 'リアルタイム利益分析',
    professionalFeature5: 'PDFエクスポートと共有',
    professionalCta: 'プロフェッショナルになる',
    
    // Features
    featuresTitle: '収益性の高いキッチンを運営するために必要なすべて',
    
    feature1Title: 'レシピバンク',
    feature1Desc: 'すべてのレシピを1か所に保存、整理、管理します。二度とレシピを失いません。',
    
    feature2Title: 'アクティブ/非アクティブレシピ',
    feature2Desc: '季節性や可用性に基づいてレシピを切り替えます。メニューを新鮮に保ちます。',
    
    feature3Title: 'リアルタイムコスト追跡',
    feature3Desc: '食材価格を一度更新すると、すべてのレシピでコスト変更を即座に確認できます。',
    
    feature4Title: 'メニュー価格インテリジェンス',
    feature4Desc: '目標マージンを設定し、収益性の高いメニュー価格の即座の推奨事項を取得します。',
    
    feature5Title: 'マルチ通貨サポート',
    feature5Desc: '現地通貨で作業します。USD、EUR、QAR、INR、および20以上の通貨をサポートしています。',
    
    feature6Title: '部門追跡',
    feature6Desc: 'キッチン、バー、ペストリー、またはカスタム部門ごとにレシピを整理して、より良い制御を実現します。',
    
    // Footer
    footerCta: 'キッチンを変革する準備はできていますか？',
    footerCtaButton: '無料アカウントを始める',
    footerTagline: 'PlateProfit — すべての部分に精度',
  },
  
  it: {
    // Header
    languageLabel: 'Lingua',
    
    // Hero Section
    heroTitle: 'Dalla Dispensa al Piatto,',
    heroSubtitle: 'Profitto su Ogni Milligrammo.',
    heroDescription: 'L\'ecosistema completo per professionisti culinari',
    heroPrecision: 'Precisione a 6 Decimali. Ogni Milligrammo Conta.',
    ctaButton: 'Da $2.99/mese',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'Salva i Segreti della Nonna per Sempre.',
    leftHeroSubtext: 'Costruisci e proteggi la tua Banca Ricette Master con precisione a 6 decimali.',
    leftHeroCta: 'Inizia Artigiano ($2.99/mese)',
    rightHeroTitle: 'Redditività dalla Dispensa al Piatto.',
    rightHeroSubtext: 'Il motore dello Chef Esecutivo per controllo 100% del margine.',
    rightHeroCta: 'Vai Pro ($5.99/mese)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: 'Ingredienti Grezzi',
    precisionTracking: 'Approvvigionamento preciso, monitorato quotidianamente',
    engineTitle: 'Motore PlateProfit',
    engineSubtitle: 'Tracciamento di precisione a 6 decimali',
    unitCostLabel: 'Precisione Costo Unitario',
    perGramLabel: 'per Grammo',
    noWastageLabel: 'Nessuno spreco nascosto. Ogni milligrammo tracciato.',
    platedLabel: 'Perfezione Impiattata',
    profitableLabel: 'Redditizio, sempre',
    joinChefs: 'Unisciti agli chef in oltre 150 paesi che tracciano ogni milligrammo',
    
    // Pricing Section
    pricingTitle: 'Prezzi Trasparenti',
    pricingSubtitle: 'Meno del costo di una tazza di caffè per un mese di precisione.',
    artisanTier: 'Artigiano',
    artisanPrice: '$2.99',
    artisanPeriod: '/mese',
    artisanDesc: 'La Banca Ricette Maestre—Pronta per l\'Eredità',
    artisanFeature1: 'La Banca Ricette Maestre (precisione a 6 decimali)',
    artisanFeature2: 'Fino a 250 Ricette Maestre',
    artisanFeature3: 'Costo a 6 Decimali per ogni ingrediente',
    artisanScalingHook: 'Cresci al tuo ritmo: +$2.99/mese per ogni 250 ricette aggiuntive.',
    artisanCta: 'Inizia Artigiano',
    professionalTier: 'Professionale',
    professionalPrice: '$5.99',
    professionalPeriod: '/mese',
    professionalDesc: 'Il Motore Esecutivo per il Controllo dei Profitti',
    professionalBridge: 'Tutto in Artigiano, più:',
    professionalFeature1: 'Ricette Maestre Illimitate',
    professionalFeature2: 'Costruzione Menu Avanzata e Prezzi Dinamici',
    professionalFeature3: 'Condivisione Menu su Più Dispositivi',
    professionalFeature4: 'Analisi Profitti in Tempo Reale e Tracciamento Multi-dipartimentale',
    professionalFeature5: 'Esportazione PDF e Reportistica Professionale',
    professionalCta: 'Inizia Professionale',
    popularBadge: 'Più Popolare',
    
    // PWA Utility Bar
    utilityNoApp: 'Nessun App Store Necessario',
    utilityOffline: 'Funziona Offline in Cucina',
    utilitySync: 'Sincronizzazione Istantanea Multi-Dispositivo',
    
    // Specialist Verticals
    specialistTitle: 'Creato per Ogni Specialista Culinario',
    mixologyTitle: 'Il Laboratorio di Mixology',
    mixologyDesc: 'Costi precisi per ogni versata, da cocktail artigianali all\'arte del caffè.',
    mixologyUnit: 'per ml',
    pastryTitle: 'L\'Artista Pasticcere',
    pastryDesc: 'Tracciamento micro-misurazione per dolci delicati e pasticceria.',
    pastryUnit: 'per grammo',
    spiceTitle: 'Il Miscelatore di Spezie',
    spiceDesc: 'Proteggi le tue miscele segrete con precisione a livello di ingrediente.',
    spiceUnit: 'per grammo',
    
    // Problem/Solution
    problemTitle: 'Il Divario Nascosto nella Tua Cucina',
    problemSubtitle: 'La maggior parte degli chef indovina i prezzi. Noi ti aiutiamo a sapere.',
    problemCard1Title: 'Costi Crescenti degli Ingredienti',
    problemCard1Desc: 'I prezzi cambiano quotidianamente, ma il tuo menu rimane lo stesso. Sei ancora redditizio?',
    problemCard2Title: 'Mistero dei Prezzi del Menu',
    problemCard2Desc: 'Quanto costa davvero ogni piatto? Senza dati accurati, voli alla cieca.',
    problemCard3Title: 'Tempo e Denaro Sprecati',
    problemCard3Desc: 'Fogli di calcolo manuali, costi obsoleti e supposizioni riducono i margini.',
    
    solutionTitle: 'PlateProfit: Il Tuo Centro di Comando Ricette e Costi',
    solutionDesc: 'Traccia ogni ingrediente, calcola i costi esatti delle ricette e stabilisci prezzi di menu redditizi—tutto su una piattaforma.',
    
    // Personas
    personasTitle: 'Costruito per Ogni Percorso Culinario',
    individualTitle: 'Casalinga Individuale',
    individualSubtitle: 'Perfetto per cuochi casalinghi appassionati',
    individualFeature1: 'Memorizza ricette illimitate',
    individualFeature2: 'Traccia i costi degli ingredienti',
    individualFeature3: 'Calcola i costi per porzione',
    individualFeature4: 'Gestione base delle ricette',
    individualCta: 'Inizia come Individuale',
    professionalTitle: 'Chef Professionista',
    professionalSubtitle: 'Per ristoranti e attività alimentari',
    professionalSpectrum: 'Dalle cucine stellate Michelin al cibo di strada globale—PlateProfit è il tuo centro di comando.',
    professionalFeature1: 'Tutto in Individuale, più:',
    professionalFeature2: 'Prezzi avanzati del menu',
    professionalFeature3: 'Tracciamento multidipartimentale',
    professionalFeature4: 'Analisi dei profitti in tempo reale',
    professionalFeature5: 'Esportazione PDF e condivisione',
    professionalCta: 'Diventa Professionista',
    
    // Features
    featuresTitle: 'Tutto ciò di cui Hai Bisogno per una Cucina Redditizia',
    feature1Title: 'Banca Ricette',
    feature1Desc: 'Memorizza, organizza e gestisci tutte le tue ricette in un unico posto. Non perdere mai più una ricetta.',
    feature2Title: 'Ricette Attive/Inattive',
    feature2Desc: 'Attiva o disattiva le ricette in base alla stagionalità o disponibilità. Mantieni il tuo menu fresco.',
    feature3Title: 'Tracciamento Costi in Tempo Reale',
    feature3Desc: 'Aggiorna i prezzi degli ingredienti una volta e vedi i cambiamenti di costo in tutte le ricette istantaneamente.',
    feature4Title: 'Intelligenza Prezzi Menu',
    feature4Desc: 'Imposta margini target e ottieni raccomandazioni istantanee per prezzi di menu redditizi.',
    feature5Title: 'Supporto Multi-Valuta',
    feature5Desc: 'Lavora nella tua valuta locale. Supportiamo USD, EUR, QAR, INR e oltre 20 valute.',
    feature6Title: 'Tracciamento Dipartimenti',
    feature6Desc: 'Organizza le ricette per Cucina, Bar, Pasticceria o dipartimenti personalizzati per un migliore controllo.',
    
    // Footer
    footerCta: 'Pronto a Trasformare la Tua Cucina?',
    footerCtaButton: 'Inizia il Tuo Account Gratuito',
    footerTagline: 'PlateProfit — Precisione in Ogni Porzione',
  },
  
  pt: {
    // Header
    languageLabel: 'Idioma',
    
    // Hero Section
    heroTitle: 'Da Despensa ao Prato,',
    heroSubtitle: 'Lucro em Cada Miligrama.',
    heroDescription: 'O ecossistema completo para profissionais culinários',
    heroPrecision: 'Precisão de 6 Decimais. Cada Miligrama Conta.',
    ctaButton: 'A partir de $2.99/mês',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'Salve os Segredos da Vovó para Sempre.',
    leftHeroSubtext: 'Construa e proteja seu Banco de Receitas Mestre com precisão de 6 decimais.',
    leftHeroCta: 'Começar Artesão ($2.99/mês)',
    rightHeroTitle: 'Lucratividade da Despensa ao Prato.',
    rightHeroSubtext: 'O motor do Chef Executivo para controle 100% de margem.',
    rightHeroCta: 'Ir Pro ($5.99/mês)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: 'Ingredientes Brutos',
    precisionTracking: 'Fornecimento preciso, rastreado diariamente',
    engineTitle: 'Motor PlateProfit',
    engineSubtitle: 'Rastreamento de precisão de 6 decimais',
    unitCostLabel: 'Precisão de Custo Unitário',
    perGramLabel: 'por Grama',
    noWastageLabel: 'Sem desperdício oculto. Cada miligrama rastreado.',
    platedLabel: 'Perfeição Emplatada',
    profitableLabel: 'Lucrativo, sempre',
    joinChefs: 'Junte-se a chefs em mais de 150 países rastreando cada miligrama',
    
    // Pricing Section
    pricingTitle: 'Preços Transparentes',
    pricingSubtitle: 'Menos que o custo de uma xícara de café por um mês de precisão.',
    artisanTier: 'Artesão',
    artisanPrice: '$2.99',
    artisanPeriod: '/mês',
    artisanDesc: 'O Banco de Receitas Mestras—Pronto para a Herança',
    artisanFeature1: 'O Banco de Receitas Mestras (precisão de 6 decimais)',
    artisanFeature2: 'Até 250 Receitas Mestras',
    artisanFeature3: 'Custo de 6 Decimais para cada ingrediente',
    artisanScalingHook: 'Cresça no seu ritmo: +$2.99/mês para cada 250 receitas adicionais.',
    artisanCta: 'Começar Artesão',
    professionalTier: 'Profissional',
    professionalPrice: '$5.99',
    professionalPeriod: '/mês',
    professionalDesc: 'O Motor Executivo para Controle de Lucros',
    professionalBridge: 'Tudo em Artesão, mais:',
    professionalFeature1: 'Receitas Mestras Ilimitadas',
    professionalFeature2: 'Construção de Cardápio Avançada e Precificação Dinâmica',
    professionalFeature3: 'Compartilhamento de Cardápio em Vários Dispositivos',
    professionalFeature4: 'Análise de Lucro em Tempo Real e Rastreamento Multi-departamental',
    professionalFeature5: 'Exportação PDF e Relatórios Profissionais',
    professionalCta: 'Começar Profissional',
    popularBadge: 'Mais Popular',
    
    // PWA Utility Bar
    utilityNoApp: 'Não Precisa de App Store',
    utilityOffline: 'Funciona Offline na Cozinha',
    utilitySync: 'Sincronização Instantânea Multi-Dispositivos',
    
    // Specialist Verticals
    specialistTitle: 'Criado para Cada Especialista Culinário',
    mixologyTitle: 'O Laboratório de Mixologia',
    mixologyDesc: 'Custeio preciso para cada despejo, de coquetéis artesanais à arte do café.',
    mixologyUnit: 'por ml',
    pastryTitle: 'O Artista Confeiteiro',
    pastryDesc: 'Rastreamento de micromedições para sobremesas delicadas e confeitaria.',
    pastryUnit: 'por grama',
    spiceTitle: 'O Misturador de Especiarias',
    spiceDesc: 'Proteja suas misturas secretas com precisão a nível de ingrediente.',
    spiceUnit: 'por grama',
    
    // Problem/Solution
    problemTitle: 'A Lacuna Oculta na Sua Cozinha',
    problemSubtitle: 'A maioria dos chefs adivinha os preços. Nós ajudamos você a saber.',
    problemCard1Title: 'Custos Crescentes de Ingredientes',
    problemCard1Desc: 'Os preços mudam diariamente, mas seu cardápio permanece o mesmo. Você ainda é lucrativo?',
    problemCard2Title: 'Mistério de Preços do Cardápio',
    problemCard2Desc: 'Quanto cada prato realmente custa? Sem dados precisos, você está voando às cegas.',
    problemCard3Title: 'Tempo e Dinheiro Desperdiçados',
    problemCard3Desc: 'Planilhas manuais, custos desatualizados e suposições reduzem as margens.',
    
    solutionTitle: 'PlateProfit: Seu Centro de Comando de Receitas e Custos',
    solutionDesc: 'Rastreie cada ingrediente, calcule custos exatos de receitas e defina preços de cardápio lucrativos—tudo em uma plataforma.',
    
    // Personas
    personasTitle: 'Construído para Cada Jornada Culinária',
    individualTitle: 'Dona de Casa Individual',
    individualSubtitle: 'Perfeito para cozinheiros caseiros apaixonados',
    individualFeature1: 'Armazene receitas ilimitadas',
    individualFeature2: 'Rastreie custos de ingredientes',
    individualFeature3: 'Calcule custos por porção',
    individualFeature4: 'Gerenciamento básico de receitas',
    individualCta: 'Começar como Individual',
    professionalTitle: 'Chef Profissional',
    professionalSubtitle: 'Para restaurantes e negócios de alimentos',
    professionalSpectrum: 'De cozinhas com estrelas Michelin à comida de rua global—PlateProfit é seu centro de comando.',
    professionalFeature1: 'Tudo em Individual, mais:',
    professionalFeature2: 'Preços avançados de cardápio',
    professionalFeature3: 'Rastreamento multidepartamental',
    professionalFeature4: 'Análise de lucros em tempo real',
    professionalFeature5: 'Exportação PDF e compartilhamento',
    professionalCta: 'Torne-se Profissional',
    
    // Features
    featuresTitle: 'Tudo o que Você Precisa para uma Cozinha Lucrativa',
    feature1Title: 'Banco de Receitas',
    feature1Desc: 'Armazene, organize e gerencie todas as suas receitas em um só lugar. Nunca perca uma receita novamente.',
    feature2Title: 'Receitas Ativas/Inativas',
    feature2Desc: 'Ative ou desative receitas com base na sazonalidade ou disponibilidade. Mantenha seu cardápio fresco.',
    feature3Title: 'Rastreamento de Custos em Tempo Real',
    feature3Desc: 'Atualize os preços dos ingredientes uma vez e veja as mudanças de custo em todas as receitas instantaneamente.',
    feature4Title: 'Inteligência de Preços de Cardápio',
    feature4Desc: 'Defina margens alvo e obtenha recomendações instantâneas para preços de cardápio lucrativos.',
    feature5Title: 'Suporte Multi-Moeda',
    feature5Desc: 'Trabalhe em sua moeda local. Suportamos USD, EUR, QAR, INR e mais de 20 moedas.',
    feature6Title: 'Rastreamento de Departamentos',
    feature6Desc: 'Organize receitas por Cozinha, Bar, Confeitaria ou departamentos personalizados para melhor controle.',
    
    // Footer
    footerCta: 'Pronto para Transformar Sua Cozinha?',
    footerCtaButton: 'Comece Sua Conta Grátis',
    footerTagline: 'PlateProfit — Precisão em Cada Porção',
  },
  
  th: {
    // Header
    languageLabel: 'ภาษา',
    
    // Hero Section
    heroTitle: 'จากครัวสู่จาน,',
    heroSubtitle: 'กำไรในทุกมิลลิกรัม.',
    heroDescription: 'ระบบนิเวศที่สมบูรณ์สำหรับมืออาชีพด้านอาหาร',
    heroPrecision: 'ความแม่นยำ 6 ตำแหน่งทศนิยม. ทุกมิลลิกรัมมีความสำคัญ.',
    ctaButton: 'เริ่มต้นที่ $2.99/เดือน',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: 'บันทึกความลับของคุณยายไว้ตลอดกาล.',
    leftHeroSubtext: 'สร้างและปกป้องธนาคารสูตรหลักของคุณด้วยความแม่นยำ 6 ตำแหน่งทศนิยม.',
    leftHeroCta: 'เริ่มช่างฝีมือ ($2.99/เดือน)',
    rightHeroTitle: 'ความสามารถในการทำกำไรจากครัวสู่จาน.',
    rightHeroSubtext: 'เครื่องมือของเชฟระดับผู้บริหารสำหรับการควบคุมกำไร 100%.',
    rightHeroCta: 'ไปมืออาชีพ ($5.99/เดือน)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: 'วัตถุดิบ',
    precisionTracking: 'การจัดหาที่แม่นยำ ติดตามรายวัน',
    engineTitle: 'เครื่องยนต์ PlateProfit',
    engineSubtitle: 'การติดตามความแม่นยำ 6 ตำแหน่งทศนิยม',
    unitCostLabel: 'ความแม่นยำต้นทุนต่อหน่วย',
    perGramLabel: 'ต่อกรัม',
    noWastageLabel: 'ไม่มีของเสียที่ซ่อนอยู่. ติดตามทุกมิลลิกรัม.',
    platedLabel: 'ความสมบูรณ์แบบบนจาน',
    profitableLabel: 'ทำกำไร ทุกครั้ง',
    joinChefs: 'เข้าร่วมกับเชฟใน 150+ ประเทศที่ติดตามทุกมิลลิกรัม',
    
    // Pricing Section
    pricingTitle: 'ราคาโปร่งใส',
    pricingSubtitle: 'น้อยกว่าค่ากาแฟหนึ่งถ้วยสำหรับความแม่นยำหนึ่งเดือน.',
    artisanTier: 'ช่างฝีมือ',
    artisanPrice: '$2.99',
    artisanPeriod: '/เดือน',
    artisanDesc: 'The Master Recipe Bank—Heritage-ready',
    artisanFeature1: 'The Master Recipe Bank (6-decimal precision)',
    artisanFeature2: 'Up to 250 Master Recipes',
    artisanFeature3: '6-Decimal Costing for every ingredient',
    artisanScalingHook: 'Grow as you go: +$2.99/mo for every additional 250 recipes.',
    artisanCta: 'Start Artisan',
    professionalTier: 'Professional',
    professionalPrice: '$5.99',
    professionalPeriod: '/mo',
    professionalDesc: 'The Executive Engine for Profit Control',
    professionalBridge: 'Everything in Artisan, plus:',
    professionalFeature1: 'Unlimited Master Recipes',
    professionalFeature2: 'Advanced Menu Building & Dynamic Pricing',
    professionalFeature3: 'Menu Sharing to Multiple Devices',
    professionalFeature4: 'Real-time Profit Analysis & Multi-department Tracking',
    professionalFeature5: 'PDF Export & Professional Reporting',
    professionalCta: 'Start Professional',
    popularBadge: 'Most Popular',
    
    // PWA Utility Bar
    utilityNoApp: 'ไม่ต้องใช้ App Store',
    utilityOffline: 'ใช้งานออฟไลน์ในครัว',
    utilitySync: 'ซิงค์หลายอุปกรณ์ทันที',
    
    // Specialist Verticals
    specialistTitle: 'สร้างขึ้นสำหรับทุกผู้เชี่ยวชาญด้านอาหาร',
    mixologyTitle: 'ห้องทดลองมิกซ์',
    mixologyDesc: 'ต้นทุนที่แม่นยำสำหรับทุกการเท จากค็อกเทลคราฟต์ถึงศิลปะกาแฟ.',
    mixologyUnit: 'ต่อมล.',
    pastryTitle: 'ศิลปินเบเกอรี่',
    pastryDesc: 'การติดตามการวัดขนาดเล็กสำหรับขนมหวานและการอบที่ละเอียดอ่อน.',
    pastryUnit: 'ต่อกรัม',
    spiceTitle: 'ผู้ผสมเครื่องเทศ',
    spiceDesc: 'ปกป้องส่วนผสมลับของคุณด้วยความแม่นยำระดับส่วนผสม.',
    spiceUnit: 'ต่อกรัม',
    
    // Problem/Solution
    problemTitle: 'ช่องว่างที่ซ่อนอยู่ในครัวของคุณ',
    problemSubtitle: 'เชฟส่วนใหญ่เดาราคา. เราช่วยคุณรู้.',
    problemCard1Title: 'ต้นทุนวัตถุดิบที่เพิ่มขึ้น',
    problemCard1Desc: 'ราคาเปลี่ยนแปลงทุกวัน แต่เมนูของคุณยังคงเหมือนเดิม. คุณยังทำกำไรอยู่หรือไม่?',
    problemCard2Title: 'ความลึกลับของราคาเมนู',
    problemCard2Desc: 'แต่ละจานมีต้นทุนเท่าไหร่จริงๆ? หากไม่มีข้อมูลที่แม่นยำ คุณกำลังบินตาบอด.',
    problemCard3Title: 'เวลาและเงินที่เสียไป',
    problemCard3Desc: 'สเปรดชีตด้วยตนเอง ต้นทุนที่ล้าสมัย และการเดาทำให้กำไรลดลง.',
    
    solutionTitle: 'PlateProfit: ศูนย์บัญชาการสูตรและต้นทุนของคุณ',
    solutionDesc: 'ติดตามวัตถุดิบทุกชิ้น คำนวณต้นทุนสูตรที่แม่นยำ และกำหนดราคาเมนูที่ทำกำไร—ทั้งหมดในแพลตฟอร์มเดียว.',
    
    // Personas
    personasTitle: 'สร้างขึ้นสำหรับทุกการเดินทางด้านอาหาร',
    individualTitle: 'แม่บ้านรายบุคคล',
    individualSubtitle: 'เหมาะสำหรับพ่อครัวที่หลงใหลในการทำอาหาร',
    individualFeature1: 'เก็บสูตรไม่จำกัด',
    individualFeature2: 'ติดตามต้นทุนวัตถุดิบ',
    individualFeature3: 'คำนวณต้นทุนต่อเสิร์ฟ',
    individualFeature4: 'การจัดการสูตรพื้นฐาน',
    individualCta: 'เริ่มต้นเป็นรายบุคคล',
    professionalTitle: 'เชฟมืออาชีพ',
    professionalSubtitle: 'สำหรับร้านอาหารและธุรกิจอาหาร',
    professionalSpectrum: 'จากครัวระดับมิชลินสตาร์ไปจนถึงอาหารริมทางทั่วโลก—PlateProfit คือศูนย์บัญชาการของคุณ.',
    professionalFeature1: 'ทุกอย่างในรายบุคคล บวก:',
    professionalFeature2: 'การกำหนดราคาเมนูขั้นสูง',
    professionalFeature3: 'การติดตามหลายแผนก',
    professionalFeature4: 'การวิเคราะห์กำไรแบบเรียลไทม์',
    professionalFeature5: 'ส่งออก PDF และแบ่งปัน',
    professionalCta: 'เป็นมืออาชีพ',
    
    // Features
    featuresTitle: 'ทุกสิ่งที่คุณต้องการเพื่อดำเนินครัวที่ทำกำไร',
    feature1Title: 'ธนาคารสูตร',
    feature1Desc: 'เก็บ จัดระเบียบ และจัดการสูตรทั้งหมดของคุณในที่เดียว. ไม่สูญเสียสูตรอีกต่อไป.',
    feature2Title: 'สูตรที่ใช้งาน/ไม่ใช้งาน',
    feature2Desc: 'สลับสูตรตามฤดูกาลหรือความพร้อม. รักษาเมนูของคุณให้สดใหม่.',
    feature3Title: 'การติดตามต้นทุนแบบเรียลไทม์',
    feature3Desc: 'อัปเดตราคาวัตถุดิบครั้งเดียว และดูการเปลี่ยนแปลงต้นทุนในสูตรทั้งหมดทันที.',
    feature4Title: 'ความฉลาดในการกำหนดราคาเมนู',
    feature4Desc: 'กำหนดเป้าหมายกำไรและรับคำแนะนำทันทีสำหรับราคาเมนูที่ทำกำไร.',
    feature5Title: 'รองรับหลายสกุลเงิน',
    feature5Desc: 'ทำงานในสกุลเงินท้องถิ่นของคุณ. เรารองรับ USD, EUR, QAR, INR และอีกกว่า 20 สกุลเงิน.',
    feature6Title: 'การติดตามแผนก',
    feature6Desc: 'จัดระเบียบสูตรตามครัว บาร์ เบเกอรี หรือแผนกที่กำหนดเองเพื่อการควบคุมที่ดีขึ้น.',
    
    // Footer
    footerCta: 'พร้อมที่จะเปลี่ยนครัวของคุณ?',
    footerCtaButton: 'เริ่มบัญชีฟรีของคุณ',
    footerTagline: 'PlateProfit — ความแม่นยำในทุกส่วน',
  },
  
  zh: {
    // Header
    languageLabel: '语言',
    
    // Hero Section
    heroTitle: '从储藏室到盘子，',
    heroSubtitle: '每毫克都有利润。',
    heroDescription: '烹饪专业人士的完整生态系统',
    heroPrecision: '6 位小数精度。每毫克都很重要。',
    ctaButton: '每月 $2.99 起',
    
    // Dual-Anchor Hero (Split Layout)
    leftHeroTitle: '永远保存奶奶的秘密.',
    leftHeroSubtext: '用 6 位小数精度建立和保护您的主食谱库.',
    leftHeroCta: '开始工匠 ($2.99/月)',
    rightHeroTitle: '从储藏室到盘子的盈利能力.',
    rightHeroSubtext: '行政总厨的 100% 利润控制引擎.',
    rightHeroCta: '成为专业版 ($5.99/月)',
    
    // Hero Triple-Dynamic Labels
    rawIngredientsLabel: '原料',
    precisionTracking: '精确采购，每日追踪',
    engineTitle: 'PlateProfit 引擎',
    engineSubtitle: '6 位小数精度追踪',
    unitCostLabel: '单位成本精度',
    perGramLabel: '每克',
    noWastageLabel: '没有隐藏浪费。每毫克都被追踪。',
    platedLabel: '完美摆盘',
    profitableLabel: '盈利，每次',
    joinChefs: '加入 150 多个国家追踪每毫克的厨师',
    
    // Pricing Section
    pricingTitle: '透明定价',
    pricingSubtitle: '每月精度的成本不到一杯咖啡.',
    artisanTier: '工匠',
    artisanPrice: '$2.99',
    artisanPeriod: '/月',
    artisanDesc: '主配方库—传承准备就绪',
    artisanFeature1: '主配方库（6位小数精度）',
    artisanFeature2: '最多250个主配方',
    artisanFeature3: '每种食材的6位小数成本计算',
    artisanScalingHook: '随您成长：每增加250个配方+$2.99/月。',
    artisanCta: '开始工匠',
    professionalTier: '专业版',
    professionalPrice: '$5.99',
    professionalPeriod: '/月',
    professionalDesc: '利润控制的行政引擎',
    professionalBridge: '工匠的所有功能，加上：',
    professionalFeature1: '无限主配方',
    professionalFeature2: '高级菜单构建和动态定价',
    professionalFeature3: '多设备菜单共享',
    professionalFeature4: '实时利润分析和多部门跟踪',
    professionalFeature5: 'PDF导出和专业报告',
    professionalCta: '开始专业版',
    popularBadge: '最受欢迎',
    
    // PWA Utility Bar
    utilityNoApp: '无需 App Store',
    utilityOffline: '厨房离线可用',
    utilitySync: '即时多设备同步',
    
    // Specialist Verticals
    specialistTitle: '为每位烹饪专家打造',
    mixologyTitle: '调酒实验室',
    mixologyDesc: '每次倾倒的精确成本计算，从手工鸡尾酒到咖啡艺术.',
    mixologyUnit: '每毫升',
    pastryTitle: '糕点艺术家',
    pastryDesc: '精致甜点和烘焙的微量测量追踪.',
    pastryUnit: '每克',
    spiceTitle: '香料调配师',
    spiceDesc: '以成分级精度保护您的秘密配方.',
    spiceUnit: '每克',
    
    // Problem/Solution
    problemTitle: '厨房中的隐藏差距',
    problemSubtitle: '大多数厨师猜测价格。我们帮助您知道。',
    problemCard1Title: '上升的原料成本',
    problemCard1Desc: '价格每天都在变化，但您的菜单保持不变。您还有盈利吗？',
    problemCard2Title: '菜单定价之谜',
    problemCard2Desc: '每道菜真正的成本是多少？没有准确的数据，您就是盲目飞行。',
    problemCard3Title: '浪费的时间和金钱',
    problemCard3Desc: '手动电子表格、过时的成本和猜测导致利润率缩小。',
    
    solutionTitle: 'PlateProfit：您的食谱和成本指挥中心',
    solutionDesc: '追踪每种原料，计算精确的食谱成本，并设定有利可图的菜单价格——全部在一个平台上。',
    
    // Personas
    personasTitle: '为每个烹饪旅程而建',
    individualTitle: '个人家庭主妇',
    individualSubtitle: '完美适合热情的家庭厨师',
    individualFeature1: '存储无限食谱',
    individualFeature2: '追踪原料成本',
    individualFeature3: '计算每份成本',
    individualFeature4: '基本食谱管理',
    individualCta: '作为个人开始',
    professionalTitle: '专业厨师',
    professionalSubtitle: '适用于餐厅和食品企业',
    professionalSpectrum: '从米其林星级厨房到全球街头美食——PlateProfit 是您的指挥中心。',
    professionalFeature1: '个人版的所有功能，加上：',
    professionalFeature2: '高级菜单定价',
    professionalFeature3: '多部门追踪',
    professionalFeature4: '实时利润分析',
    professionalFeature5: 'PDF 导出和分享',
    professionalCta: '成为专业人士',
    
    // Features
    featuresTitle: '经营有利可图的厨房所需的一切',
    feature1Title: '食谱库',
    feature1Desc: '在一个地方存储、组织和管理所有食谱。再也不会丢失食谱。',
    feature2Title: '活动/非活动食谱',
    feature2Desc: '根据季节性或可用性切换食谱。保持菜单新鲜。',
    feature3Title: '实时成本追踪',
    feature3Desc: '更新一次原料价格，立即查看所有食谱中的成本变化。',
    feature4Title: '菜单定价智能',
    feature4Desc: '设定目标利润率，并获得有利可图的菜单定价的即时建议。',
    feature5Title: '多货币支持',
    feature5Desc: '使用您的本地货币工作。我们支持 USD、EUR、QAR、INR 和 20 多种货币。',
    feature6Title: '部门追踪',
    feature6Desc: '按厨房、酒吧、糕点或自定义部门组织食谱，以便更好地控制。',
    
    // Footer
    footerCta: '准备好改变您的厨房了吗？',
    footerCtaButton: '开始您的免费帐户',
    footerTagline: 'PlateProfit — 每一部分都精确',
  },
};

const Landing = () => {
  usePageTitle(null); // keeps baseline "PlateProfit"
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [userCountry, setUserCountry] = useState('GLOBAL'); // For IP-based personalization
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Country to language mapping for auto-detection
  const countryToLanguage = {
    'AE': 'ar', 'IN': 'hi', 'GB': 'en', 'FR': 'fr', 'IT': 'it',
    'BR': 'pt', 'JP': 'ja', 'ES': 'es', 'MX': 'es', 'CL': 'es',
    'TH': 'th', 'PH': 'en', 'SG': 'en', 'CN': 'zh', 'HK': 'zh',
    'GLOBAL': 'en'
  };
  
  // Load language from localStorage on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('plateprofit_landing_language') || 'en';
    setSelectedLanguage(savedLang);
  }, []);
  
  // IP Geolocation for visual personalization and auto-language
  useEffect(() => {
    const detectUserCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/', { timeout: 5000 });
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code; // e.g., "IN", "QA", "US"
          setUserCountry(countryCode);
          console.log('🌍 User country detected:', countryCode);
          
          // Auto-set language based on country if not already set by user
          const savedLang = localStorage.getItem('plateprofit_landing_language');
          if (!savedLang) {
            const autoLang = countryToLanguage[countryCode] || 'en';
            setSelectedLanguage(autoLang);
            console.log('🌐 Auto-set language to:', autoLang);
          }
        }
      } catch (error) {
        console.log('⚠️ Geolocation failed, using global visuals');
        setUserCountry('GLOBAL');
      }
    };
    
    detectUserCountry();
  }, []);
  
  // Save language preference
  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    localStorage.setItem('plateprofit_landing_language', lang);
    // Keep i18next (used by usePageTitle + the rest of the app) in sync so a
    // chef who picks Tamil on the landing lands on /login with the right
    // browser-tab title + persisted preference.
    try {
      i18n.changeLanguage(lang);
      localStorage.setItem('language', lang);
    } catch { /* non-blocking */ }
  };
  
  // Check if user is super admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/settings`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const settings = await response.json();
          setIsAdmin(settings.is_super_admin || false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, [user]);
  
  // Navigate to signup with language preference
  const handleGetStarted = () => {
    localStorage.setItem('plateprofit_signup_language', selectedLanguage);
    navigate('/signup');
  };
  
  // Get geo-personalized individual persona image
  const getIndividualPersonaImage = () => {
    const images = {
      'IN': 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/9707edc9097fb175b3db79ccc99d77efbd7361343d40b6eac1652b4f0a14a56c.png', // India
      'QA': 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/d7f0cbf337ea1fb3aa7483096b7fc123d55a61915ea7171db28646e011ba205b.png', // Qatar
      'GLOBAL': 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/19f69232cf276260cdff8ecae46776e925e59fc782ebf8b1fae72acfa2e10bfd.png' // Default
    };
    
    return images[userCountry] || images['GLOBAL'];
  };
  
  // Get hero assets based on user's country (Triple-Dynamic Hero - GLOBAL TEN)
  const getHeroAssets = () => {
    const assets = {
      'AE': { // Dubai - Date Cake Story (NO PORK/ALCOHOL)
        ingredients: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/624982d85ba4f7b3e6d9d996eab52763052cbd214dca929ddc9bd1e2a3efc7f2.png',
        finished: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/9eaa43d6fcebccd39e5e7415a0aa25ee57df1974dcca63aa33d33fbc12205c03.png',
        family: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/11661acdd98bd1b1088184eb90334b34811c5d0d1f8ca17835a5b62d9ba439b2.png',
        featured: 'Saffron, Organic Dates, Honey',
        featuredIngredient: 'Saffron',
        costPerUnit: '$0.000412',
        menuPrice: '$87.50',
        margin: '78.4%',
        language: 'ar'
      },
      'IN': { // India - Chicken Biryani Story (STRICTLY NO BEEF)
        ingredients: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/cecef01c902d16941107219b342044201d7ef50cad966dea15335eaf0f5d210a.png',
        finished: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/30f42e83ad2646d6ea6d945cd56d19e6ea3ddc6927425b10ae67400570cf0b87.png',
        family: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/9707edc9097fb175b3db79ccc99d77efbd7361343d40b6eac1652b4f0a14a56c.png',
        featured: 'Basmati Rice, Spices, Chicken',
        featuredIngredient: 'Saffron',
        costPerUnit: '₹0.034000',
        menuPrice: '₹650',
        margin: '73.2%',
        language: 'hi'
      },
      'GB': { // England - Fish & Chips Story
        ingredients: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/5a7dfef69659417ec383fce410bf53d3c55ac8803f28bfb8e366a02753589f43.png',
        finished: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/700b1652f85a70c09c1e83c70c2495cc4181ca9993ddc6a5e5bc93c4c037d750.png',
        featured: 'Cod, Flour, Potatoes',
        featuredIngredient: 'Fresh Cod',
        costPerUnit: '£0.000187',
        menuPrice: '£12.50',
        margin: '71.8%',
        language: 'en'
      },
      'FR': { // France - Croissant Story
        ingredients: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/9488718335dc544ec56d30674c1f2a1fb43d58cef9023b4036a86f640c170857.png',
        finished: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/b0f8efceef8d139c4be017194358847a962ad1e724e64bd9e2c338e157d38be4.png',
        family: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/8047d6066800c6282c723690cdd5ed4f59b9788898a57d6b8c89d0d5ebacb876.png',
        featured: 'Isigny Butter, Flour, Vanilla',
        featuredIngredient: 'Vanilla Bean',
        costPerUnit: '€0.000324',
        menuPrice: '€42.50',
        margin: '75.8%',
        language: 'fr'
      },
      'IT': { // Italy - Pasta Precision
        ingredients: 'https://static.prod-images.emergentagent.com/791834/02b7da5d8f3a6d88.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/908ea59266c7ef2f.png',
        featured: '00 Flour, Eggs, Fresh Basil',
        featuredIngredient: 'Tipo 00 Flour',
        costPerUnit: '€0.000156',
        menuPrice: '€18.50',
        margin: '76.3%',
        language: 'it'
      },
      'BR': { // Brazil - Heritage Feijoada
        ingredients: 'https://static.prod-images.emergentagent.com/791834/bdad02706bec5206.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/605ff41cacb58ced.png',
        featured: 'Black Beans, Smoked Meats',
        featuredIngredient: 'Black Beans',
        costPerUnit: 'R$0.000892',
        menuPrice: 'R$58.00',
        margin: '72.1%',
        language: 'pt'
      },
      'JP': { // Japan - Zen Omakase
        ingredients: 'https://static.prod-images.emergentagent.com/791834/21ab2e67b709ee56.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/9277e1c13897d17f.png',
        featured: 'Tuna, Wasabi, Sushi Rice',
        featuredIngredient: 'Bluefin Tuna',
        costPerUnit: '¥0.052000',
        menuPrice: '¥4,800',
        margin: '74.5%',
        language: 'ja'
      },
      'ES': { // LatAm/Spain - Taco Economy (also handles MX, CL)
        ingredients: 'https://static.prod-images.emergentagent.com/791834/f8bfc5331d3db802.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/2cab8f379d72cfb4.png',
        featured: 'Masa, Chilies, Cilantro',
        featuredIngredient: 'Fresh Masa',
        costPerUnit: '$0.000287',
        menuPrice: '$24.00',
        margin: '71.9%',
        language: 'es'
      },
      'MX': { // Mexico - Same as ES/LatAm
        ingredients: 'https://static.prod-images.emergentagent.com/791834/f8bfc5331d3db802.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/2cab8f379d72cfb4.png',
        featured: 'Masa, Chilies, Cilantro',
        featuredIngredient: 'Fresh Masa',
        costPerUnit: '$0.000287',
        menuPrice: 'MX$420',
        margin: '71.9%',
        language: 'es'
      },
      'CL': { // Chile - Same as ES/LatAm
        ingredients: 'https://static.prod-images.emergentagent.com/791834/f8bfc5331d3db802.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/2cab8f379d72cfb4.png',
        featured: 'Masa, Chilies, Cilantro',
        featuredIngredient: 'Fresh Masa',
        costPerUnit: '$0.000287',
        menuPrice: '$18,500',
        margin: '71.9%',
        language: 'es'
      },
      'TH': { // SE Asia - Tom Yum (also handles PH, SG)
        ingredients: 'https://static.prod-images.emergentagent.com/791834/db9935f5e3190159.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/3b7a0b09afbbb2a5.png',
        featured: 'Lemongrass, Chilies, Shrimp',
        featuredIngredient: 'Thai Chilies',
        costPerUnit: '฿0.009400',
        menuPrice: '฿380',
        margin: '73.8%',
        language: 'th'
      },
      'PH': { // Philippines - Same as TH/SE Asia
        ingredients: 'https://static.prod-images.emergentagent.com/791834/db9935f5e3190159.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/3b7a0b09afbbb2a5.png',
        featured: 'Lemongrass, Chilies, Shrimp',
        featuredIngredient: 'Thai Chilies',
        costPerUnit: '₱0.015000',
        menuPrice: '₱580',
        margin: '73.8%',
        language: 'en'
      },
      'SG': { // Singapore - Same as TH/SE Asia
        ingredients: 'https://static.prod-images.emergentagent.com/791834/db9935f5e3190159.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/3b7a0b09afbbb2a5.png',
        featured: 'Lemongrass, Chilies, Shrimp',
        featuredIngredient: 'Thai Chilies',
        costPerUnit: 'S$0.000450',
        menuPrice: 'S$28.00',
        margin: '73.8%',
        language: 'en'
      },
      'CN': { // China - Dim Sum
        ingredients: 'https://static.prod-images.emergentagent.com/791834/14f33e7c25c5b2a7.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/97fba8ea4f026d28.png',
        featured: 'Dim Sum Wrappers, Duck',
        featuredIngredient: 'Duck Breast',
        costPerUnit: '¥0.003800',
        menuPrice: '¥188',
        margin: '75.2%',
        language: 'zh'
      },
      'HK': { // Hong Kong - Same as CN
        ingredients: 'https://static.prod-images.emergentagent.com/791834/14f33e7c25c5b2a7.png',
        finished: 'https://static.prod-images.emergentagent.com/791834/97fba8ea4f026d28.png',
        featured: 'Dim Sum Wrappers, Duck',
        featuredIngredient: 'Duck Breast',
        costPerUnit: 'HK$0.003400',
        menuPrice: 'HK$298',
        margin: '75.2%',
        language: 'zh'
      },
      'GLOBAL': { // Default fallback - India assets
        ingredients: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/cecef01c902d16941107219b342044201d7ef50cad966dea15335eaf0f5d210a.png',
        finished: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/30f42e83ad2646d6ea6d945cd56d19e6ea3ddc6927425b10ae67400570cf0b87.png',
        family: 'https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/19f69232cf276260cdff8ecae46776e925e59fc782ebf8b1fae72acfa2e10bfd.png',
        featured: 'Premium Ingredients',
        featuredIngredient: 'Saffron',
        costPerUnit: '$0.000412',
        menuPrice: '$47.50',
        margin: '73.2%',
        language: 'en'
      }
    };
    
    return assets[userCountry] || assets['GLOBAL'];
  };
  
  const heroAssets = getHeroAssets();
  
  // Fallback: new Indic locales (bn, gu, kn, ml, mr, or, pa, ta, te) land in
  // the picker before the full landing-page copy is translated.  Fall back to
  // the Hindi block for linguistic / cultural proximity, then English.
  const t = TRANSLATIONS[selectedLanguage] || TRANSLATIONS.hi || TRANSLATIONS.en;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header with Language Selector */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChefHat className="h-8 w-8 text-amber-500" />
              <span className="text-2xl font-bold text-white">PlateProfit</span>
            </div>
            
            {/* Language Selector & Auth Button */}
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-slate-400" />
              <select
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="bg-slate-800 text-white border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <optgroup label="Global">
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                  <option value="ja">日本語</option>
                  <option value="pt">Português</option>
                  <option value="th">ไทย</option>
                  <option value="zh">中文</option>
                </optgroup>
                <optgroup label="🇮🇳 Indic">
                  <option value="hi">हिन्दी · Hindi</option>
                  <option value="bn">বাংলা · Bengali</option>
                  <option value="gu">ગુજરાતી · Gujarati</option>
                  <option value="kn">ಕನ್ನಡ · Kannada</option>
                  <option value="ml">മലയാളം · Malayalam</option>
                  <option value="mr">मराठी · Marathi</option>
                  <option value="or">ଓଡ଼ିଆ · Odia</option>
                  <option value="pa">ਪੰਜਾਬੀ · Punjabi</option>
                  <option value="ta">தமிழ் · Tamil</option>
                  <option value="te">తెలుగు · Telugu</option>
                </optgroup>
              </select>
              
              {/* Login/Dashboard Button */}
              {user ? (
                <button
                  onClick={() => navigate(isAdmin ? '/admin-kv' : '/recipes')}
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-4 py-2 rounded-lg transition-all"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>{isAdmin ? 'Admin Dashboard' : 'Dashboard'}</span>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold px-4 py-2 rounded-lg transition-all border border-slate-600"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Dual-Anchor Split Hero Section (Two Brand Pillars) */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" dir={selectedLanguage === 'ar' ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* Split Hero: Legacy (Left) + Profit (Right) */}
          <div className="grid md:grid-cols-2 gap-6">
            
            {/* Left Side: The Bank (Emotion/Legacy) */}
            <div className="relative overflow-hidden rounded-3xl h-[600px] group">
              {/* Emotive Background Image */}
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/1237165fd5ee14738394143035a7433c0b44e637e13c7b73a34b511babb0124d.png"
                alt="Recipe Bank Legacy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                style={{ objectPosition: 'center 25%' }}
                loading="eager"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent" />
              
              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <div className="mb-6">
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                    {t.leftHeroTitle}
                  </h2>
                  <p className="text-base sm:text-lg text-slate-300 mb-6">
                    {t.leftHeroSubtext}
                  </p>
                </div>
                
                {/* CTA for Artisan Tier */}
                <button
                  onClick={handleGetStarted}
                  className="bg-slate-800/90 hover:bg-slate-700 backdrop-blur-sm text-white font-semibold text-base sm:text-lg px-8 py-4 rounded-xl transition-all transform hover:scale-105 border border-slate-600 hover:border-slate-500 w-full sm:w-auto"
                >
                  {t.leftHeroCta}
                </button>
              </div>
            </div>
            
            {/* Right Side: The Flow (Profit/Process) */}
            <div className="relative overflow-hidden rounded-3xl h-[600px] group">
              {/* Professional Action Shot */}
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/cd95df0eae62d1f496e7e848475730ad0928f177b0d52334a27440f5467978c3.png"
                alt="Pantry to Plate Professional"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="eager"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent" />
              
              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <div className="mb-6">
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                    {t.rightHeroTitle}
                  </h2>
                  <p className="text-base sm:text-lg text-slate-300 mb-6">
                    {t.rightHeroSubtext}
                  </p>
                </div>
                
                {/* CTA for Professional Tier */}
                <button
                  onClick={handleGetStarted}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-base sm:text-lg px-8 py-4 rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-amber-500/50 w-full sm:w-auto"
                >
                  {t.rightHeroCta}
                </button>
              </div>
            </div>
            
          </div>
        </div>
      </section>
      
      {/* PWA Utility Bar - App-Less Benefits */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-800/30 border-y border-slate-700/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {/* No App Store Needed */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-4 text-4xl">📱</div>
              <p className="text-base font-semibold text-white">{t.utilityNoApp}</p>
            </div>
            
            {/* Kitchen-Proof Offline */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-4 text-4xl">📶</div>
              <p className="text-base font-semibold text-white">{t.utilityOffline}</p>
            </div>
            
            {/* Instant Multi-Device Sync */}
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 mb-4 text-4xl">⚡</div>
              <p className="text-base font-semibold text-white">{t.utilitySync}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Transparent Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          {/* Pricing Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t.pricingTitle}
            </h2>
            <p className="text-base sm:text-lg text-amber-400">
              {t.pricingSubtitle}
            </p>
          </div>
          
          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Artisan Tier */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-amber-500/50 transition-all hover:transform hover:scale-105">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-2">{t.artisanTier}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-amber-400">{t.artisanPrice}</span>
                  <span className="text-slate-400 text-lg">{t.artisanPeriod}</span>
                </div>
                <p className="text-slate-300 text-sm mb-6">{t.artisanDesc}</p>
                
                {/* Features */}
                <ul className="text-left space-y-3 mb-6">
                  <li className="flex items-start text-slate-300 text-sm">
                    <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {t.artisanFeature1}
                  </li>
                  <li className="flex items-start text-slate-300 text-sm">
                    <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {t.artisanFeature2}
                  </li>
                  <li className="flex items-start text-slate-300 text-sm">
                    <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {t.artisanFeature3}
                  </li>
                </ul>

                {/* Scaling Hook */}
                <div className="mb-8 text-center">
                  <p className="text-sm text-amber-500/80 italic font-medium">
                    {t.artisanScalingHook}
                  </p>
                </div>
                
                <button
                  onClick={handleGetStarted}
                  className="w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 px-6 rounded-lg transition-all"
                >
                  {t.artisanCta}
                </button>
              </div>
            </div>
            
            {/* Professional Tier */}
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 backdrop-blur-sm border-2 border-amber-500 rounded-2xl p-8 relative hover:transform hover:scale-105 transition-all">
              {/* Popular Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-amber-500 text-slate-950 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
                  {t.popularBadge}
                </span>
              </div>
              
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-2">{t.professionalTier}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-amber-400">{t.professionalPrice}</span>
                  <span className="text-slate-400 text-lg">{t.professionalPeriod}</span>
                </div>
                <p className="text-slate-300 text-sm mb-6">{t.professionalDesc}</p>
                
                {/* Bridge Text */}
                <p className="text-slate-400 text-sm font-semibold mb-4 text-left">
                  {t.professionalBridge}
                </p>

                {/* Features */}
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-start text-slate-300 text-sm">
                    <svg className="w-5 h-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {t.professionalFeature1}
                  </li>
                  <li className="flex items-start text-slate-300 text-sm">
                    <svg className="w-5 h-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {t.professionalFeature2}
                  </li>
                  <li className="flex items-start text-slate-300 text-sm">
                    <svg className="w-5 h-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {t.professionalFeature3}
                  </li>
                  <li className="flex items-start text-slate-300 text-sm">
                    <svg className="w-5 h-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {t.professionalFeature4}
                  </li>
                  <li className="flex items-start text-slate-300 text-sm">
                    <svg className="w-5 h-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {t.professionalFeature5}
                  </li>
                </ul>
                
                <button
                  onClick={handleGetStarted}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-amber-500/50"
                >
                  {t.professionalCta}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Specialist Verticals Row */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-900/50 to-slate-950">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t.specialistTitle}
            </h2>
          </div>
          
          {/* 3 Specialist Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Card 1: The Mixology Lab */}
            <div className="relative overflow-hidden rounded-3xl h-[500px] group">
              <img 
                src="https://images.unsplash.com/photo-1722537049087-88b6a257dd32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxiYXJ0ZW5kZXIlMjBmbGFpciUyMGp1Z2dsaW5nJTIwYm90dGxlcyUyMGNvY2t0YWlsJTIwZHluYW1pYyUyMGFjdGlvbnxlbnwwfHx8fDE3NzM4NzE0MzJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Mixology Lab"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              {/* Luminous Stage Spot Gradient - Bottom dark, center/top bright */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/20 to-transparent" />
              
              {/* 6-Decimal Precision Overlay with Extra Strong Backdrop Blur */}
              <div className="absolute top-6 right-6">
                <div className="bg-slate-950/98 backdrop-blur-lg rounded-xl px-5 py-3 border-2 border-amber-400/50 shadow-2xl">
                  <p className="text-xs text-slate-300 uppercase tracking-widest mb-1 font-semibold">Unit Cost</p>
                  <p className="text-2xl font-mono font-bold text-amber-400 mb-1">$0.000412</p>
                  <p className="text-xs text-slate-400">{t.mixologyUnit}</p>
                </div>
              </div>
              
              {/* Content */}
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-2xl font-bold text-white mb-3">{t.mixologyTitle}</h3>
                <p className="text-sm text-slate-300">{t.mixologyDesc}</p>
              </div>
            </div>
            
            {/* Card 2: The Pastry Artist */}
            <div className="relative overflow-hidden rounded-3xl h-[500px] group">
              <img 
                src="https://images.unsplash.com/photo-1684244090854-9bc9227a6faf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxwYXN0cnklMjBjaGVmJTIwZGVjb3JhdGluZyUyMGNvbG9yZnVsJTIwY2FrZSUyMGJlYXV0aWZ1bCUyMGRlc2lnbiUyMGVsZWdhbnR8ZW58MHx8fHwxNzczODcxNDM5fDA&ixlib=rb-4.1.0&q=85"
                alt="Pastry Artist"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              {/* Luminous Stage Spot Gradient - Bottom dark, center/top bright */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/20 to-transparent" />
              
              {/* 6-Decimal Precision Overlay with Extra Strong Backdrop Blur */}
              <div className="absolute top-6 right-6">
                <div className="bg-slate-950/98 backdrop-blur-lg rounded-xl px-5 py-3 border-2 border-amber-400/50 shadow-2xl">
                  <p className="text-xs text-slate-300 uppercase tracking-widest mb-1 font-semibold">Unit Cost</p>
                  <p className="text-2xl font-mono font-bold text-amber-400 mb-1">$0.000187</p>
                  <p className="text-xs text-slate-400">{t.pastryUnit}</p>
                </div>
              </div>
              
              {/* Content */}
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-2xl font-bold text-white mb-3">{t.pastryTitle}</h3>
                <p className="text-sm text-slate-300">{t.pastryDesc}</p>
              </div>
            </div>
            
            {/* Card 3: The Spice Blender */}
            <div className="relative overflow-hidden rounded-3xl h-[500px] group">
              <img 
                src="https://images.unsplash.com/photo-1706553707046-335e3e8afdf5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxzcGljZSUyMG1hcmtldCUyMHNhY2tzJTIwYmFncyUyMGNvbG9yZnVsJTIwc3BpY2VzJTIwc2VsbGVyJTIwYmF6YWFyfGVufDB8fHx8MTc3Mzg3MTQ0NXww&ixlib=rb-4.1.0&q=85"
                alt="Spice Blender"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              {/* Luminous Stage Spot Gradient - Bottom dark, center/top bright */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/20 to-transparent" />
              
              {/* 6-Decimal Precision Overlay with Extra Strong Backdrop Blur */}
              <div className="absolute top-6 right-6">
                <div className="bg-slate-950/98 backdrop-blur-lg rounded-xl px-5 py-3 border-2 border-amber-400/50 shadow-2xl">
                  <p className="text-xs text-slate-300 uppercase tracking-widest mb-1 font-semibold">Unit Cost</p>
                  <p className="text-2xl font-mono font-bold text-amber-400 mb-1">$0.000324</p>
                  <p className="text-xs text-slate-400">{t.spiceUnit}</p>
                </div>
              </div>
              
              {/* Content */}
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-2xl font-bold text-white mb-3">{t.spiceTitle}</h3>
                <p className="text-sm text-slate-300">{t.spiceDesc}</p>
              </div>
            </div>
            
          </div>
        </div>
      </section>
      
      {/* Inline CSS for 6-Decimal Precision Pulse Animation */}
      <style>{`
        @keyframes precisionPulse {
          0%, 100% { 
            transform: scale(1); 
            opacity: 0.95;
            box-shadow: 0 0 0 0 rgba(251, 191, 36, 0);
          }
          50% { 
            transform: scale(1.02); 
            opacity: 1;
            box-shadow: 0 0 20px 5px rgba(251, 191, 36, 0.3);
          }
        }
        .animate-precisionPulse {
          animation: precisionPulse 3s ease-in-out infinite;
        }
      `}</style>
      
      {/* Section B: Problem/Solution */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          {/* Problem */}
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {t.problemTitle}
            </h2>
            <p className="text-xl text-slate-300">
              {t.problemSubtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {/* Problem Card 1 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
              <DollarSign className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.problemCard1Title}
              </h3>
              <p className="text-slate-300">
                {t.problemCard1Desc}
              </p>
            </div>
            
            {/* Problem Card 2 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
              <TrendingUp className="h-12 w-12 text-yellow-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.problemCard2Title}
              </h3>
              <p className="text-slate-300">
                {t.problemCard2Desc}
              </p>
            </div>
            
            {/* Problem Card 3 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
              <XCircle className="h-12 w-12 text-orange-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.problemCard3Title}
              </h3>
              <p className="text-slate-300">
                {t.problemCard3Desc}
              </p>
            </div>
          </div>
          
          {/* Solution */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-10 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t.solutionTitle}
            </h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto">
              {t.solutionDesc}
            </p>
          </div>
        </div>
      </section>
      
      {/* Section C: Personas */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-white text-center mb-16">
            {t.personasTitle}
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Individual Persona - Geo-Personalized */}
            <div className="bg-slate-800/50 border-2 border-blue-500/30 rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all">
              {/* Hero Image - Geo-specific */}
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={getIndividualPersonaImage()} 
                  alt="Multi-generational cooking" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
              </div>
              
              <div className="p-8">
                <Users className="h-12 w-12 text-blue-500 mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">
                  {t.individualTitle}
                </h3>
                <p className="text-slate-400 mb-6">
                  {t.individualSubtitle}
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-1" />
                    {t.individualFeature1}
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-1" />
                    {t.individualFeature2}
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-1" />
                    {t.individualFeature3}
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-1" />
                    {t.individualFeature4}
                  </li>
                </ul>
                <button
                  onClick={handleGetStarted}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  {t.individualCta}
                </button>
              </div>
            </div>
            
            {/* Professional Persona - Culinary Spectrum */}
            <div className="bg-slate-800/50 border-2 border-amber-500/50 rounded-2xl overflow-hidden hover:border-amber-500/70 transition-all relative">
              <div className="absolute -top-4 left-8 bg-amber-500 text-slate-950 px-4 py-1 rounded-full text-sm font-bold z-10">
                Popular
              </div>
              
              {/* Dual Image Montage - Executive Chef + Street Vendor */}
              <div className="relative h-64 grid grid-cols-2 gap-0.5">
                {/* Executive Chef */}
                <div className="relative overflow-hidden">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/0b60e15e1ea8fd4a2dc171469b30de3f992cae79f074c8de23ca232642256513.png" 
                    alt="Executive Chef in luxury kitchen" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent"></div>
                  <div className="absolute bottom-2 left-2 bg-slate-950/80 px-2 py-1 rounded text-xs text-amber-400 font-semibold">
                    Michelin-Star
                  </div>
                </div>
                {/* Street Vendor */}
                <div className="relative overflow-hidden">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/d7a438ef-f568-4c50-b48e-88742f81b7b4/images/102d0510a20a800963829a88a00ada78b772f8315a917391747c5f2904771dcc.png" 
                    alt="Street food vendor in market" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/30 to-transparent"></div>
                  <div className="absolute bottom-2 right-2 bg-slate-950/80 px-2 py-1 rounded text-xs text-amber-400 font-semibold">
                    Street Food
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <ChefHat className="h-12 w-12 text-amber-500 mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">
                  {t.professionalTitle}
                </h3>
                <p className="text-amber-400 font-semibold mb-3 text-sm">
                  {t.professionalSpectrum}
                </p>
                <p className="text-slate-400 mb-6">
                  {t.professionalSubtitle}
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3 text-slate-300 font-semibold">
                    <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />
                    {t.professionalFeature1}
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />
                    {t.professionalFeature2}
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />
                    {t.professionalFeature3}
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />
                    {t.professionalFeature4}
                  </li>
                  <li className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />
                    {t.professionalFeature5}
                  </li>
                </ul>
                <button
                  onClick={handleGetStarted}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold py-3 rounded-lg transition-colors"
                >
                  {t.professionalCta}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Section D: Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-white text-center mb-16">
            {t.featuresTitle}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all">
              <BookOpen className="h-10 w-10 text-amber-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.feature1Title}
              </h3>
              <p className="text-slate-300">
                {t.feature1Desc}
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all">
              <CheckCircle className="h-10 w-10 text-green-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.feature2Title}
              </h3>
              <p className="text-slate-300">
                {t.feature2Desc}
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all">
              <TrendingUp className="h-10 w-10 text-blue-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.feature3Title}
              </h3>
              <p className="text-slate-300">
                {t.feature3Desc}
              </p>
            </div>
            
            {/* Feature 4 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all">
              <DollarSign className="h-10 w-10 text-purple-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.feature4Title}
              </h3>
              <p className="text-slate-300">
                {t.feature4Desc}
              </p>
            </div>
            
            {/* Feature 5 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all">
              <Globe className="h-10 w-10 text-cyan-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.feature5Title}
              </h3>
              <p className="text-slate-300">
                {t.feature5Desc}
              </p>
            </div>
            
            {/* Feature 6 */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-amber-500/50 transition-all">
              <Package className="h-10 w-10 text-orange-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                {t.feature6Title}
              </h3>
              <p className="text-slate-300">
                {t.feature6Desc}
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Global Authority Ribbon - Infinite Scroll Marquee */}
      <section className="py-12 bg-slate-900/50 border-y border-slate-800 overflow-hidden">
        <div className="flex items-center gap-12 animate-marquee whitespace-nowrap">
          {/* Repeat cities twice for seamless infinite scroll */}
          {[...Array(2)].map((_, index) => (
            <div key={index} className="flex items-center gap-12">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇦🇪</span>
                <span className="text-xl font-semibold text-white">Doha</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇦🇪</span>
                <span className="text-xl font-semibold text-white">Dubai</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇫🇷</span>
                <span className="text-xl font-semibold text-white">Paris</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇯🇵</span>
                <span className="text-xl font-semibold text-white">Tokyo</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇬🇧</span>
                <span className="text-xl font-semibold text-white">London</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇮🇳</span>
                <span className="text-xl font-semibold text-white">Kerala</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇮🇹</span>
                <span className="text-xl font-semibold text-white">Roma</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇺🇸</span>
                <span className="text-xl font-semibold text-white">New York</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇧🇷</span>
                <span className="text-xl font-semibold text-white">São Paulo</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇹🇭</span>
                <span className="text-xl font-semibold text-white">Bangkok</span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Subtext */}
        <div className="text-center mt-6">
          <p className="text-slate-400 text-sm">
            Trusted by chefs in <span className="text-amber-500 font-semibold">150+ countries</span> worldwide
          </p>
        </div>
      </section>
      
      {/* Footer CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t.footerCta}
          </h2>
          <button
            onClick={handleGetStarted}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-lg px-10 py-4 rounded-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-amber-500/50 mb-8"
          >
            {t.footerCtaButton} →
          </button>
          <p className="text-slate-400 text-lg">
            {t.footerTagline}
          </p>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-400 text-sm mb-2">
            © {new Date().getFullYear()} PlateProfit. All rights reserved.
          </p>
          <p className="text-slate-500 text-xs">
            Powered by <span className="text-amber-500 font-semibold">Kritajna Ventures LLP</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
